import { useState, useEffect, useCallback, useRef, useMemo, Fragment, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getDoc, setDoc, deleteDoc, deleteField, doc, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import { chooseSaveBase, enqueueSave, isFormChangedError, isAlreadyExistsError, FORM_CHANGED_MESSAGE } from '../../features/crf/saveConcurrency';
import { renderAllMessages, type RenderAllResult } from '../../features/crf/renderAllOutcome';
import { VersionedDoc, readDocVersion, docChangedMessage } from '../../utils/versionedDoc';
import { versionedDocFor } from '../../utils/versionedFirestoreDoc';
import { functions, db, auth } from '../../firebase';
import { functionsBase } from '../../config/appConfig';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import {
  projectDoc,
  protocolAnalysisLatestDoc,
  studyDesignConfigDoc,
  crfBuilderSessionDoc,
  crfBuilderCrossFormConfirmationsDoc,
  crfPacketLatestDoc,
  artifactsCollection,
} from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import { approvalCoverageGate } from '../../../../functions/src/schedule/scheduleCoverage';
import { protocolFactGate } from '../../../../functions/src/crf/protocolFactChecks';
import { usePermission } from '../../components/permissions/PermissionTooltip';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import StepProgress from '../../components/StepProgress';
import {
  Upload,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Search,
  X,
  Check,
  Edit2,
  RotateCcw,
  AlertCircle,
  AlertTriangle,
  ChevronUp,
  ClipboardList,
  Play,
  CheckCircle2,
  Download,
  CalendarDays,
  LayoutList,
  GripVertical,
  Copy,
} from 'lucide-react';
import VisitScheduleMatrix from '../../components/crf/VisitScheduleMatrix';
import VisitPacketExport from '../../components/VisitPacketExport';
import VisitManager from '../../components/crf/VisitManager';
import CtBadge from '../../components/crf/CtBadge';
import CtReviewModal, { CtDecision } from '../../components/crf/CtReviewModal';
import UploadCrfPacketModal from '../../components/UploadCrfPacketModal';
import UploadDataDictionaryModal from '../../components/UploadDataDictionaryModal';
import ApplyDictionaryEditsModal from '../../components/ApplyDictionaryEditsModal';
import { deriveFormCode } from '../../utils/docxGenerator';
import { formNeedsVariables, shouldAutoGenerate } from './crfBulkActions';
import { planBulkRecovery, describeRecovery, generatedFormsLeftEmpty, MAX_BULK_SAVE_ATTEMPTS, type GeneratedForm } from '../../features/crf/bulkVariableMerge';
import { chooseProtocolText } from './crfProtocolText';
import { structurePayloadFields, type ClientStructureChoice } from './crfStructurePayload';
import { exportVariablePayload, exportStructurePayload } from './crfExportPayload';
import { gridRowsToText, hasSectionLayout, layoutSectionFields, sectionEditUpdates, sectionsFromStructure, sectionsToPayload, type GridRow } from './crfLayoutSections';
import { carriedQuestionFields, carriedSectionFields } from './crfCarriedFields';
import { answerCodesLine } from './crfCodesFrom';
import { codesFromAfterEdit } from './crfCodesFromEdit';
import { hasApprovedForm, hasFormsNeedingVariables } from './crfBulkActions';
import { isUploadedForm, blankPdfDownloadState, hasUsableRendering } from './crfFormDownloads';
import {
  ATTACH_RENDERING_ACCEPT,
  attachRenderingMessage,
  isUserRendering,
  keepsOwnDocument,
  renderingUploadContentType,
} from './attachRendering';
import {
  groupCrossFormDuplicates,
  summariseRenderings,
} from '../../features/crf/crfBuilderHeader';
import {
  runClientQualityChecks,
  approveBarSummary,
  type QualityWarning,
} from '../../features/crf/crfQualityChecks';
import { assignFormCodes } from '../../features/crf/assignFormCodes';
import { applyContentToForm, contentPayloadFields, type ClientContentRecord, type ClientContentResult } from './crfContentPayload';
import { codeForNewForm } from '../../features/crf/newFormCode';
import { formCodeToSaveOnClose } from '../../features/crf/formCodeEdit';
import { stripUndefined, pickFreshestSession, mergeServerWithCache } from '../../features/crf/sessionCopy';
import { buildCleanSession, persistSession, recoveryWasSaved, storedSessionFingerprint, type PersistedFingerprint } from '../../features/crf/sessionSave';
import {
  normaliseConfirmationKey,
  confirmedKeysFrom,
  isCrossFormDuplicateConfirmed,
  buildConfirmationEntry,
} from '../../features/crf/crossFormConfirmations';
import { moveVariableBefore } from './reorderVariables';
import { useProtocolReview } from '../../features/crf/useProtocolReview';
import { isStillRunningError, isStaleRunning, continueLabel } from '../../features/crf/protocolReview';
import { ProtocolReviewBanner, ProtocolReviewBadge, ProtocolReviewFindings } from '../../components/crf/ProtocolReviewParts';
import { storedOptionToBuilder, importedOptionToBuilder, optionCodeFromInput, nextOptionCode, type BuilderOption } from './crfOptionCodes';
import { sortForms, moveInOrder, isFormSortMode, type FormSortMode } from './sortForms';

// ─── Types ───────────────────────────────────────────────

type FormCategory = 'screening' | 'demographics' | 'medical_history' | 'procedures' | 'outcomes' | 'safety' | 'administrative' | 'other';
type FormStatus = 'draft' | 'in_review' | 'approved' | 'final';
type ResponseType = 'yesno' | 'single_select' | 'multi_select' | 'numeric' | 'date' | 'time' | 'text';
type AppPhase = 'upload' | 'ready' | 'analyzing' | 'workspace';

// An option's code is kept exactly as stored: a number, text (DLM1), or
// absent. See crfOptionCodes.ts.
type CRFOption = BuilderOption;

interface CRFVariable {
  id: string;
  varName: string;
  questionText: string;
  /** A table column's short header ("Clin. Sig."); stored as short_label (§C6, 2026-09-18). */
  shortLabel?: string;
  responseType: ResponseType;
  options: CRFOption[];
  unit: string;
  editing?: boolean;
  ct_codelist_ref?: string;
  ct_codelist_version?: string;
  ct_match_confidence?: 'auto' | 'needs_review' | 'user_confirmed' | 'sponsor_defined';
  // ── Margin content (content pass, 2026-09-14). Written by generation,
  // kept on read-back, sent on every save. The page used to drop guidance
  // on read-back, so server-written content was erased by the next save.
  guidance?: string;
  guidanceSource?: string;
  protocolRef?: string;
  isParent?: boolean;
  indent?: number;
  // From the study's data dictionary (D2, 2026-09-16). Carried with the rest
  // through crfCarriedFields.ts, so a save cannot drop them.
  helpText?: string;
  required?: boolean;
  /** Where the answer codes came from (order of authority). Carried through crfCarriedFields.ts. */
  codesFrom?: string;
}

type CrfSectionKind = 'questions' | 'repeating' | 'grid';

/**
 * Optional section metadata — added 2026-05 to support repeating tables
 * (Disease Assessment's Target Lesions / T2-FLAIR Lesions / etc.).
 *
 * Variables themselves stay in the flat `CRFForm.variables` array so the
 * existing rendering / DnD / quality-check / save paths don't have to be
 * rewritten. Sections reference variables by id and carry the structural
 * information (title, repeating flag, expected row count) so the form
 * round-trips through the server without losing layout.
 *
 * When `sections` is absent (legacy forms, plain Demographics-style forms
 * without internal headings), the form is treated as a single
 * `kind: 'questions'` section by both the renderer and the save path —
 * no behavioral difference vs. before this field existed.
 */
interface ClientCrfSection {
  /** React key. Generated client-side; not persisted. */
  id: string;
  title: string;
  kind?: CrfSectionKind;
  /** Pre-printed row count for repeating sections. Only meaningful when
   *  `kind === 'repeating'`. Undefined = unbounded log. */
  expectedRows?: number;
  /** Instruction printed under the section heading, and the protocol
   *  section it comes from (content pass, 2026-09-14). */
  instructions?: string;
  protocolRef?: string;
  /** A question-list section completed once per item (D2). Never on a table. */
  repeat?: { max?: number; label?: string };
  /** Only on a grid (2026-09-18): its pre-printed rows, label-column header and
   *  blank write-in rows. See crfLayoutSections.ts. */
  rowEntities?: GridRow[];
  gridLabelHeader?: string;
  blankRows?: number;
  /** Ordered list of `CRFVariable.id` values that belong to this section.
   *  Variable IDs are stable CFQ-* strings minted server-side, so they
   *  survive renames and reorders within a section. Drag-reorder across
   *  sections updates membership; we never duplicate a variable across
   *  sections. */
  variableIds: string[];
}

interface LicensedInstrumentInfo {
  isLicensed: boolean;
  instrumentName: string;
  publisher: string;
  searchGrounded: boolean;
}

/**
 * A verified source form available for this suggestion. Present only when the
 * platform found exactly one confident match; absent is the common case and is
 * not an error. Deliberately carries no score or ranking — the user is being
 * told where content would come from, not how it was located.
 */
interface ReuseMatch {
  domain: string;
  sourceFormName: string;
  referenceRecordId: string;
  sourceVersion: string;
  fieldCount: number;
}

/** Provenance stamped on a form that was imported rather than generated. */
interface SourceProvenance {
  method: string;
  sourceVersion?: string;
  sourceFormName?: string;
  fieldCount?: number;
  importedAt?: string;
}

interface CRFForm {
  id: string;
  /**
   * The stored artifact's version as of the last server read (the loader's
   * `updatedAt`). Sent with every save so the server can refuse a save from a
   * copy that is out of date. See features/crf/saveConcurrency.ts.
   */
  serverUpdatedAt?: string;
  /**
   * The server built this Draft's questions from the working copy (the Builder's
   * own draft, or a form never stored). See mergeServerWithCache.
   */
  contentFromWorkingCopy?: boolean;
  name: string;
  rationale: string;
  category: FormCategory;
  status: FormStatus;
  variables: CRFVariable[] | null;
  /** Set when a verified source form is available for this suggestion. */
  reuseMatch?: ReuseMatch | null;
  /** Set once the user has imported this form from a standard. */
  sourceProvenance?: SourceProvenance;
  /** True while an import is in flight, so the buttons can disable. */
  importing?: boolean;
  /** Set when the user declined the offer; sends the form to generation. */
  reuseDeclined?: boolean;
  /**
   * Optional section grouping over `variables`. When present, each section
   * has a title + kind + variable-id list; the variables themselves stay
   * in the flat `variables` array so existing renderers and DnD logic
   * keep working unchanged. When undefined, the form behaves like a
   * legacy single-section form.
   */
  sections?: ClientCrfSection[];
  /**
   * The page structure the generator chose for this form — layout,
   * orientation, any code legends, and whether the model decided it or a
   * default was applied because the structure pass failed. Persisted via
   * saveCrfForm; see crfStructurePayload.ts. Absent on forms created or
   * imported by any path other than generation, which keeps those payloads
   * identical to what they were before this existed.
   */
  structure?: ClientStructureChoice;
  /**
   * What the content pass did for this form (2026-09-14): its provenance,
   * persisted via saveCrfForm as `contentPass`; see crfContentPayload.ts.
   * The notes themselves live on the variables and sections.
   */
  content?: ClientContentRecord;
  expanded?: boolean;
  generatingVars?: boolean;
  /** The last per-form generation failed; opening the form again retries. Not persisted. */
  generationFailed?: boolean;
  licensedInstrument?: LicensedInstrumentInfo;
  crfGenerated?: boolean;
  /**
   * Optional manual override for this form's derived code. When set, takes
   * precedence over `deriveFormCode(name)` in both the badge displayed in
   * the form list and in the collision check that gates approval. Lets the
   * user disambiguate two forms that base-derive to the same code without
   * having to rename one of them. Persisted via saveCrfForm + saveSession.
   */
  code?: string;
  /**
   * Messages of quality warnings that have been explicitly dismissed for
   * this form. Dismissals are per-form and keyed by the full warning
   * message text (which encodes both the variable and the check type).
   * Dismissed warnings are filtered out of the top-level banner, the
   * per-form quality panel, and the Approve-All blocking check.
   *
   * Use case: licensed instruments like CSSRS cannot legally change
   * their published response format, so the "check all that apply"
   * GCDMP style guidance doesn't apply. The user dismisses the error
   * on the instrument's variable and regains the Approve button.
   */
  dismissedWarnings?: string[];
  /** Storage path to a per-form clip of the source document (when extracted from a CRF packet). */
  sourceFilePath?: string;
  /** Full packet path — fallback used when per-form clipping couldn't locate this form. */
  sourcePacketPath?: string;
  /** 1-based page range in the original packet. */
  sourcePageRange?: { start: number; end: number };
  /** 'user' when sourceFilePath is a file attached with "Attach rendering". */
  renderingSource?: 'user';
}

export interface CRFSession {
  protocolTitle: string;
  protocolText: string;
  forms: CRFForm[];
  /** EDC system key at the time forms were generated, for staleness detection */
  edcSystemAtGeneration?: string;
  /**
   * Variable names whose cross-form duplication has been confirmed by the
   * user as intentional (e.g. a repeated-measures instrument like the
   * C-SSRS administered at baseline and follow-up). Confirmations suppress
   * the cross-form duplicate warning permanently and never block approval.
   *
   * Intra-form duplicates are NOT covered by this list — they are always
   * errors and must be resolved by rename or removal.
   */
  confirmedCrossFormDuplicates?: string[];
}

// Shape returned by the listCrfForms Cloud Function. Mirrors CrfForm from
// functions/src/crf/crfTypes.ts. Used to hydrate the session when a client
// opens a project without a cached localStorage session.
interface ServerCrfForm {
  id: string;
  artifactId: string;
  /** Set by loadCrfFormsForProject when the questions came from the working copy. */
  contentFromWorkingCopy?: boolean;
  name: string;
  category: string;
  status: FormStatus;
  content: {
    id: string;
    name: string;
    category: string;
    sections?: Array<{
      title: string;
      kind?: CrfSectionKind;
      expectedRows?: number;
      instructions?: string;
      protocolRef?: string;
      repeat?: { max?: number; label?: string };
      rowEntities?: GridRow[];
      gridLabelHeader?: string;
      blankRows?: number;
      questions: Array<{
        id: string;
        version: number;
        number: number;
        text: string;
        short_label?: string;
        variable: string;
        response_type: ResponseType;
        options: Array<{ label: string; value: number | string }>;
        format?: string;
        ct_codelist_ref?: string;
        ct_codelist_version?: string;
        ct_match_confidence?: 'auto' | 'needs_review' | 'user_confirmed' | 'sponsor_defined';
        guidance?: string;
        guidanceSource?: string;
        protocolRef?: string;
        isParent?: boolean;
        indent?: number;
        helpText?: string;
        required?: boolean;
        codesFrom?: string;
      }>;
    }>;
  };
  updatedAt: string;
  sourceFilePath?: string;
  sourcePacketPath?: string;
  sourcePageRange?: { start: number; end: number };
  renderingSource?: 'user';
  /** Manual form_code override persisted on the artifact, when present. */
  code?: string;
}

function serverFormToClientForm(sf: ServerCrfForm): CRFForm {
  const serverSections = sf.content.sections ?? [];
  const variables: CRFVariable[] = [];
  const sections: ClientCrfSection[] = [];

  // Walk every server section in source order — DON'T stop at sections[0].
  // Each section's questions get appended to the flat `variables[]` array
  // (preserving global order: section 1 vars, then section 2 vars, …),
  // and a parallel ClientCrfSection is built to remember the grouping +
  // kind + expectedRows so the structure round-trips through save.
  for (let sIdx = 0; sIdx < serverSections.length; sIdx++) {
    const s = serverSections[sIdx];
    const variableIds: string[] = [];
    for (const q of s.questions || []) {
      variables.push({
        id: q.id,
        varName: q.variable,
        questionText: q.text,
        ...(q.short_label ? { shortLabel: q.short_label } : {}),
        responseType: q.response_type,
        options: (q.options || []).map(storedOptionToBuilder),
        unit: q.format || '',
        ...(q.ct_codelist_ref ? { ct_codelist_ref: q.ct_codelist_ref } : {}),
        ...(q.ct_codelist_version ? { ct_codelist_version: q.ct_codelist_version } : {}),
        ...(q.ct_match_confidence ? { ct_match_confidence: q.ct_match_confidence } : {}),
        // Margin content and the dictionary's details: kept, so the next save
        // does not erase them. ONE list, checked against the server's type.
        ...carriedQuestionFields(q),
      });
      variableIds.push(q.id);
    }
    const cs: ClientCrfSection = {
      id: `sec-${sf.id}-${sIdx}`,
      title: s.title || sf.name,
      variableIds,
      ...carriedSectionFields(s),
    };
    // The section's layout — a table's row count, a grid's named rows.
    Object.assign(cs, layoutSectionFields(s));
    sections.push(cs);
  }

  // Only attach `sections` when the form actually has structure worth
  // preserving. A single untitled questions section adds noise without
  // information — leaving sections undefined makes legacy / unstructured
  // forms behave exactly as before.
  const hasMeaningfulStructure = hasSectionLayout(sections);

  return {
    serverUpdatedAt: sf.updatedAt,
    ...(sf.contentFromWorkingCopy ? { contentFromWorkingCopy: true } : {}),
    id: sf.id,
    name: sf.name,
    rationale: '',
    category: (sf.category as FormCategory) || 'other',
    status: sf.status,
    variables,
    ...(hasMeaningfulStructure ? { sections } : {}),
    ...(sf.sourceFilePath ? { sourceFilePath: sf.sourceFilePath } : {}),
    ...(sf.sourcePacketPath ? { sourcePacketPath: sf.sourcePacketPath } : {}),
    ...(sf.sourcePageRange ? { sourcePageRange: sf.sourcePageRange } : {}),
    ...(sf.renderingSource === 'user' ? { renderingSource: 'user' as const } : {}),
    ...(sf.code ? { code: sf.code } : {}),
  };
}

// ─── Quality Check Types (GCDMP compliance) ─────────────

// `QualityWarning` and the checks that produce it now live in
// features/crf/crfQualityChecks.ts, imported above — pure logic, kept where
// it can be unit-tested (`web` has no test runner of its own).

// ─── Constants ───────────────────────────────────────────

// Project-specific storage key so cached forms don't leak across projects
const getStorageKey = (projectId: string) => `crf-app-v1-${projectId}`;
const MAX_PROTOCOL_LENGTH = 50000;

const CATEGORY_COLORS: Record<FormCategory, string> = {
  screening: 'bg-purple-100 text-purple-700',
  demographics: 'bg-blue-100 text-blue-700',
  medical_history: 'bg-amber-100 text-amber-700',
  procedures: 'bg-green-100 text-green-700',
  outcomes: 'bg-cyan-100 text-cyan-700',
  safety: 'bg-red-100 text-red-700',
  administrative: 'bg-gray-100 text-gray-700',
  other: 'bg-slate-100 text-slate-700',
};

const CATEGORY_LABELS: Record<FormCategory, string> = {
  screening: 'Screening',
  demographics: 'Demographics',
  medical_history: 'Medical History',
  procedures: 'Procedures',
  outcomes: 'Outcomes',
  safety: 'Safety',
  administrative: 'Administrative',
  other: 'Other',
};

const STATUS_COLORS: Record<FormStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  in_review: 'bg-gray-100 text-gray-600',
  approved: 'bg-green-100 text-green-700',
  final: 'bg-green-100 text-green-700',
};

const STATUS_LABELS: Record<FormStatus, string> = {
  draft: 'Draft',
  in_review: 'Draft',
  approved: 'Final',
  final: 'Final',
};

const RESPONSE_TYPE_LABELS: Record<ResponseType, string> = {
  yesno: 'Yes / No',
  single_select: 'Single Select',
  multi_select: 'Multi-Select',
  numeric: 'Numeric',
  date: 'Date',
  time: 'Time',
  text: 'Free Text',
};

const RESPONSE_TYPE_COLORS: Record<ResponseType, string> = {
  yesno: 'bg-emerald-100 text-emerald-700',
  single_select: 'bg-blue-100 text-blue-700',
  multi_select: 'bg-indigo-100 text-indigo-700',
  numeric: 'bg-orange-100 text-orange-700',
  date: 'bg-pink-100 text-pink-700',
  time: 'bg-rose-100 text-rose-700',
  text: 'bg-gray-100 text-gray-600',
};

const ALL_CATEGORIES: FormCategory[] = ['screening', 'demographics', 'medical_history', 'procedures', 'outcomes', 'safety', 'administrative', 'other'];
const ALL_RESPONSE_TYPES: ResponseType[] = ['yesno', 'single_select', 'multi_select', 'numeric', 'date', 'time', 'text'];
const OPTION_BASED_TYPES: ResponseType[] = ['yesno', 'single_select', 'multi_select'];

// ─── Helpers ─────────────────────────────────────────────

function uid(): string {
  return crypto.randomUUID();
}

function saveSession(
  projectId: string,
  session: CRFSession,
  sessionVersion: VersionedDoc,
  onStale: () => void,
  persisted: PersistedFingerprint,
): void {
  const clean = buildCleanSession(session);
  // NOTHING TO WRITE when this is exactly what the page last loaded or saved.
  // Opening the page used to rewrite the whole session (the auto-save effect fires
  // once on load) and bump its stored version, which made any other open copy of
  // the page stale (measured 2026-10-11). The browser copy is skipped too: it
  // holds the same content, and an older cache loses to the stored copy on load.
  const now = Date.now();
  try {
    // updatedAt must NOT go through stripUndefined: a Date has no own keys,
    // so it arrived as {} and every load then preferred the browser copy.
    const serialised = { ...stripUndefined(clean), updatedAt: new Date(now) };
    persistSession({
      persisted,
      clean,
      version: sessionVersion,
      writeDoc: (next) => setDoc(crfBuilderSessionDoc(projectId), { ...serialised, docVersion: next }),
      marker: String(now),
      // Once a session save has been refused as stale, this browser's cached
      // copy is no longer written: it holds the refused content, and a reload
      // would prefer it (it is newer by timestamp) and write it back.
      beforeWrite: () => {
        if (sessionVersion.isStale) return;
        try {
          localStorage.setItem(getStorageKey(projectId), JSON.stringify(clean));
          localStorage.setItem(getStorageKey(projectId) + ':updatedAt', String(now));
        } catch { /* quota exceeded — ignore */ }
      },
    })
      .then((outcome) => {
        if (outcome !== 'stale') return;
        // Drop the cached copy written above for this same save: it is the
        // refused content (see the isStale guard above).
        try {
          localStorage.removeItem(getStorageKey(projectId));
          localStorage.removeItem(getStorageKey(projectId) + ':updatedAt');
        } catch { /* ignore */ }
        onStale();
      })
      .catch((err) => {
        console.warn('[CRFBuilder] failed to persist crfBuilder session to Firestore:', err);
      });
  } catch (err) {
    console.warn('[CRFBuilder] saveSession failed to prepare write:', err);
  }
}

function loadSession(projectId: string): CRFSession | null {
  try {
    const raw = localStorage.getItem(getStorageKey(projectId));
    if (!raw) return null;
    return JSON.parse(raw) as CRFSession;
  } catch {
    return null;
  }
}

/** Local-cache tag mirrored alongside the Firestore session for freshness comparison. */
function loadSessionUpdatedAt(projectId: string): number | null {
  try {
    const raw = localStorage.getItem(getStorageKey(projectId) + ':updatedAt');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Load the canonical session from Firestore (`crfBuilderSessionDoc`).
 * Returns null when the doc doesn't exist or when the read fails.
 *
 * The Firestore session is the source of truth for *local-only* fields
 * — `dismissedWarnings`, `licensedInstrument`, `rationale`, and
 * `confirmedCrossFormDuplicates` — that aren't round-tripped to the
 * canonical CRF artifacts. Without this read, those fields would be
 * tied to whichever browser the user happened to dismiss on, breaking
 * collaboration and cross-device continuity.
 */
async function loadSessionFromServer(projectId: string): Promise<{ session: CRFSession; updatedAt: number | null; docVersion: number } | null> {
  try {
    const snap = await getDoc(crfBuilderSessionDoc(projectId));
    if (!snap.exists()) return null;
    const data = snap.data() as Partial<CRFSession> & { updatedAt?: { toMillis?: () => number } | Date | string };
    if (!data || !Array.isArray((data as CRFSession).forms)) return null;
    let updatedAt: number | null = null;
    const ts = data.updatedAt;
    if (ts) {
      if (typeof ts === 'object' && 'toMillis' in ts && typeof ts.toMillis === 'function') {
        updatedAt = ts.toMillis();
      } else if (ts instanceof Date) {
        updatedAt = ts.getTime();
      } else if (typeof ts === 'string') {
        const t = Date.parse(ts);
        if (!Number.isNaN(t)) updatedAt = t;
      }
    }
    return { session: data as CRFSession, updatedAt, docVersion: readDocVersion(data) };
  } catch (err) {
    console.warn('[CRFBuilder] failed to read crfBuilder session from Firestore:', err);
    return null;
  }
}



/**
 * The study's cross-form duplicate confirmations, as stored. One field per
 * confirmed variable name; an absent document means none.
 */
async function loadCrossFormConfirmations(projectId: string): Promise<Record<string, unknown> | null> {
  try {
    const snap = await getDoc(crfBuilderCrossFormConfirmationsDoc(projectId));
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  } catch (err) {
    console.warn('[CRFBuilder] failed to read cross-form confirmations:', err);
    return null;
  }
}

// Safe helper: Gemini sometimes returns strings instead of arrays
function toArray(val: unknown): string[] {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function analysisToProtocolText(analysis: any): string {
  if (!analysis || typeof analysis !== 'object') return '';
  const lines: string[] = [];

  try {
    if (analysis.studyTitle) lines.push(`Study Title: ${analysis.studyTitle}`);
    if (analysis.protocolNumber) lines.push(`Protocol Number: ${analysis.protocolNumber}`);

    const sd = analysis.studyDesign;
    if (sd && typeof sd === 'object') {
      lines.push('', '--- Study Design ---');
      if (sd.type) lines.push(`Type: ${sd.type}`);
      if (sd.phase) lines.push(`Phase: ${sd.phase}`);
      if (sd.blinding) lines.push(`Blinding: ${sd.blinding}`);
      if (sd.randomization) lines.push(`Randomization: ${sd.randomization}`);
    }

    const tx = analysis.treatment;
    if (tx && typeof tx === 'object') {
      lines.push('', '--- Treatment ---');
      if (tx.duration) lines.push(`Duration: ${tx.duration}`);
      const arms = Array.isArray(tx.arms) ? tx.arms : [];
      arms.forEach((arm: any) => {
        lines.push(`\nArm: ${arm.name || 'Unnamed'}`);
        if (arm.description) lines.push(arm.description);
        const drugs = Array.isArray(arm.drugs) ? arm.drugs : [];
        drugs.forEach((d: any) => {
          lines.push(`  Drug: ${d.name || ''}, Dose: ${d.dose || ''}, Route: ${d.route || ''}, Frequency: ${d.frequency || ''}`);
        });
      });
    }

    const obj = analysis.objectives;
    if (obj && typeof obj === 'object') {
      lines.push('', '--- Objectives ---');
      const pri = toArray(obj.primary);
      if (pri.length) { lines.push('Primary:'); pri.forEach(o => lines.push(`  - ${o}`)); }
      const sec = toArray(obj.secondary);
      if (sec.length) { lines.push('Secondary:'); sec.forEach(o => lines.push(`  - ${o}`)); }
      const exp = toArray(obj.exploratory);
      if (exp.length) { lines.push('Exploratory:'); exp.forEach(o => lines.push(`  - ${o}`)); }
    }

    const ep = analysis.endpoints;
    if (ep && typeof ep === 'object') {
      lines.push('', '--- Endpoints ---');
      const pri = toArray(ep.primary);
      if (pri.length) { lines.push('Primary:'); pri.forEach(e => lines.push(`  - ${e}`)); }
      const sec = toArray(ep.secondary);
      if (sec.length) { lines.push('Secondary:'); sec.forEach(e => lines.push(`  - ${e}`)); }
    }

    const pop = analysis.population;
    if (pop && typeof pop === 'object') {
      lines.push('', '--- Population ---');
      if (pop.targetSize) lines.push(`Target enrollment: ${pop.targetSize}`);
      const inc = toArray(pop.inclusion);
      if (inc.length) { lines.push('Inclusion criteria:'); inc.forEach(c => lines.push(`  - ${c}`)); }
      const exc = toArray(pop.exclusion);
      if (exc.length) { lines.push('Exclusion criteria:'); exc.forEach(c => lines.push(`  - ${c}`)); }
    }

    const sched = analysis.schedule;
    if (sched && typeof sched === 'object') {
      lines.push('', '--- Schedule ---');
      if (sched.duration) lines.push(`Duration: ${sched.duration}`);
      const visits = Array.isArray(sched.visits) ? sched.visits : [];
      if (visits.length) {
        lines.push('Visits:');
        visits.forEach((v: any) => {
          const procs = Array.isArray(v.procedures) && v.procedures.length ? ` — Procedures: ${v.procedures.join(', ')}` : '';
          lines.push(`  - ${v.name || v.visit || 'Visit'}: ${v.timing || v.window || ''}${procs}`);
        });
      }
    }

    // Questionnaires / PROs — critical for CRF form identification
    const questionnaires = toArray(analysis.questionnaires);
    if (questionnaires.length) {
      lines.push('', '--- Questionnaires and Patient-Reported Outcomes ---');
      questionnaires.forEach(q => lines.push(`  - ${q}`));
    }

    // Study procedures
    const procedures = toArray(analysis.procedures);
    if (procedures.length) {
      lines.push('', '--- Study Procedures ---');
      procedures.forEach(p => lines.push(`  - ${p}`));
    }

    // Interventions / devices
    const interventions = Array.isArray(analysis.interventions) ? analysis.interventions : [];
    if (interventions.length) {
      lines.push('', '--- Interventions / Investigational Devices ---');
      interventions.forEach((inv: any) => {
        const parts = [inv.name, inv.dose, inv.route, inv.frequency, inv.duration].filter(Boolean);
        lines.push(`  - ${parts.join(' — ')}`);
      });
    }

    // Lab tests
    const labTests = toArray(analysis.labTests);
    if (labTests.length) {
      lines.push('', '--- Laboratory Tests ---');
      labTests.forEach(t => lines.push(`  - ${t}`));
    }

    // Biomarkers / data collection
    const dc = analysis.dataCollection;
    if (dc && typeof dc === 'object') {
      const biomarkers = toArray(dc.biomarkers);
      const imaging = toArray(dc.imagingStudies);
      if (biomarkers.length || imaging.length) {
        lines.push('', '--- Biomarkers and Data Collection ---');
        biomarkers.forEach(b => lines.push(`  - Biomarker: ${b}`));
        imaging.forEach(img => lines.push(`  - Imaging: ${img}`));
      }
    }

    const safety = analysis.safety;
    if (safety && typeof safety === 'object') {
      lines.push('', '--- Safety ---');
      const assess = toArray(safety.assessments);
      if (assess.length) { lines.push('Assessments:'); assess.forEach(a => lines.push(`  - ${a}`)); }
      const report = toArray(safety.reporting);
      if (report.length) { lines.push('Reporting:'); report.forEach(r => lines.push(`  - ${r}`)); }
      const aes = toArray(safety.aesOfInterest);
      if (aes.length) { lines.push('AEs of interest:'); aes.forEach(a => lines.push(`  - ${a}`)); }
      const stops = toArray(safety.stoppingRules);
      if (stops.length) { lines.push('Stopping rules:'); stops.forEach(r => lines.push(`  - ${r}`)); }
    }

    // Concomitant medications
    const conmeds = analysis.concomitantMedications;
    if (conmeds && typeof conmeds === 'object') {
      const prohibited = toArray(conmeds.prohibited);
      const required = toArray(conmeds.requiredBackground);
      if (prohibited.length || required.length) {
        lines.push('', '--- Concomitant Medications ---');
        prohibited.forEach(m => lines.push(`  - Prohibited: ${m}`));
        required.forEach(m => lines.push(`  - Required background: ${m}`));
      }
    }

    // Withdrawal criteria
    const withdrawal = toArray(analysis.withdrawalCriteria);
    if (withdrawal.length) {
      lines.push('', '--- Withdrawal Criteria ---');
      withdrawal.forEach(w => lines.push(`  - ${w}`));
    }

    const contacts = analysis.contacts;
    if (contacts && typeof contacts === 'object') {
      lines.push('', '--- Contacts ---');
      if (contacts.sponsor) lines.push(`Sponsor: ${contacts.sponsor}`);
      if (contacts.pi) lines.push(`PI: ${contacts.pi}`);
    }
  } catch (err) {
    console.error('Error converting analysis to text:', err);
  }

  return lines.join('\n');
}

// ─── Component ───────────────────────────────────────────

export default function CRFBuilder() {
  const { projectId } = useParams<{ projectId: string }>();
  // Optimistic concurrency for crfBuilder/session, which saveSession writes
  // whole on every change (utils/versionedDoc.ts). One per project.
  const sessionVersion = useMemo(
    // The marker is the save's own timestamp: a refusal caused by this page's own earlier copy of the same
    // save (acknowledgement lost, write resent) is told apart from another writer by it.
    () => (projectId
      ? versionedDocFor(crfBuilderSessionDoc(projectId), (d) => {
          const u = (d as { updatedAt?: { toMillis?: () => number } } | undefined)?.updatedAt;
          return u && typeof u.toMillis === 'function' ? String(u.toMillis()) : undefined;
        })
      : null),
    [projectId],
  );
  // Fingerprint of the session content this page last loaded or saved; saveSession skips a write
  // that would not change it (features/crf/sessionSave.ts). Null = unknown, so the next save writes.
  const persistedSessionRef = useRef<PersistedFingerprint>({ current: null });
  const canEdit = usePermission('edit');
  const canGenerate = usePermission('generate');
  const canUpload = usePermission('upload');

  // Phase
  const [phase, setPhase] = useState<AppPhase>('upload');
  const [loadingProject, setLoadingProject] = useState(true);

  // Protocol ingestion
  const [protocolTitle, setProtocolTitle] = useState('');
  const [studyShortName, setStudyShortName] = useState('');
  const [protocolText, setProtocolText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Forms
  const [forms, setForms] = useState<CRFForm[]>([]);
  // The forms as last rendered, for a bulk run that must read them after awaiting.
  const formsRef = useRef<CRFForm[]>([]);
  formsRef.current = forms;
  // True while a bulk variable run is going: a refused session save is then handled by the run itself
  // (it reloads and saves again), so the generic "changed elsewhere" toast is held back.
  const bulkRunningRef = useRef(false);
  // Optional `?view=forms|visits|schedule` selects the initial sub-view — used
  // by the Validation tab's "Take me there" to land on Form Assignments
  // (view=schedule). Read ONCE on mount (lazy initializer), so a later manual
  // tab switch is never overridden; absent/invalid param falls back to 'forms'.
  const [initialViewParams] = useSearchParams();
  const [workspaceView, setWorkspaceView] = useState<'forms' | 'visits' | 'schedule'>(() => {
    const v = initialViewParams.get('view');
    return v === 'visits' || v === 'schedule' || v === 'forms' ? v : 'forms';
  });

  // Forms whose guide page rendering is a duplicate of another form's (a
  // wrong/shared page). The CRF Instruction Guide generator writes this set to
  // dataManagement/crfRenderingStatus when it detects the collision; we badge
  // each affected form so the user can see at a glance which need re-rendering
  // — without opening forms one by one. Clears when a guide regenerates cleanly.
  const [duplicateRenderingNames, setDuplicateRenderingNames] = useState<Set<string>>(new Set());

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [newFormCategory, setNewFormCategory] = useState<FormCategory>('other');

  // Rename
  const [renamingFormId, setRenamingFormId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Inline form-code override edit
  const [editingCodeFormId, setEditingCodeFormId] = useState<string | null>(null);
  // What the badge editor showed when it opened: a close with the box
  // unchanged from this writes nothing.
  const [codeEditOpenedWith, setCodeEditOpenedWith] = useState('');
  const [codeEditValue, setCodeEditValue] = useState('');

  // Delete confirmation
  const [deletingFormId, setDeletingFormId] = useState<string | null>(null);

  // CT review modal — which form's modal is open (null = closed)
  const [ctReviewFormId, setCtReviewFormId] = useState<string | null>(null);

  // Toast — backed by the global ToastProvider in main.tsx.
  const { showToast } = useToast();
  const [expandedVarIds, setExpandedVarIds] = useState<Set<string>>(new Set());

  // Drag-and-drop reordering of variable rows. `dragVarId` is the row being
  // dragged; `dropTarget.beforeId` is the variable it will land in front of
  // (null = end of the form).
  const [dragVarId, setDragVarId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ formId: string; beforeId: string | null } | null>(null);

  // Form-list ordering: a sort mode (A–Z / form code / category / as-created / custom) plus
  // a custom drag order, persisted per project in localStorage.
  const [sortMode, setSortMode] = useState<FormSortMode>('created');
  const [customOrder, setCustomOrder] = useState<string[]>([]);
  const [dragFormId, setDragFormId] = useState<string | null>(null);
  const [dropFormTarget, setDropFormTarget] = useState<{ beforeId: string | null } | null>(null);
  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = localStorage.getItem(`crf-sort-${projectId}`);
      if (raw) {
        const p = JSON.parse(raw);
        if (isFormSortMode(p.mode)) setSortMode(p.mode);
        if (Array.isArray(p.customOrder)) setCustomOrder(p.customOrder.filter((x: unknown) => typeof x === 'string'));
      }
    } catch { /* ignore corrupt prefs */ }
  }, [projectId]);
  useEffect(() => {
    if (!projectId) return;
    try {
      localStorage.setItem(`crf-sort-${projectId}`, JSON.stringify({ mode: sortMode, customOrder }));
    } catch { /* ignore quota / disabled storage */ }
    return undefined;
  }, [projectId, sortMode, customOrder]);

  // Subscribe to the duplicate-rendering set written by the guide generator.
  useEffect(() => {
    if (!projectId) return undefined;
    const ref = doc(db, 'projects', projectId, 'dataManagement', 'crfRenderingStatus');
    return onSnapshot(
      ref,
      snap => {
        const names = (snap.data()?.duplicateForms as string[] | undefined) || [];
        setDuplicateRenderingNames(new Set(names));
      },
      () => setDuplicateRenderingNames(new Set()),
    );
  }, [projectId]);

  // The schedule, read only for the approval gate: a form-bearing assessment on
  // the schedule (a blood panel, vital signs, a questionnaire) with no form in
  // the list blocks Approve and Approve All, in plain words.
  const [scheduleDesign, setScheduleDesign] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      studyDesignConfigDoc(projectId),
      snap => setScheduleDesign(snap.exists() ? (snap.data() as Record<string, unknown>) : null),
      () => setScheduleDesign(null),
    );
  }, [projectId]);
  const scheduleGate = approvalCoverageGate({
    studyDesign: scheduleDesign,
    forms: forms.map(f => ({ name: f.name, code: f.code })),
  });

  // The protocol's own facts, read only for the approval gate (docgen pfacts,
  // 2026-10-02): a severity scale, a coding convention, a stratification factor,
  // a named questionnaire or a follow-up contact the forms do not honour holds
  // Approve and Approve All back, in plain words. Generation already repairs
  // what is deterministic; this gate holds back what is left.
  const [factAnalysis, setFactAnalysis] = useState<Record<string, unknown> | null>(null);
  const [factSetup, setFactSetup] = useState<Record<string, unknown> | null>(null);
  // Not known until both documents have answered: the gate never reports ok on nothing.
  const [factAnalysisLoaded, setFactAnalysisLoaded] = useState(false);
  const [factSetupLoaded, setFactSetupLoaded] = useState(false);
  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      protocolAnalysisLatestDoc(projectId),
      snap => { setFactAnalysis(snap.exists() ? ((snap.data()?.analysis as Record<string, unknown>) ?? null) : null); setFactAnalysisLoaded(true); },
      () => { setFactAnalysis(null); setFactAnalysisLoaded(true); },
    );
  }, [projectId]);
  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(
      doc(db, 'projects', projectId),
      snap => { setFactSetup((snap.data()?.studySetup as Record<string, unknown>) ?? null); setFactSetupLoaded(true); },
      () => { setFactSetup(null); setFactSetupLoaded(true); },
    );
  }, [projectId]);
  const factGate = useMemo(
    () => protocolFactGate({
      analysis: factAnalysis,
      studySetup: factSetup,
      studyDesign: scheduleDesign,
      forms: forms.map(f => ({ name: f.name, status: f.status, sections: f.sections, variables: f.variables })),
      loaded: factAnalysisLoaded && factSetupLoaded && !loadingProject,
    }),
    [factAnalysis, factSetup, factAnalysisLoaded, factSetupLoaded, loadingProject, scheduleDesign, forms],
  );

  /**
   * Section being edited inline. Stored as `${formId}|${sectionIdx}` so the
   * key uniquely identifies one section across the whole form list. Null
   * when no section is in edit mode. Only one section is editable at a
   * time; opening another implicitly cancels.
   */
  const [editingSectionKey, setEditingSectionKey] = useState<string | null>(null);
  const [sectionEditDraft, setSectionEditDraft] = useState<{
    title: string;
    kind: CrfSectionKind;
    expectedRows: string; // string in the input; coerced to number on save
    /** A grid's rows, one per line, and its row-label heading (§C9). */
    rowsText: string;
    gridLabelHeader: string;
  } | null>(null);

  // CRF packet upload modal
  // Which entry point opened the packet upload modal, and therefore how its
  // "single CRF" checkbox starts. Two buttons share this one modal mount —
  // "Upload CRF Packet" (empty state) and "Upload CRF" (toolbar) — so the mode
  // has to travel with the click. A bare boolean here is what let the packet
  // button open in single-form mode and merge 42 forms into one (2026-08-21).
  const [showUploadPacket, setShowUploadPacket] =
    useState<{ defaultSingleForm: boolean } | null>(null);
  const [showUploadDd, setShowUploadDd] = useState(false);
  /** "Import Data Dictionary" — the adoption flow, which creates the forms the file names. */
  const [showAdoptDd, setShowAdoptDd] = useState(false);

  // LIFO undo stack for destructive dedup actions. Each click of the
  // toolbar Undo button pops the most recent action and reverts it; older
  // actions remain until they bubble up. Capped so a marathon dedup
  // session can't grow memory without bound.
  const [undoStack, setUndoStack] = useState<Array<{ description: string; undo: () => void }>>([]);

  // Variable names whose cross-form duplication has been confirmed by the
  // user. Persisted via saveSession (round-trips localStorage + Firestore
  // session doc) so a confirmation in this session does not re-surface on
  // the next approval attempt. Intra-form duplicates are NOT eligible —
  // they always block.
  const [confirmedCrossFormDuplicates, setConfirmedCrossFormDuplicates] = useState<string[]>([]);
  // Which cross-form row has its Rename/Remove options open. Only one at a
  // time — these lists run to dozens of rows and expanding them all would
  // reproduce the wall of buttons this replaced.
  const [openCrossFixFor, setOpenCrossFixFor] = useState<string | null>(null);

  // EDC staleness detection
  const [edcSystemAtGeneration, setEdcSystemAtGeneration] = useState<string | undefined>();
  const [currentEdcSystem, setCurrentEdcSystem] = useState<string | undefined>();
  const edcStale = !!(
    edcSystemAtGeneration &&
    currentEdcSystem &&
    edcSystemAtGeneration !== currentEdcSystem &&
    currentEdcSystem !== 'unknown'
  );

  // Quality checks (GCDMP compliance). `qualityWarnings` is the raw
  // set of all computed warnings; `visibleWarnings` excludes any that
  // the user has explicitly dismissed on the owning form. All display
  // and blocking logic should use `visibleWarnings`.
  const qualityWarnings = useMemo(() => runClientQualityChecks(forms), [forms]);
  const formSummaries = useMemo(() =>
    forms.map(f => ({ id: f.id, name: f.name, category: f.category })),
    [forms]
  );

  const confirmedCrossFormSet = useMemo(
    () => new Set(confirmedCrossFormDuplicates.map(normaliseConfirmationKey)),
    [confirmedCrossFormDuplicates],
  );

  const visibleWarnings = useMemo(() => {
    return qualityWarnings.filter((w) => {
      // Confirmed cross-form duplicates: drop entirely so they don't
      // re-surface in the duplicate panel or the per-form quality list.
      if (
        w.duplicateScope === 'cross' &&
        isCrossFormDuplicateConfirmed(w.field, confirmedCrossFormSet)
      ) {
        return false;
      }
      const form =
        forms.find((f) => f.id === w.formId) ||
        forms.find((f) => f.name === w.formId);
      return !form?.dismissedWarnings?.includes(w.message);
    });
  }, [qualityWarnings, forms, confirmedCrossFormSet]);

  const confirmCrossFormDuplicate = useCallback(
    (varName: string) => {
      const key = normaliseConfirmationKey(varName);
      if (!key) return;
      setConfirmedCrossFormDuplicates((prev) => (prev.includes(key) ? prev : [...prev, key]));
      if (!projectId) return;
      // ONE FIELD in its own document, merged. In the session document a
      // confirmation was an entry in an array the next page load overwrote:
      // 43 of them were deleted twice in a minute on 2026-09-12.
      const formIds = forms
        .filter((f) => (f.variables || []).some((v) => normaliseConfirmationKey(v.varName) === key))
        .map((f) => f.id);
      void setDoc(
        crfBuilderCrossFormConfirmationsDoc(projectId),
        { [key]: buildConfirmationEntry(formIds, new Date()) },
        { merge: true },
      ).catch((err) => {
        console.warn('[CRFBuilder] failed to store cross-form confirmation:', err);
        showToast('Could not save the confirmation of "' + varName + '" - please retry', 'error');
      });
    },
    [projectId, forms, showToast],
  );

  /** Reverse a confirmation (toolbar Undo): the stored field goes too. */
  const unconfirmCrossFormDuplicates = useCallback(
    (varNames: string[]) => {
      const keys = varNames.map(normaliseConfirmationKey).filter(Boolean);
      if (!keys.length) return;
      setConfirmedCrossFormDuplicates((prev) => prev.filter((n) => !keys.includes(n)));
      if (!projectId) return;
      const removals: Record<string, unknown> = {};
      for (const k of keys) removals[k] = deleteField();
      void setDoc(crfBuilderCrossFormConfirmationsDoc(projectId), removals, { merge: true }).catch(
        (err) => console.warn('[CRFBuilder] failed to remove cross-form confirmation:', err),
      );
    },
    [projectId],
  );

  // Push an undoable action onto the LIFO stack. The toolbar Undo button
  // reverts the most recent one; repeated clicks unwind further.
  const pushUndoable = useCallback((description: string, undo: () => void) => {
    setUndoStack(prev => {
      const next = [...prev, { description, undo }];
      // Hard cap so a long dedup session doesn't balloon memory.
      return next.length > 50 ? next.slice(next.length - 50) : next;
    });
  }, []);

  const performUndo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const top = prev[prev.length - 1];
      // Run the undo fn outside of the setter's call frame so React state
      // updates inside it don't stack-queue against this one.
      setTimeout(() => top.undo(), 0);
      return prev.slice(0, -1);
    });
  }, []);

  // ── Session restore — server is the authority for phase ─────────────
  //
  // The server (artifacts collection) is the source of truth for whether
  // a project has in-progress CRF work. localStorage is only a perf cache
  // for in-progress draft edits. Phase selection MUST come from the server
  // so stale localStorage from a prior buggy state can't pin a customer in
  // the wrong screen.
  //
  // Flow:
  //   1. Ask listCrfForms what CRF artifacts exist.
  //   2. If any are approved/final → workspace, using server data
  //      (localStorage merged in only to restore unsynced draft edits,
  //      and only when localStorage and server agree on approved form ids).
  //   3. Otherwise → ready screen to run protocol generation.

  useEffect(() => {
    if (!projectId) { setLoadingProject(false); return; }
    // A different study: nothing is known to be stored yet (see persistedSessionRef).
    persistedSessionRef.current.current = null;

    (async () => {
      try {
        const projDoc = await getDoc(projectDoc(projectId));
        const projData = projDoc.exists() ? projDoc.data() : null;
        const projName = projData?.name || '';
        const projShortName = projData?.shortName || '';

        let serverForms: ServerCrfForm[] = [];
        try {
          const list = httpsCallable<{ projectId: string }, { forms: ServerCrfForm[] }>(functions, CLOUD_FUNCTIONS.LIST_CRF_FORMS);
          const resp = await list({ projectId });
          serverForms = resp.data?.forms || [];
        } catch (err) {
          console.warn('[CRFBuilder] listCrfForms failed; treating project as empty:', err);
        }

        const hasUserApproved = serverForms.some(
          (f) => f.status === 'approved' || f.status === 'final'
        );

        // If a CRF packet was uploaded (even with no approved forms yet) OR
        // the project already has any extracted forms, skip the "upload
        // protocol" screen — the user already has CRFs to work with, either
        // draft or approved. The protocol-only analysis flow is only the
        // right landing screen when *nothing* exists.
        let hasCrfPacketMarker = false;
        try {
          const packetDoc = await getDoc(crfPacketLatestDoc(projectId));
          hasCrfPacketMarker = packetDoc.exists();
        } catch { /* non-critical */ }

        /**
         * Resolve the protocol text for the workspace, from the session if it
         * has one and otherwise rebuilt from the stored analysis.
         *
         * Shared by BOTH entry paths. It used to live only in the resume path,
         * so a freshly generated CRF list arrived with none and every call to
         * generateCRFVariables was refused with "protocolText is required" —
         * 0 of 46 forms, and no Data Dictionary or study documents after it.
         */
        const resolveProtocolText = async (cachedText: unknown): Promise<string> => {
          let rebuilt = '';
          if (!(typeof cachedText === 'string' && cachedText.trim().length > 0)) {
            try {
              const paDoc = await getDoc(protocolAnalysisLatestDoc(projectId));
              const analysis = paDoc.exists() ? paDoc.data().analysis : null;
              if (analysis) rebuilt = analysisToProtocolText(analysis);
            } catch (err) {
              console.warn('[CRFBuilder] could not rebuild protocolText from analysis', err);
            }
          }
          return chooseProtocolText(cachedText, rebuilt);
        };

        if (!hasUserApproved && (hasCrfPacketMarker || serverForms.length > 0)) {
          // CRF-packet project (or project with draft forms from extraction) —
          // jump straight to the workspace with whatever draft forms exist.
          //
          // Local-only fields (`dismissedWarnings`, `licensedInstrument`,
          // `rationale`, `confirmedCrossFormDuplicates`) come from the
          // canonical Firestore session (`crfBuilderSessionDoc`), with
          // localStorage as a perf cache when it's strictly newer than
          // the server. This makes dismissals propagate across browsers
          // and collaborators instead of being tied to one device.
          const clientForms = serverForms.map(serverFormToClientForm);
          const localRaw = loadSession(projectId);
          const localTagged = localRaw ? { session: localRaw, updatedAt: loadSessionUpdatedAt(projectId) } : null;
          const serverTagged = await loadSessionFromServer(projectId);
          // The page takes its session from here on, so it records the
          // stored version its saves will be based on.
          if (serverTagged) {
            sessionVersion?.observe(serverTagged.docVersion);
            persistedSessionRef.current.current = storedSessionFingerprint(serverTagged.session);
          }
          const cached = pickFreshestSession(localTagged, serverTagged);
          const serverIds = new Set(clientForms.map(f => f.id));
          const cachedIdsMatch = cached
            && cached.forms.length === clientForms.length
            && cached.forms.every(f => serverIds.has(f.id));
          setProtocolTitle(projName);
          setStudyShortName(projShortName);
          setForms(
            cachedIdsMatch && cached
              ? mergeServerWithCache(clientForms, cached.forms)
              : clientForms,
          );
          // Confirmations come from their own document, with anything recorded
          // in the old session array still honoured.
          const confirmedKeys = confirmedKeysFrom({
            stored: await loadCrossFormConfirmations(projectId),
            legacy: cached?.confirmedCrossFormDuplicates,
          });
          if (confirmedKeys.size) setConfirmedCrossFormDuplicates([...confirmedKeys]);
          setProtocolText(await resolveProtocolText(cached?.protocolText));
          setPhase('workspace');
          setLoadingProject(false);
          return;
        }

        if (hasUserApproved) {
          // Resume in-progress project. Server forms are canonical for
          // renameable / shared fields (name, category, code, status,
          // and variables on approved artifacts). The Firestore session
          // (`crfBuilderSessionDoc`) is canonical for local-only fields
          // — licensedInstrument, dismissedWarnings, rationale,
          // confirmedCrossFormDuplicates — that aren't round-tripped to
          // artifacts. localStorage acts as a perf cache and only wins
          // when it's strictly newer than the server (typical case:
          // mid-edit changes that haven't yet round-tripped).
          const clientForms = serverForms.map(serverFormToClientForm);
          const localRaw = loadSession(projectId);
          const localTagged = localRaw ? { session: localRaw, updatedAt: loadSessionUpdatedAt(projectId) } : null;
          const serverTagged = await loadSessionFromServer(projectId);
          // The page takes its session from here on, so it records the
          // stored version its saves will be based on.
          if (serverTagged) {
            sessionVersion?.observe(serverTagged.docVersion);
            persistedSessionRef.current.current = storedSessionFingerprint(serverTagged.session);
          }
          const cached = pickFreshestSession(localTagged, serverTagged);
          const serverIds = new Set(clientForms.map(f => f.id));
          const cachedIdsMatch = cached
            && cached.forms.length === clientForms.length
            && cached.forms.every(f => serverIds.has(f.id));

          setProtocolTitle(cached?.protocolTitle || projName);
          setStudyShortName(projShortName);
          setForms(
            cachedIdsMatch && cached
              ? mergeServerWithCache(clientForms, cached.forms)
              : clientForms,
          );
          // Restore cached protocolText if present; otherwise rebuild from the
          // server-side analysis so downstream generators (generateCRFVariables)
          // don't reject with "protocolText is required" on session resume.
          setProtocolText(await resolveProtocolText(cached?.protocolText));
          if (cached?.edcSystemAtGeneration) setEdcSystemAtGeneration(cached.edcSystemAtGeneration);
          // Confirmations come from their own document, with anything recorded
          // in the old session array still honoured.
          const confirmedKeys = confirmedKeysFrom({
            stored: await loadCrossFormConfirmations(projectId),
            legacy: cached?.confirmedCrossFormDuplicates,
          });
          if (confirmedKeys.size) setConfirmedCrossFormDuplicates([...confirmedKeys]);
          setPhase('workspace');
          setLoadingProject(false);
          return;
        }

        // No approved server forms — clear any stale localStorage cache so
        // a previous broken state can't resurface, then fall through to
        // the protocol-analysis path that shows the Generate button.
        //
        // A fresh start replaces whatever session is stored, so record its
        // version: the first save then claims the stored version plus one
        // instead of being refused as if someone else had written.
        {
          const storedSession = await loadSessionFromServer(projectId);
          if (storedSession) sessionVersion?.observe(storedSession.docVersion);
        }
        try { localStorage.removeItem(getStorageKey(projectId)); } catch { /* ignore */ }

        const paDoc = await getDoc(protocolAnalysisLatestDoc(projectId));
        if (paDoc.exists()) {
          const data = paDoc.data();
          const analysis = data.analysis;
          if (analysis) {
            const title = analysis.studyTitle || data.metadata?.title || projName || '';
            const text = analysisToProtocolText(analysis);
            if (text.trim().length > 50) {
              // Protocol analysis is present — prepare inputs but DO NOT auto-run.
              // Show the 'ready' screen with a Generate button so the user
              // decides when to kick off form suggestion.
              setProtocolTitle(title);
              setStudyShortName(projShortName || analysis.shortTitle || '');
              setProtocolText(text);
              setPhase('ready');
              setLoadingProject(false);
              return;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load protocol analysis:', err);
      }
      setLoadingProject(false);
    })();
  }, [projectId]);

  // ── Load current EDC system for staleness detection ──

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const projSnap = await getDoc(projectDoc(projectId));
        const edcSystem = projSnap.data()?.studySetup?.edcSystem;
        if (edcSystem) setCurrentEdcSystem(edcSystem);
      } catch { /* non-critical */ }
    })();
  }, [projectId]);

  // ── Auto-save ───────────────────

  useEffect(() => {
    if (phase === 'workspace' && forms.length > 0 && projectId && sessionVersion) {
      saveSession(projectId, {
        protocolTitle,
        protocolText,
        // Each form carries the latest stored version this page knows of,
        // its own last save included, so a reload can tell whether the
        // working copy is still current.
        forms: forms.map((f) => {
          const base = chooseSaveBase(lastSavedVersionRef.current.get(f.id), f.serverUpdatedAt);
          return base && base !== f.serverUpdatedAt ? { ...f, serverUpdatedAt: base } : f;
        }),
        edcSystemAtGeneration,
      }, sessionVersion, () => { if (!bulkRunningRef.current) showToast(docChangedMessage('The CRF Builder workspace'), 'error'); }, persistedSessionRef.current);
    }
    // showToast is deliberately not a dependency: this effect SAVES, and must
    // run when the session changes, not when a context value is recreated.
  }, [forms, protocolTitle, protocolText, phase, projectId, edcSystemAtGeneration, sessionVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fallback to 'ready' when all forms are deleted ─────────────
  //
  // If the user removes every form (typically to recover from a bad
  // packet extraction), drop them back to the ready screen so they can
  // pick Generate CRF List or Upload CRF Packet again. Without this,
  // they'd be stranded on an empty workspace with no clear next action.
  //
  // Also clear the localStorage session and the crfPacket/latest marker:
  // both of those would otherwise route the next page load right back
  // into the empty workspace via the hydration logic above.
  //
  // The CRF-packet hydration branch above does not populate
  // `protocolText` (it routes straight to workspace), so when this
  // fallback fires we may need to rebuild protocolText from the
  // protocol analysis doc — otherwise the Generate CRF List button on
  // the ready screen stays disabled (its gate is protocolText.length
  // > 0).

  useEffect(() => {
    if (phase !== 'workspace') return;
    if (!projectId) return;
    if (forms.length !== 0) return;
    // Guard: only fire after the load effect has actually populated
    // the workspace. The initial render can briefly show forms.length=0
    // while hydration is still in flight.
    if (loadingProject) return;

    try {
      localStorage.removeItem(getStorageKey(projectId));
    } catch {
      /* ignore quota errors */
    }
    void deleteDoc(crfPacketLatestDoc(projectId)).catch(() => {
      /* non-critical — marker will still get cleared on next successful extraction */
    });
    // Note: we don't need to delete crfBuilder/session here. The
    // saveCrfForm callable removes each form from session.forms[]
    // atomically as part of the same delete that drops the artifact
    // (see functions/src/crf/saveCrfForm.ts), so by the time forms.length
    // hits 0 the session doc's forms[] is already empty. Other session
    // fields (protocolText, edcSystemAtGeneration, …) are intentionally
    // preserved — wiping them here would force the user to re-enter
    // protocol context if they immediately re-generate from the ready
    // screen.

    // If protocolText isn't set yet (the CRF-packet branch never
    // populated it), rebuild from the analysis doc so the ready
    // screen's Generate button isn't stuck disabled.
    if (protocolText.trim().length === 0) {
      void (async () => {
        try {
          const paDoc = await getDoc(protocolAnalysisLatestDoc(projectId));
          if (paDoc.exists()) {
            const analysis = paDoc.data().analysis;
            if (analysis) {
              const text = analysisToProtocolText(analysis);
              if (text.trim().length > 50) setProtocolText(text);
            }
          }
        } catch (err) {
          console.warn(
            '[CRFBuilder] could not rebuild protocolText after auto-fallback',
            err,
          );
        }
      })();
    }

    setPhase('ready');
    // Intentionally exclude protocolText from deps so this effect only
    // fires when phase / forms.length / loadingProject change, not when
    // the async setProtocolText we kick off above lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, forms.length, projectId, loadingProject]);

  // ── One-shot reconciliation ─────
  // For projects created before the canonical saveCrfForm path existed, the
  // session may have approved forms without matching artifacts. Fire the
  // reconcile Cloud Function once per project on first entry to workspace.
  // Guarded by a client-side session key so we only run it once per session
  // per project.
  const reconciledProjectRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (phase !== 'workspace' || !projectId) return;
    if (forms.length === 0) return;
    if (reconciledProjectRef.current.has(projectId)) return;
    reconciledProjectRef.current.add(projectId);

    (async () => {
      try {
        const callable = httpsCallable<
          { projectId: string },
          { upsertedCount: number; deletedCount: number }
        >(functions, CLOUD_FUNCTIONS.RECONCILE_CRF_ARTIFACTS);
        const resp = await callable({ projectId });
        const { upsertedCount, deletedCount } = resp.data || {};
        if ((upsertedCount ?? 0) > 0 || (deletedCount ?? 0) > 0) {
          console.log(
            `[CRFBuilder] reconcileCrfArtifacts: upserted ${upsertedCount}, deleted ${deletedCount}`
          );
        }
      } catch (err) {
        console.warn('[CRFBuilder] reconcileCrfArtifacts failed (non-fatal):', err);
      }
    })();
  }, [phase, projectId, forms.length]);

  // ── Form helpers ────────────────

  /**
   * Effective form code for a single form: the manual override if the user
   * has set one, otherwise the deterministic derivation from the form name.
   * Override is uppercased + trimmed so case/whitespace differences can't
   * mask a real collision.
   */
  const effectiveFormCode = useCallback(
    (form: { name: string; code?: string }): string => {
      const override = (form.code || '').trim();
      if (override) return override.toUpperCase();
      return deriveFormCode(form.name);
    },
    []
  );

  /**
   * Detect form_code collisions in a candidate set of forms (already-approved
   * + about-to-approve). Two forms collide when their EFFECTIVE codes match
   * (override-or-derived). Returns a list of collision groups so the caller
   * can build a clear blocking message.
   *
   * Why this matters: every Study Build document is generated from the Data
   * Dictionary, where each form is keyed by form_code. If two approved forms
   * derive the same code their fields collapse into one bucket and one form
   * silently disappears from downstream docs. Block at approval-time so the
   * collision is fixed at its source (rename one form, or set a manual code
   * override on one of the forms).
   */
  const findFormCodeCollisions = useCallback(
    (
      candidates: { id: string; name: string; code?: string }[]
    ): Array<{ code: string; names: string[] }> => {
      const byCode = new Map<string, string[]>();
      for (const c of candidates) {
        const code = effectiveFormCode(c);
        if (!code) continue;
        if (!byCode.has(code)) byCode.set(code, []);
        byCode.get(code)!.push(c.name);
      }
      const collisions: Array<{ code: string; names: string[] }> = [];
      for (const [code, names] of byCode.entries()) {
        if (names.length > 1) collisions.push({ code, names });
      }
      return collisions;
    },
    [effectiveFormCode]
  );

  // Optimistic concurrency (2026-09-10, FOG project): a page loaded before a
  // dictionary adoption saved the Adverse Event form nine minutes after it and
  // put the whole old form back. These hold, per form: the save queue, the
  // version the last save returned, and whether the server refused it as stale.
  const saveQueueRef = useRef(new Map<string, Promise<unknown>>());
  const lastSavedVersionRef = useRef(new Map<string, string>());
  const staleFormsRef = useRef(new Set<string>());

  // Save a form through the canonical server-side saveCrfForm Cloud Function.
  // This is the only place in the client that writes CRF state to Firestore
  // artifacts — direct client writes to artifacts/crf-* are denied by rules.
  // CFQ-* ID reconciliation, artifact/session atomicity, and draft cleanup
  // all happen server-side in saveCrfFormInternal.
  const syncFormToFirestore = useCallback(async (
    form: CRFForm,
    newStatus: string,
    opts?: { intent?: 'save' | 'delete' },
  ) => {
    if (!projectId) return;
    const refusedAsStale = () => Object.assign(new Error(FORM_CHANGED_MESSAGE), {
      code: 'failed-precondition', details: { reason: 'form-changed' },
    });
    const doSave = async () => {
      // A form the server has already refused as stale is not written again
      // from this page: every further save would claim the same old base.
      if (staleFormsRef.current.has(form.id)) throw refusedAsStale();
      const callable = httpsCallable(functions, CLOUD_FUNCTIONS.SAVE_CRF_FORM);
      const buildPayload = () => {
        const allVars = form.variables || [];
        const base = {
          id: form.id,
          name: form.name,
          category: form.category,
          status: newStatus,
          // Persist manual code override (or empty string to clear).
          ...(form.code !== undefined ? { code: form.code } : {}),
          ...(opts?.intent ? { intent: opts.intent } : {}),
          // The structure the generator chose, when there was one. Yields
          // {} for every other path, so their payloads are unchanged.
          ...structurePayloadFields(form.structure),
          // What the content pass did, when it ran. Same contract.
          ...contentPayloadFields(form.content),
        };
        if (Array.isArray(form.sections) && form.sections.length > 0) {
          // Bucket the flat variables by their assigned section, but
          // preserve the flat-array order WITHIN each bucket so that
          // user reorders inside a section round-trip correctly.
          // Unassigned variables (newly added, no section binding yet)
          // tail-append to the last section so nothing is lost.
          const varToSection = new Map<string, number>();
          form.sections.forEach((s, idx) => {
            s.variableIds.forEach((id) => varToSection.set(id, idx));
          });
          const buckets: CRFVariable[][] = form.sections.map(() => []);
          for (const v of allVars) {
            const sIdx = varToSection.get(v.id);
            const target =
              sIdx !== undefined && sIdx < buckets.length
                ? sIdx
                : buckets.length - 1;
            buckets[target].push(v);
          }
          const sections = form.sections.map((s, idx) => ({
            title: s.title,
            // A table's row count, a grid's named rows (2026-09-18).
            ...layoutSectionFields(s),
            ...carriedSectionFields(s),
            variables: buckets[idx],
          }));
          return { ...base, sections };
        }
        // Legacy single-section path: send flat variables.
        return { ...base, variables: allVars };
      };
      // Claim the version this copy is based on: the later of what this page's
      // last save returned and what its last server read said. Omitted for a
      // form the server has never seen, which the server does not check.
      const base = chooseSaveBase(lastSavedVersionRef.current.get(form.id), form.serverUpdatedAt);
      const send = () => callable({
        projectId,
        form: buildPayload(),
        ...(base !== undefined ? { baseUpdatedAt: base } : {}),
      });
      // A delete also strips the form from crfBuilder/session on the server,
      // moving the session's version on. Queue it with this page's session
      // saves and adopt the move, or the page's next session save would be
      // refused for a change it asked for.
      const res = opts?.intent === 'delete' && sessionVersion
        ? await sessionVersion.exclusive(async () => {
            const r = await send();
            const moved = (r.data as { sessionVersion?: { before?: unknown; after?: unknown } } | null)?.sessionVersion;
            if (moved && typeof moved.before === 'number' && typeof moved.after === 'number') {
              sessionVersion.adoptIfCurrent(moved.before, moved.after);
            }
            return r;
          })
        : await send();
      const written = (res.data as { updatedAt?: unknown } | null)?.updatedAt;
      if (typeof written === 'string') lastSavedVersionRef.current.set(form.id, written);
    };
    // One save per form at a time. Two edits in quick succession would
    // otherwise leave with the same base, and the second would be refused for
    // a change this very page made.
    const run = enqueueSave(saveQueueRef.current, form.id, doSave);
    try {
      await run;
    } catch (err) {
      console.error('[CRFBuilder] saveCrfForm failed:', err);
      if (isFormChangedError(err)) {
        // Said once per form. Nothing was written, and the page's copy is
        // older than the stored one, so the only safe next step is a reload.
        if (!staleFormsRef.current.has(form.id)) {
          staleFormsRef.current.add(form.id);
          showToast(FORM_CHANGED_MESSAGE, 'error');
        }
      } else {
        // Surface the server's actual message when it is meaningful — the
        // most common case is the project-wide name-uniqueness check
        // ("Another form in this project is already named …"). The
        // generic fallback covers transient network failures.
        const httpsErr = err as { code?: string; message?: string };
        // Either spelling of the code: the web SDK reports 'functions/already-exists'.
        if (isAlreadyExistsError(err) && httpsErr.message) {
          showToast(httpsErr.message, 'error');
        } else {
          showToast('Failed to save form — please retry', 'error');
        }
      }
      throw err;
    }
  }, [projectId, showToast, sessionVersion]);

  const updateForm = useCallback((formId: string, updates: Partial<CRFForm>) => {
    setForms(prev => {
      const form = prev.find(f => f.id === formId);
      const next = prev.map(f => f.id === formId ? { ...f, ...updates } : f);
      if (form) {
        // Fire-and-forget the server write. Errors surface via toast.
        // saveCrfForm is idempotent — calling it on unchanged state is safe.
        const merged = { ...form, ...updates };
        void syncFormToFirestore(merged, merged.status || 'draft');
      }
      return next;
    });
  }, [syncFormToFirestore]);

  /**
   * Functional-update helper. Unlike `updateForm`, which reads `updates` from
   * the render-time closure, this passes the *latest* form from `prev` into
   * the transform so rapid sibling clicks can't stomp each other's writes.
   *
   * The dedup UI has to use this form because sibling "Remove from X" /
   * "Rename duplicates" buttons all target the same form: without the
   * functional read, a second click's stale `targetForm.variables` would
   * re-add the variable the first click just removed (see bug where toasts
   * fired but duplicates stayed).
   */
  const applyFormUpdate = useCallback((
    match: (form: CRFForm) => boolean,
    transform: (form: CRFForm) => CRFForm,
  ) => {
    setForms(prev => {
      const idx = prev.findIndex(match);
      if (idx < 0) return prev;
      const updated = transform(prev[idx]);
      void syncFormToFirestore(updated, updated.status || 'draft');
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
  }, [syncFormToFirestore]);

  const removeForm = useCallback((formId: string) => {
    // Capture the form before filtering it out of state so we can still
    // instruct the server to delete its artifact. Without this, reload
    // would resurrect the form from `projects/{id}/artifacts/crf-{id}`.
    const target = forms.find(f => f.id === formId);
    const targetIndex = forms.findIndex(f => f.id === formId);
    setForms(prev => prev.filter(f => f.id !== formId));
    setDeletingFormId(null);
    if (!target) return;

    // Explicit delete intent — saveCrfFormInternal would otherwise treat
    // this as a draft save and (because CRF-packet forms always carry
    // source linkage) preserve the artifact in the database. Wait on the
    // callable so we can detect failures: if the server-side delete
    // fails, restore the form to local state at its original index so
    // the user sees that it didn't actually delete instead of finding it
    // resurrected on next reload.
    (async () => {
      try {
        await syncFormToFirestore({ ...target, status: 'draft' }, 'draft', { intent: 'delete' });
        showToast('Form removed', 'info');
      } catch (err) {
        console.error('[CRFBuilder] delete failed for', formId, err);
        setForms(prev => {
          // Re-insert at original position if it isn't already back.
          if (prev.some(f => f.id === formId)) return prev;
          const next = [...prev];
          const insertAt = Math.min(targetIndex, next.length);
          next.splice(insertAt, 0, target);
          return next;
        });
        showToast(`Couldn't delete "${target.name}" — please retry`, 'error');
      }
    })();
  }, [forms, syncFormToFirestore, showToast]);

  /**
   * Add a new form manually, rejecting names that collide (case-insensitive,
   * whitespace-normalised) with any existing form. Two identically-named
   * forms masquerade as within-form duplicates in the quality checker and
   * have caused hours of confusion — prevent the situation at creation time.
   *
   * Returns true on success so callers can close the Add-Form panel only
   * when the add actually happened.
   */
  const tryAddForm = useCallback((name: string, category: FormCategory): boolean => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    const normalized = trimmed.toLowerCase().replace(/\s+/g, ' ');
    const collision = forms.some(f => f.name.trim().toLowerCase().replace(/\s+/g, ' ') === normalized);
    if (collision) {
      showToast(`A form named "${trimmed}" already exists. Pick a different name.`, 'error');
      return false;
    }
    setForms(prev => [...prev, {
      id: uid(),
      name: trimmed,
      rationale: 'Manually added',
      category,
      status: 'draft',
      variables: null,
      // A form carries its code from the moment it exists (Step 2,
      // 2026-09-14): the badge value, or the letter alternative if taken.
      code: codeForNewForm(forms, trimmed),
    }]);
    showToast('Form added', 'success');
    return true;
  }, [forms, showToast]);

  // Dismiss / restore quality warnings. Dismissals are stored on the
  // form itself (`dismissedWarnings`) and persist via the auto-save
  // useEffect that watches `forms`. The dismiss key is the full warning
  // message text — unique per (variable, check type) and stable across
  // renders.
  const dismissWarning = useCallback(
    (formIdOrName: string, message: string) => {
      const form =
        forms.find((f) => f.id === formIdOrName) ||
        forms.find((f) => f.name === formIdOrName);
      if (!form) return;
      const existing = form.dismissedWarnings || [];
      if (existing.includes(message)) return;
      updateForm(form.id, { dismissedWarnings: [...existing, message] });
      showToast('Quality warning dismissed for this form', 'info');
    },
    [forms, updateForm, showToast]
  );

  const restoreWarning = useCallback(
    (formIdOrName: string, message: string) => {
      const form =
        forms.find((f) => f.id === formIdOrName) ||
        forms.find((f) => f.name === formIdOrName);
      if (!form) return;
      const existing = form.dismissedWarnings || [];
      if (!existing.includes(message)) return;
      updateForm(form.id, {
        dismissedWarnings: existing.filter((m) => m !== message),
      });
      showToast('Quality warning restored', 'info');
    },
    [forms, updateForm, showToast]
  );

  const updateVariable = useCallback((formId: string, varId: string, updates: Partial<CRFVariable>) => {
    setForms(prev => prev.map(f => {
      if (f.id !== formId || !f.variables) return f;
      return { ...f, variables: f.variables.map(v => v.id === varId ? { ...v, ...updates } : v) };
    }));
  }, []);

  // Fetch a signed URL for the form's original source document and open it
  // in a new tab. For extracted CRF-packet forms this is usually a per-form
  // PDF clip; when the locator couldn't find the form, the server falls
  // back to the full packet and the UI explains that.
  const downloadFormSource = useCallback(async (
    formId: string,
    opts?: { preferOriginalPacket?: boolean },
  ) => {
    if (!projectId) return;
    try {
      const call = httpsCallable<
        { projectId: string; formId: string; preferOriginalPacket?: boolean },
        { url: string; isClip: boolean; pageRange: { start: number; end: number } | null; filename?: string }
      >(functions, CLOUD_FUNCTIONS.GET_CRF_SOURCE_URL);
      const res = await call({
        projectId,
        formId,
        // "Original upload" passes this so the backend returns the true upload
        // (sourcePacketPath), never the blank render that may have overwritten
        // sourceFilePath. "Download PDF" omits it and gets the rendered PDF.
        ...(opts?.preferOriginalPacket ? { preferOriginalPacket: true } : {}),
      });
      const { url, isClip, pageRange } = res.data || ({} as any);
      if (!url) {
        showToast('No source document linked to this form', 'error');
        return;
      }
      window.open(url, '_blank', 'noopener');
      if (!isClip) {
        showToast(
          pageRange
            ? `Opened the full packet — this form is on page ${pageRange.start}${pageRange.end !== pageRange.start ? `–${pageRange.end}` : ''}.`
            : 'Opened the full packet — couldn\'t locate this specific form.',
          'info',
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Couldn't load original: ${msg}`, 'error');
    }
  }, [projectId, showToast]);

  // Apply a CT review decision to a variable and persist through
  // saveCrfForm. Used by the CT review modal.
  const applyCtDecision = useCallback((formId: string, decision: CtDecision) => {
    const form = forms.find(f => f.id === formId);
    if (!form || !form.variables) return;
    const nextVariables = form.variables.map(v => {
      if (v.id !== decision.varId) return v;
      return {
        ...v,
        ct_codelist_ref: decision.ct_codelist_ref,
        ct_codelist_version: decision.ct_codelist_version,
        ct_match_confidence: decision.ct_match_confidence,
      };
    });
    updateForm(formId, { variables: nextVariables });
  }, [forms, updateForm]);

  const removeVariable = useCallback((formId: string, varId: string) => {
    setForms(prev => prev.map(f => {
      if (f.id !== formId || !f.variables) return f;
      return { ...f, variables: f.variables.filter(v => v.id !== varId) };
    }));
  }, []);

  // Duplicate a variable: insert a copy directly below the original (same
  // section), with a unique varName and opened in edit mode so the user can
  // immediately rename it. Pairs with drag-and-drop to make manual fix-ups
  // (e.g. a second per-column "If Yes: describe") fast.
  const duplicateVariable = useCallback((formId: string, varId: string) => {
    let snapshotForSync: CRFForm | undefined;
    setForms(prev => prev.map(f => {
      if (f.id !== formId || !f.variables) return f;
      const idx = f.variables.findIndex(v => v.id === varId);
      if (idx < 0) return f;
      const orig = f.variables[idx];
      const newId = uid();
      const existing = new Set(f.variables.map(v => (v.varName || '').toUpperCase()));
      const baseName = (orig.varName || 'VAR').slice(0, 36);
      let n = 2;
      let candidate = `${baseName}${n}`;
      while (existing.has(candidate.toUpperCase())) { n++; candidate = `${baseName}${n}`; }
      const copy: CRFVariable = {
        ...orig,
        id: newId,
        varName: candidate,
        options: orig.options.map(o => ({ ...o })),
        editing: true,
      };
      const variables = [
        ...f.variables.slice(0, idx + 1),
        copy,
        ...f.variables.slice(idx + 1),
      ];
      let sections = f.sections;
      if (Array.isArray(f.sections)) {
        sections = f.sections.map(s => {
          const si = s.variableIds.indexOf(varId);
          if (si < 0) return s;
          return { ...s, variableIds: [...s.variableIds.slice(0, si + 1), newId, ...s.variableIds.slice(si + 1)] };
        });
      }
      const updated: CRFForm = { ...f, variables, ...(sections ? { sections } : {}) };
      snapshotForSync = updated;
      return updated;
    }));
    if (snapshotForSync) void syncFormToFirestore(snapshotForSync, snapshotForSync.status || 'draft');
  }, [syncFormToFirestore]);

  /**
   * Update a section's metadata (title, kind, expectedRows). Variables
   * themselves stay flat in `form.variables` and don't move; this just
   * rewrites the `sections[sectionIdx]` entry. The save fires through
   * `syncFormToFirestore`, which already knows how to ship the new
   * section structure server-side.
   *
   * `expectedRows` is normalized to undefined when:
   *  - the user clears the input (empty string), OR
   *  - kind transitions away from 'repeating' (the field is meaningless
   *    for question-list sections and would otherwise round-trip).
   */
  const updateSectionMetadata = useCallback((
    formId: string,
    sectionIdx: number,
    updates: {
      title?: string; kind?: CrfSectionKind; expectedRows?: number | undefined;
      /** A grid's rows and row-label heading (§C9). */
      rowEntities?: GridRow[]; gridLabelHeader?: string;
    },
  ) => {
    let snapshotForSync: CRFForm | undefined;
    setForms(prev => {
      const next = prev.map(f => {
        if (f.id !== formId || !Array.isArray(f.sections)) return f;
        const sections = f.sections.map((s, i) => {
          if (i !== sectionIdx) return s;
          const merged: ClientCrfSection = {
            ...s,
            ...(updates.title !== undefined ? { title: updates.title } : {}),
            ...(updates.kind !== undefined ? { kind: updates.kind } : {}),
            ...(updates.rowEntities !== undefined ? { rowEntities: updates.rowEntities } : {}),
            ...(updates.gridLabelHeader !== undefined ? { gridLabelHeader: updates.gridLabelHeader } : {}),
          };
          // Drop expectedRows when the section isn't repeating — keeping
          // a stale row count on a questions section would surface a
          // wrong "Repeating · N rows" badge if the kind flips back later.
          const finalKind = merged.kind ?? 'questions';
          // A grid's rows belong to a grid only (2026-09-18).
          if (finalKind !== 'grid') {
            delete merged.rowEntities;
            delete merged.gridLabelHeader;
            delete merged.blankRows;
          }
          if (finalKind !== 'repeating') {
            delete merged.expectedRows;
          } else if ('expectedRows' in updates) {
            if (updates.expectedRows === undefined) {
              delete merged.expectedRows;
            } else {
              merged.expectedRows = updates.expectedRows;
            }
          }
          return merged;
        });
        const updated: CRFForm = { ...f, sections };
        snapshotForSync = updated;
        return updated;
      });
      return next;
    });
    if (snapshotForSync) {
      void syncFormToFirestore(snapshotForSync, snapshotForSync.status || 'draft');
    }
  }, [syncFormToFirestore]);

  const moveVariable = useCallback((formId: string, varId: string, direction: 'up' | 'down') => {
    setForms(prev => prev.map(f => {
      if (f.id !== formId || !f.variables) return f;
      const vars = [...f.variables];
      const idx = vars.findIndex(v => v.id === varId);
      if (idx < 0) return f;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= vars.length) return f;
      [vars[idx], vars[targetIdx]] = [vars[targetIdx], vars[idx]];
      return { ...f, variables: vars };
    }));
  }, []);

  // Drag-and-drop reorder: move `draggedId` so it sits before `beforeId`
  // (null = end), updating cross-section membership. Persist via
  // syncFormToFirestore — the artifact save ships `sections` (the flat order +
  // membership), which is what makes a cross-section move stick.
  const reorderVariable = useCallback((formId: string, draggedId: string, beforeId: string | null) => {
    let snapshotForSync: CRFForm | undefined;
    setForms(prev => prev.map(f => {
      if (f.id !== formId || !f.variables) return f;
      const { variables, sections } = moveVariableBefore(f.variables, f.sections, draggedId, beforeId);
      const updated: CRFForm = { ...f, variables, ...(sections ? { sections } : {}) };
      snapshotForSync = updated;
      return updated;
    }));
    if (snapshotForSync) {
      void syncFormToFirestore(snapshotForSync, snapshotForSync.status || 'draft');
    }
  }, [syncFormToFirestore]);

  const addVariable = useCallback((formId: string) => {
    const newVar: CRFVariable = {
      id: uid(),
      varName: '',
      questionText: '',
      responseType: 'text',
      options: [],
      unit: '',
      editing: true,
    };
    setForms(prev => prev.map(f => {
      if (f.id !== formId) return f;
      return { ...f, variables: [...(f.variables || []), newVar] };
    }));
  }, []);

  // ── File handling ───────────────

  const handleFile = async (file: File) => {
    try {
      const nameWithoutExt = file.name.replace(/\.(docx|txt)$/i, '').replace(/_/g, ' ');
      setProtocolTitle(nameWithoutExt);

      if (file.name.toLowerCase().endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        setProtocolText(result.value.substring(0, MAX_PROTOCOL_LENGTH));
      } else {
        const text = await file.text();
        setProtocolText(text.substring(0, MAX_PROTOCOL_LENGTH));
      }
    } catch (err) {
      showToast(`Failed to read file: ${(err as Error).message}`, 'error');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.toLowerCase().endsWith('.docx') || file.name.toLowerCase().endsWith('.txt'))) {
      handleFile(file);
    } else {
      showToast('Please drop a .docx or .txt file', 'error');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  // ── AI: Suggest forms ───────────

  const analyzeProtocol = async () => {
    setPhase('analyzing');
    try {
      const suggestForms = httpsCallable(functions, CLOUD_FUNCTIONS.SUGGEST_CRF_FORMS);
      const result = await suggestForms({ protocolText, projectId });
      const { forms: suggested } = result.data as {
        forms: Array<{
          name: string;
          rationale: string;
          category: FormCategory;
          reuseMatch?: ReuseMatch | null;
        }>;
      };

      const newForms: CRFForm[] = suggested.map(f => ({
        id: uid(),
        name: f.name,
        rationale: f.rationale,
        category: f.category,
        status: 'draft' as FormStatus,
        variables: null,
        expanded: false,
        generatingVars: false,
        reuseMatch: f.reuseMatch ?? null,
      }));

      // Capture EDC system at generation time for staleness detection
      if (projectId) {
        try {
          const projSnap = await getDoc(projectDoc(projectId));
          const edcSystem = projSnap.data()?.studySetup?.edcSystem;
          if (edcSystem) {
            setEdcSystemAtGeneration(edcSystem);
            setCurrentEdcSystem(edcSystem); // Also refresh current
          }
        } catch { /* non-critical */ }
      }

      // Assign non-colliding codes NOW, not at approval. On 2026-08-28
      // "Screening Visit" and "Scheduled Visit" both derived SV and approval
      // refused — a wall a customer alone cannot easily clear, since it means
      // guessing which form to rename or discovering the code badge is
      // editable. The approval-time check stays as the backstop.
      setForms(assignFormCodes(newForms));
      setPhase('workspace');
      const availableCount = newForms.filter(f => f.reuseMatch).length;
      showToast(
        availableCount > 0
          ? `${newForms.length} forms suggested — ${availableCount} available from a published standard`
          : `${newForms.length} forms suggested`,
        'success',
      );
    } catch (err) {
      console.error('Error analyzing protocol:', err);
      showToast(`Analysis failed: ${(err as Error).message}`, 'error');
      setPhase('upload');
    }
  };

  // ── Use a published standard ──────────────────────────────────────────
  //
  // Every import is one explicit click on one form. There is deliberately no
  // "import all": bulk import is exactly the unattended behaviour the spec
  // forbids, and it would put content into a study nobody looked at.

  const adoptStandardForm = async (formId: string) => {
    const form = forms.find(f => f.id === formId);
    if (!form?.reuseMatch || form.importing || !projectId) return;

    updateForm(formId, { importing: true });
    try {
      const importFn = httpsCallable(functions, CLOUD_FUNCTIONS.IMPORT_REFERENCE_FORM);
      const result = await importFn({
        projectId,
        formName: form.name,
        formCategory: form.category,
        formId: form.id,
        userConfirmed: true,
      });
      const data = result.data as {
        status: string;
        detail: string;
        fieldCount?: number;
      };

      if (data.status !== 'imported') {
        // 'alreadyExists' is the never-replace guard doing its job, so it is
        // reported as-is rather than as a failure.
        updateForm(formId, { importing: false });
        showToast(data.detail, data.status === 'alreadyExists' ? 'warning' : 'error');
        return;
      }

      // Re-read the stored form so the card shows exactly what was written,
      // rather than a client-side guess at it.
      const list = httpsCallable<{ projectId: string }, { forms: ServerCrfForm[] }>(
        functions, CLOUD_FUNCTIONS.LIST_CRF_FORMS,
      );
      const resp = await list({ projectId });
      const stored = (resp.data?.forms || []).find(sf => sf.id === form.id);

      updateForm(formId, {
        importing: false,
        reuseMatch: form.reuseMatch,
        // The import wrote the form server-side; the save updateForm is
        // about to make must claim THAT version, not the one loaded earlier.
        ...(stored ? { serverUpdatedAt: stored.updatedAt } : {}),
        sourceProvenance: {
          method: 'reuse',
          sourceVersion: form.reuseMatch.sourceVersion,
          sourceFormName: form.reuseMatch.sourceFormName,
          fieldCount: data.fieldCount,
          importedAt: new Date().toISOString(),
        },
        variables: stored
          ? (stored.content?.sections || [])
              .flatMap(section => section.questions || [])
              .map(q => ({
                id: uid(),
                varName: q.variable,
                questionText: q.text,
                ...(q.short_label ? { shortLabel: q.short_label } : {}),
                responseType: q.response_type,
                options: (q.options || []).map(importedOptionToBuilder),
                unit: q.format || '',
                // The same carried list as read-back, so an imported form's
                // notes and dictionary details survive the next save.
                ...carriedQuestionFields(q),
              }))
          : form.variables,
      });
      showToast(
        `${form.name} added from ${form.reuseMatch.sourceVersion} — ${data.fieldCount} fields`,
        'success',
      );
    } catch (err) {
      updateForm(formId, { importing: false });
      showToast(`Could not add ${form.name}: ${(err as Error).message}`, 'error');
    }
  };

  /**
   * Declining is a first-class choice, not a dismissal: it clears the offer and
   * leaves the form to the normal generation path, unchanged.
   */
  const declineStandardForm = (formId: string) => {
    updateForm(formId, { reuseDeclined: true, reuseMatch: null });
  };

  // ── AI: Generate variables (lazy) ──

  const generateVariables = useCallback(async (formId: string) => {
    const form = forms.find(f => f.id === formId);
    if (!form || !formNeedsVariables(form) || form.generatingVars) return;

    updateForm(formId, { generatingVars: true });

    try {
      const genVars = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_CRF_VARIABLES, { timeout: CALLABLE_TIMEOUTS.LONG });
      const result = await genVars({
        formName: form.name,
        formRationale: form.rationale,
        protocolText,
        projectId,
      });
      const { variables, licensedInstrument, warning, structure, content } = result.data as {
        variables: Array<Omit<CRFVariable, 'id' | 'editing'>>;
        licensedInstrument?: LicensedInstrumentInfo;
        warning?: string;
        structure?: ClientStructureChoice;
        content?: ClientContentResult;
      };

      const varsWithIds: CRFVariable[] = variables.map(v => ({
        ...v,
        id: uid(),
        options: v.options || [],
        unit: v.unit || '',
      }));

      let updates: Partial<CRFForm> = { variables: varsWithIds, generatingVars: false };
      if (licensedInstrument) {
        updates.licensedInstrument = licensedInstrument;
      }
      if (structure) updates.structure = structure;
      // The layout the structure pass chose — grids with their rows, tables
      // with their row counts — becomes the form's sections (2026-09-18).
      // Before this it stayed on `structure`, unread, and the next save stored
      // a flat list. The old sections are replaced either way: they name the
      // previous generation's variable ids, which no longer exist.
      updates.sections = sectionsFromStructure(varsWithIds, structure) as ClientCrfSection[] | undefined;
      // The margin the content pass wrote, onto the variables, sections and
      // form line it belongs to; and its provenance onto the form.
      if (content) {
        const applied = applyContentToForm(
          { variables: varsWithIds, sections: updates.sections, structure: updates.structure },
          content,
        );
        updates = {
          ...updates,
          variables: applied.variables as CRFVariable[],
          ...(applied.sections ? { sections: applied.sections as ClientCrfSection[] } : {}),
          ...(applied.structure ? { structure: applied.structure as ClientStructureChoice } : {}),
          content: applied.content,
        };
      }
      updateForm(formId, updates);
      if (warning) {
        showToast(warning, 'warning');
      } else if (licensedInstrument?.isLicensed) {
        showToast(`Licensed instrument detected: ${licensedInstrument.instrumentName}`, 'info');
      } else {
        showToast(`Variables generated for ${form.name}`, 'success');
      }
    } catch (err) {
      updateForm(formId, { generatingVars: false, generationFailed: true });
      showToast(`Failed to generate variables: ${(err as Error).message}`, 'error');
    }
  }, [forms, protocolText, updateForm, showToast]);

  // Trigger lazy loading when a form is expanded with null variables
  useEffect(() => {
    forms.forEach(form => {
      if (shouldAutoGenerate(form)) {
        generateVariables(form.id);
      }
    });
  }, [forms, generateVariables]);

  // ── Export ──────────────────────

  const [exportingFormId, setExportingFormId] = useState<string | null>(null);
  const [exportingAllCrfs, setExportingAllCrfs] = useState(false);

  // Decode a base64 DOCX and trigger a browser download.
  const downloadDocxBase64 = (base64: string, filename: string) => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    saveAs(blob, filename);
  };

  // Re-read CRF artifacts from the server and merge each form's stored
  // rendering paths (sourceFilePath / sourcePacketPath) into local state, so
  // cards reflect rendered status after a server-side render. Preserves all
  // local-only fields — only the rendering paths are updated.
  const refreshRenderingState = async () => {
    if (!projectId) return;
    try {
      const list = httpsCallable<{ projectId: string }, { forms: ServerCrfForm[] }>(functions, CLOUD_FUNCTIONS.LIST_CRF_FORMS);
      const resp = await list({ projectId });
      const byId = new Map((resp.data?.forms || []).map((sf) => [sf.id, sf]));
      setForms((prev) => prev.map((f) => {
        const sf = byId.get(f.id);
        if (!sf) return f;
        return {
          ...f,
          ...(sf.sourceFilePath ? { sourceFilePath: sf.sourceFilePath } : {}),
          ...(sf.sourcePacketPath ? { sourcePacketPath: sf.sourcePacketPath } : {}),
          ...(sf.renderingSource === 'user'
            ? { renderingSource: 'user' as const, sourcePageRange: undefined }
            : {}),
          serverUpdatedAt: sf.updatedAt,
        };
      }));
    } catch (err) {
      console.warn('[CRFBuilder] refreshRenderingState failed:', err);
    }
  };

  // Page-level: render and store every approved CRF in one call so the CRF
  // Instruction Guide can embed them. This is a render-and-persist action, not
  // a download — it persists each form's rendering server-side, then refreshes
  // local state so the cards reflect rendered status.
  const exportAllForms = async () => {
    if (!projectId || exportingAllCrfs) return;
    setExportingAllCrfs(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_ALL_CRFS_TO_DOCX, { timeout: CALLABLE_TIMEOUTS.LONG });
      const result: any = await fn({ projectId });
      const data = result.data as RenderAllResult & {
        success: boolean;
        forms: Array<{ formId: string; formName: string }>;
      };
      // Reflect the freshly-stored renderings in the cards.
      await refreshRenderingState();
      // A run that saved nothing says so once, with the reason - never a
      // green "All 0 CRFs generated and saved." (2026-09-10).
      renderAllMessages(data).forEach((m) => showToast(m.text, m.type));
    } catch (err: any) {
      showToast(`Generate All CRFs failed: ${err?.message || 'unknown error'}`, 'error');
    } finally {
      setExportingAllCrfs(false);
    }
  };

  const exportForm = async (form: CRFForm) => {
    if (!form.variables) return;
    setExportingFormId(form.id);

    // An editable Word CRF, built from the fields the platform extracted from
    // this form. For an UPLOADED form we send projectId with renderOnly (so the
    // study's date format applies) and omit formId, so the backend does NOT persist a blank render over the form's original source (no
    // shadowing — the uploaded version stays canonical, and "Original upload"
    // still returns the true PDF). Generated forms pass them so their rendering
    // is stored for the CRF Instruction Guide to embed. A rendering the user
    // attached is treated the same way: it is the form's document.
    const isUploaded = keepsOwnDocument(form);

    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_CRF_TO_DOCX);
      const result = await exportFn({
        formName: form.name,
        formCategory: form.category,
        studyTitle: protocolTitle,
        studyShortName: studyShortName,
        variables: form.variables.map(exportVariablePayload),
        ...exportStructurePayload(form.structure),
        // The form's sections, as bulk print uses them (2026-09-18).
        ...(form.sections ? { sections: sectionsToPayload(form) } : {}),
        ...(isUploaded ? { projectId, renderOnly: true } : { projectId, formId: form.id }),
      });

      const { base64, filename } = result.data as { base64: string; filename: string };
      downloadDocxBase64(base64, filename);
      showToast(`Downloaded Word CRF for ${form.name}`, 'success');
      if (!isUploaded) {
        // The generated-form call also rendered + stored a blank-CRF PDF
        // (sourceFilePath). Re-read it so "Download PDF" flips to available.
        updateForm(form.id, { crfGenerated: true });
        await refreshRenderingState();
      }
    } catch (err) {
      showToast(`Download failed: ${(err as Error).message}`, 'error');
    } finally {
      setExportingFormId(null);
    }
  };

  /**
   * The blank CRF as PDF. Always regenerates first for a platform-generated
   * form: the PDF is a conversion of the form's Word document, so regenerating
   * guarantees it reflects the form's current fields and the current renderer
   * rather than whatever was cached earlier. Uploaded forms skip that — their
   * own document is canonical and must not be overwritten.
   */
  const exportFormPdf = async (form: CRFForm) => {
    if (!form.variables) return;
    setExportingFormId(form.id);
    const isUploaded = keepsOwnDocument(form);
    try {
      if (!isUploaded) {
        const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_CRF_TO_DOCX, {
          timeout: CALLABLE_TIMEOUTS.MEDIUM,
        });
        await exportFn({
          formName: form.name,
          formCategory: form.category,
          studyTitle: protocolTitle,
          studyShortName: studyShortName,
          variables: form.variables.map(exportVariablePayload),
          ...exportStructurePayload(form.structure),
          ...(form.sections ? { sections: sectionsToPayload(form) } : {}),
          projectId,
          formId: form.id,
        });
        updateForm(form.id, { crfGenerated: true });
        await refreshRenderingState();
      }
      await downloadFormSource(form.id);
    } catch (err) {
      showToast(`Download failed: ${(err as Error).message}`, 'error');
    } finally {
      setExportingFormId(null);
    }
  };

  // ── Attach rendering ──────────────────────────────────────────────
  // One hidden file input serves every card; the card sets which form it is
  // for. The file goes to the rendering upload folder, then the server makes
  // it the form's page rendering (converting Word) without touching the
  // form's questions, variables, status or version.
  const attachInputRef = useRef<HTMLInputElement>(null);
  const [attachTargetId, setAttachTargetId] = useState<string | null>(null);
  const [attachingFormId, setAttachingFormId] = useState<string | null>(null);

  const chooseRenderingFile = (formId: string) => {
    setAttachTargetId(formId);
    if (attachInputRef.current) {
      attachInputRef.current.value = '';
      attachInputRef.current.click();
    }
  };

  const attachRendering = async (form: CRFForm, file: File) => {
    if (!projectId) return;
    const contentType = renderingUploadContentType(file.name);
    if (!contentType) {
      showToast('Attach a PDF or a Word (.docx) file.', 'error');
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      showToast('Sign in again to attach a rendering.', 'error');
      return;
    }
    setAttachingFormId(form.id);
    try {
      const idToken = await user.getIdToken();
      const urlRes = await fetch(`${functionsBase()}/getUploadUrlCore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ projectId, fileName: file.name, storagePath: 'crfRenderingUploads', contentType }),
      });
      if (!urlRes.ok) {
        const serverError = await urlRes.json().then(b => b?.error).catch(() => null);
        throw new Error(serverError || `Could not start the upload (HTTP ${urlRes.status})`);
      }
      const { uploadUrl, filePath } = await urlRes.json();
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('The file could not be uploaded');

      const attach = httpsCallable<
        { projectId: string; formId: string; uploadPath: string },
        { sourceFilePath: string; pageCount: number; converted: boolean }
      >(functions, CLOUD_FUNCTIONS.ATTACH_CRF_RENDERING, { timeout: CALLABLE_TIMEOUTS.MEDIUM });
      const res = await attach({ projectId, formId: form.id, uploadPath: filePath });
      // Local copy only: updateForm would also re-save the whole form, and an
      // attach must not save the form (2026-09-17, FOG MDS UPDRS).
      setForms(prev => prev.map(f => f.id === form.id
        ? { ...f, sourceFilePath: res.data.sourceFilePath, renderingSource: 'user' as const, sourcePageRange: undefined }
        : f));
      await refreshRenderingState();
      showToast(attachRenderingMessage(form.name, res.data), 'success');
    } catch (err) {
      showToast(`Attach rendering failed: ${(err as Error).message}`, 'error');
    } finally {
      setAttachingFormId(null);
    }
  };

  const [exportMenuFormId, setExportMenuFormId] = useState<string | null>(null);
  /** Which form's blank-CRF download menu is open (separate from the aCRF one). */
  const [crfMenuFormId, setCrfMenuFormId] = useState<string | null>(null);

  const exportAnnotatedWord = async (form: CRFForm) => {
    if (!form.variables) return;
    setExportingFormId(form.id);
    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_ANNOTATED_CRF_TO_DOCX);
      const result = await exportFn({
        formName: form.name,
        formCategory: form.category,
        studyTitle: protocolTitle,
        studyShortName: studyShortName,
        variables: form.variables.map(exportVariablePayload),
        ...exportStructurePayload(form.structure),
        ...(form.sections ? { sections: sectionsToPayload(form) } : {}),
        // Lets the backend read this study's data dictionary so the annotation
        // column shows the SDTM mapping (AE.AETERM) rather than the bare CRF
        // variable name.
        projectId,
      });
      const { base64, filename } = result.data as { base64: string; filename: string };
      const byteChars = atob(base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      saveAs(blob, filename);
      showToast(`Exported annotated CRF for ${form.name}`, 'success');
    } catch (err) {
      showToast(`Export failed: ${(err as Error).message}`, 'error');
    } finally {
      setExportingFormId(null);
    }
  };

  const exportAnnotatedPdf = async (form: CRFForm) => {
    if (!form.variables) return;
    setExportingFormId(form.id);
    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_ANNOTATED_CRF_TO_PDF);
      const result = await exportFn({
        formName: form.name,
        formCategory: form.category,
        studyTitle: protocolTitle,
        studyShortName: studyShortName,
        variables: form.variables.map(exportVariablePayload),
        ...exportStructurePayload(form.structure),
        ...(form.sections ? { sections: sectionsToPayload(form) } : {}),
        // Lets the backend read this study's data dictionary so the annotation
        // column shows the SDTM mapping (AE.AETERM) rather than the bare CRF
        // variable name.
        projectId,
      });
      const { base64, filename } = result.data as { base64: string; filename: string };
      const byteChars = atob(base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      saveAs(blob, filename);
      showToast(`Exported annotated CRF PDF for ${form.name}`, 'success');
    } catch (err) {
      showToast(`Export failed: ${(err as Error).message}`, 'error');
    } finally {
      setExportingFormId(null);
    }
  };

  // ── Raw CRF JSON export (for external data-dictionary verification) ─────
  //
  // Reads CRF artifact docs straight from Firestore — bypassing
  // loadCrfFormsForProject's normalization — so reviewers see what is
  // actually stored, not what the inputAdapter/inputAdapter-like layers
  // would emit. Outer fields mirror the verbatim Firestore values; the
  // `_raw` field on each question carries the question object as-stored.

  const [exportingRawJson, setExportingRawJson] = useState(false);
  // Surfaced inline below the Export button so a non-zero count after
  // download can't be missed. Cleared on the next click.
  const [lastExportWarningCount, setLastExportWarningCount] = useState<number | null>(null);

  const exportRawCrfJson = useCallback(async () => {
    if (!projectId || exportingRawJson) return;
    setExportingRawJson(true);
    setLastExportWarningCount(null);
    try {
      const snap = await getDocs(
        query(artifactsCollection(projectId), where('type', '==', 'crf')),
      );

      type ExportedQuestion = {
        number: string;
        label: string;
        type: string;
        options: Array<{ label: string; value: number | string }>;
        format: string;
        units: string;
        conditional: string | null;
        notDoneAllowed: boolean;
        ongoingAllowed: boolean;
        charLimit: number | null;
        notes: string;
        requiresNarrative: boolean;
        _raw: Record<string, unknown>;
      };
      type ExportedSection = {
        heading: string | null;
        kind?: 'questions' | 'repeating' | 'grid';
        expectedRows?: number;
        rowEntities?: GridRow[];
        gridLabelHeader?: string;
        blankRows?: number;
        questions: ExportedQuestion[];
      };
      type ExportedForm = {
        formCode: string;
        firestoreId: string;
        formName: string;
        pageIndex?: number;
        pageCount?: number;
        formVersion?: string;
        formPurpose?: string;
        sections: ExportedSection[];
        _error?: string;
      };
      type DataQualityWarning = {
        formCode: string;
        formName: string;
        questionNumber: string | number;
        fieldLabel: string;
        variable: string;
        issue: string;
      };

      const forms: ExportedForm[] = [];
      const dataQualityWarnings: DataQualityWarning[] = [];
      let totalFieldCount = 0;

      snap.forEach((d) => {
        const data: any = d.data() || {};
        const name: string = (data.name || '').toString();
        // doc.id is the canonical Firestore artifact ID (e.g. "crf-adverse-events").
        const firestoreId = d.id;

        // Content may be a JSON string (legacy) or an object — match
        // loadCrfFormsForProject's parser exactly so we don't silently
        // drop a form that the server would still load.
        let content: any = data.content;
        if (typeof content === 'string') {
          try { content = JSON.parse(content); } catch { content = null; }
        }

        // Short-code resolution mirrors what the CRF Builder UI badge
        // displays: a persisted manual override (`data.code`) wins; otherwise
        // derive from the display name the same way the badge does
        // (`deriveFormCode(name)` — produces "AE", "CMP", "HEMA", …).
        // The slug (`content.id` / doc.id) is NEVER used as the form code —
        // it's the document identifier, not the form's short code.
        const displayName: string = (content?.name || name || '').toString();
        const formCode: string = ((data.code || '').toString().trim() || deriveFormCode(displayName)) || '';

        if (!content || typeof content !== 'object') {
          forms.push({
            formCode,
            firestoreId,
            formName: displayName || 'Unnamed Form',
            sections: [],
            _error: 'content missing or unparseable',
          });
          return;
        }

        const formNameOut: string = displayName || 'Unnamed Form';

        const sectionsRaw: any[] = Array.isArray(content.sections) ? content.sections : [];
        const sections: ExportedSection[] = sectionsRaw.map((s: any) => {
          const questionsRaw: any[] = Array.isArray(s?.questions) ? s.questions : [];
          // Sort by display_order when present; otherwise preserve doc order.
          const hasDisplayOrder = questionsRaw.some(
            (q) => typeof q?.display_order === 'number',
          );
          const ordered = hasDisplayOrder
            ? [...questionsRaw].sort(
                (a: any, b: any) =>
                  (typeof a?.display_order === 'number' ? a.display_order : Number.MAX_SAFE_INTEGER) -
                  (typeof b?.display_order === 'number' ? b.display_order : Number.MAX_SAFE_INTEGER),
              )
            : questionsRaw;

          const questions: ExportedQuestion[] = ordered.map((q: any) => {
            totalFieldCount += 1;
            const opts = Array.isArray(q?.options) ? q.options : [];
            const rawType = typeof q?.response_type === 'string' ? q.response_type : '';
            const processedType = rawType;
            const label = typeof q?.text === 'string' ? q.text : (q?.label || '');
            const variable = typeof q?.variable === 'string' ? q.variable : '';
            const number = q?.number != null ? String(q.number) : '';

            // ── Data-quality scans ──
            // (a) select/multi-select fields with zero options. Same gap
            //     that the CRF Builder UI surfaces as "0 options"; exposing
            //     it in the export means downstream verification can flag
            //     missing controlled vocabularies (e.g. AECAT for CTCAE
            //     system organ class).
            if ((rawType === 'single_select' || rawType === 'multi_select') && opts.length === 0) {
              dataQualityWarnings.push({
                formCode,
                formName: formNameOut,
                questionNumber: number,
                fieldLabel: label,
                variable,
                issue: 'Single/multi-select field has no options defined',
              });
            }
            // (b) Defensive: raw vs processed type mismatch. The current
            //     mapping is identity, so this never fires today — but
            //     it's cheap insurance if a future transformation lands
            //     here without the warning being updated alongside.
            if (
              (rawType === 'single_select' || rawType === 'multi_select') &&
              rawType !== processedType
            ) {
              dataQualityWarnings.push({
                formCode,
                formName: formNameOut,
                questionNumber: number,
                fieldLabel: label,
                variable,
                issue: 'Field type mismatch between raw and processed',
              });
            }

            return {
              number,
              label,
              type: processedType,
              options: opts.map((o: any) => ({
                label: String(o?.label ?? ''),
                value: o?.value as number | string,
              })),
              format: typeof q?.format === 'string' ? q.format : '',
              units: typeof q?.unit === 'string' ? q.unit : '',
              conditional: typeof q?.conditional === 'string' && q.conditional
                ? q.conditional
                : null,
              notDoneAllowed: q?.not_done_option === true || q?.notDoneAllowed === true,
              ongoingAllowed:
                q?.response_type === 'date_or_continuing' || q?.ongoingAllowed === true,
              charLimit: typeof q?.charLimit === 'number' ? q.charLimit : null,
              notes:
                (typeof q?.completionNote === 'string' && q.completionNote) ||
                (typeof q?.notes === 'string' && q.notes) ||
                '',
              requiresNarrative:
                q?.response_type === 'sequence' ||
                q?.requiresNarrative === true ||
                q?.skip_instruction != null,
              _raw: q as Record<string, unknown>,
            };
          });

          const out: ExportedSection = {
            heading: typeof s?.title === 'string' ? s.title : null,
            questions,
          };
          // The section's layout, grids included (2026-09-18).
          Object.assign(out, layoutSectionFields(s ?? {}));
          return out;
        });

        const f: ExportedForm = {
          formCode,
          firestoreId,
          formName: formNameOut,
          sections,
        };
        if (typeof content.pageIndex === 'number') f.pageIndex = content.pageIndex;
        if (typeof content.pageCount === 'number') f.pageCount = content.pageCount;
        if (typeof content?.footer?.version === 'string') f.formVersion = content.footer.version;
        else if (typeof content.formVersion === 'string') f.formVersion = content.formVersion;
        if (typeof content.instructions === 'string') f.formPurpose = content.instructions;
        else if (typeof content.description === 'string') f.formPurpose = content.description;
        forms.push(f);
      });

      forms.sort((a, b) => a.formName.localeCompare(b.formName));

      const payload = {
        exportedAt: new Date().toISOString(),
        studyId: projectId,
        studyShortTitle: studyShortName || '',
        protocolTitle: protocolTitle || '',
        formCount: forms.length,
        totalFieldCount,
        dataQualityWarnings,
        forms,
      };

      const safeShort = (studyShortName || protocolTitle || 'study')
        .replace(/[^A-Za-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'study';
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `${safeShort}_CRF_Export_${dateStr}.json`;

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      setLastExportWarningCount(dataQualityWarnings.length);
      showToast(`Exported ${forms.length} CRF forms (${totalFieldCount} fields)`, 'success');
    } catch (err) {
      showToast(`Export failed: ${(err as Error).message}`, 'error');
    } finally {
      setExportingRawJson(false);
    }
  }, [projectId, exportingRawJson, studyShortName, protocolTitle, showToast]);

  // ── New session ─────────────────

  const resetSession = () => {
    if (!confirm('Start a new session? All current work will be cleared.')) return;
    if (projectId) localStorage.removeItem(getStorageKey(projectId));
    setProtocolTitle('');
    setProtocolText('');
    setForms([]);
    setPhase('upload');
  };

  // ── Bulk Variable Generation ────────────────────

  // Protocol review (flag only; never blocks approval). Results are read from
  // Firestore, so a client-side timeout is not a failure.
  const { summary: reviewSummary, reviewForms } = useProtocolReview(projectId);
  const [reviewStarting, setReviewStarting] = useState(false);
  const [reviewStillRunningNote, setReviewStillRunningNote] = useState(false);
  const reviewSummaryRunning = reviewSummary?.status === 'running' && !isStaleRunning(reviewSummary, new Date());
  const reviewRunning = reviewStarting || reviewSummaryRunning;
  const runProtocolReview = async (continueRun = false) => {
    if (!projectId) return;
    setReviewStarting(true);
    setReviewStillRunningNote(false);
    try {
      const call = httpsCallable(functions, CLOUD_FUNCTIONS.REVIEW_CRF_AGAINST_PROTOCOL, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      await call(continueRun ? { projectId, continueRun: true } : { projectId });
    } catch (err) {
      if (isStillRunningError(err)) setReviewStillRunningNote(true);
      else showToast(err instanceof Error && err.message ? err.message : 'The protocol review could not be started. Please try again.', 'error');
    } finally {
      setReviewStarting(false);
    }
  };

  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentForm: '' });
  const [bulkLog, setBulkLog] = useState<Array<{ form: string; status: 'success' | 'error' | 'skipped' | 'warning'; message: string }>>([]);
  const bulkLogEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bulkLogEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [bulkLog]);

  const generateAllVariables = async () => {
    setBulkConfirmOpen(false);
    setBulkGenerating(true);
    setBulkLog([]);

    // Only generate for forms that don't have variables yet
    const formsToGenerate = forms.filter(formNeedsVariables);
    const total = formsToGenerate.length;
    setBulkProgress({ current: 0, total, currentForm: '' });

    if (total === 0) {
      setBulkLog([{ form: '', status: 'skipped', message: 'All forms already have variables generated.' }]);
      setBulkGenerating(false);
      return;
    }

    // What this run generated, by form id. If a session save is refused as stale part-way through, the
    // page stops saving; these are re-applied to the stored session afterwards (features/crf/bulkVariableMerge.ts).
    const generatedByForm = new Map<string, GeneratedForm<CRFForm>>();
    bulkRunningRef.current = true;

    for (let i = 0; i < formsToGenerate.length; i++) {
      const form = formsToGenerate[i];
      setBulkProgress({ current: i, total, currentForm: form.name });
      setBulkLog(prev => [...prev, { form: form.name, status: 'success', message: `Generating variables...` }]);

      updateForm(form.id, { generatingVars: true });

      try {
        const genVars = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_CRF_VARIABLES, { timeout: CALLABLE_TIMEOUTS.LONG });
        const result = await genVars({
          formName: form.name,
          formRationale: form.rationale,
          protocolText,
          projectId,
        });
        const { variables, licensedInstrument, warning, structure, content } = result.data as {
          variables: Array<Omit<CRFVariable, 'id' | 'editing'>>;
          licensedInstrument?: LicensedInstrumentInfo;
          warning?: string;
          structure?: ClientStructureChoice;
          content?: ClientContentResult;
        };

        const varsWithIds: CRFVariable[] = variables.map(v => ({
          ...v,
          id: uid(),
          options: v.options || [],
          unit: v.unit || '',
        }));

        let updates: Partial<CRFForm> = { variables: varsWithIds, generatingVars: false };
        if (licensedInstrument) updates.licensedInstrument = licensedInstrument;
        if (structure) updates.structure = structure;
        // The layout the structure pass chose — grids with their rows, tables
        // with their row counts — becomes the form's sections (2026-09-18).
        // Before this it stayed on `structure`, unread, and the next save stored
        // a flat list. The old sections are replaced either way: they name the
        // previous generation's variable ids, which no longer exist.
        updates.sections = sectionsFromStructure(varsWithIds, structure) as ClientCrfSection[] | undefined;
        if (content) {
          const applied = applyContentToForm(
            { variables: varsWithIds, sections: updates.sections, structure: updates.structure },
            content,
          );
          updates = {
            ...updates,
            variables: applied.variables as CRFVariable[],
            ...(applied.sections ? { sections: applied.sections as ClientCrfSection[] } : {}),
            ...(applied.structure ? { structure: applied.structure as ClientStructureChoice } : {}),
            content: applied.content,
          };
        }
        updateForm(form.id, updates);
        generatedByForm.set(form.id, { name: form.name, updates });

        const varCount = varsWithIds.length;
        const suffix = licensedInstrument?.isLicensed ? ' (licensed instrument)' : '';
        setBulkLog(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = warning
            ? { form: form.name, status: 'warning', message: warning }
            : { form: form.name, status: 'success', message: `${varCount} variables generated${suffix}` };
          return updated;
        });
      } catch (err) {
        updateForm(form.id, { generatingVars: false });
        setBulkLog(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { form: form.name, status: 'error', message: `Failed: ${(err as Error).message}` };
          return updated;
        });
      }
    }

    // Make sure what the run generated is SAVED. A refused session save (another writer really did change the
    // stored copy) stops all further saves, and the dialog would still say every form succeeded. Reload the
    // stored session, keep the forms the page shows, take the stored copy only of what changed elsewhere,
    // put this run's variables on forms still empty there, and save again - three attempts at most.
    if (sessionVersion && generatedByForm.size > 0) {
      const settle = async () => {
        // Let the auto-save effect queue its write, then wait for the queue to drain.
        await new Promise(r => setTimeout(r, 150));
        await sessionVersion.exclusive(async () => undefined);
      };
      // The recovery is reported only once the save after it has been observed: "saved" is said when the
      // page is no longer stale, not when the merge was made.
      let pending: ReturnType<typeof planBulkRecovery<typeof formsRef.current[number]>> | null = null;
      const report = async () => {
        if (!pending) return;
        // The auto-save effect may not have run yet: wait (briefly) for the save after the recovery to report.
        for (let i = 0; i < 5 && persistedSessionRef.current.lastOutcome == null && !sessionVersion.isStale; i++) await settle();
        const saved = recoveryWasSaved(persistedSessionRef.current, sessionVersion.isStale);
        setBulkLog(prev => [...prev, { form: '', status: 'warning', message: describeRecovery(pending!, saved) }]);
        pending = null;
      };
      for (let attempt = 0; attempt < MAX_BULK_SAVE_ATTEMPTS; attempt++) {
        await settle();
        await report();
        if (!sessionVersion.isStale) break;
        const stored = await loadSessionFromServer(projectId!);
        if (!stored) break;
        const plan = planBulkRecovery(formsRef.current, stored.session.forms, generatedByForm);
        setForms(plan.forms);
        sessionVersion.recover(stored.docVersion);
        // What is stored now is the recovered copy, so the merged forms (which differ from it) are written.
        persistedSessionRef.current.current = storedSessionFingerprint(stored.session);
        persistedSessionRef.current.lastOutcome = null;
        pending = plan;
      }
      await settle();
      await report();
      if (!sessionVersion.isStale && persistedSessionRef.current.lastOutcome === 'failed') {
        const msg = 'The generated variables could not be saved (the save failed). Run Generate All Variables again.';
        setBulkLog(prev => [...prev, { form: '', status: 'error', message: msg }]);
        showToast(msg, 'error');
      }
      if (sessionVersion.isStale) {
        const stored = await loadSessionFromServer(projectId!);
        const left = stored ? generatedFormsLeftEmpty(stored.session.forms, generatedByForm) : [...generatedByForm.values()].map(g => g.name);
        const msg = `Variables for ${left.length ? left.join(', ') : 'some forms'} could not be saved. Run Generate All Variables again.`;
        setBulkLog(prev => [...prev, { form: '', status: 'error', message: msg }]);
        showToast(msg, 'error');
      }
    }
    bulkRunningRef.current = false;

    setBulkProgress(prev => ({ ...prev, current: total, currentForm: '' }));
    setBulkGenerating(false);
  };

  // ── Counters ────────────────────

  const totalForms = forms.length;
  // Display order chosen by the sort control (does not mutate `forms`).
  const displayForms = sortForms(forms, sortMode, customOrder, { codeOf: effectiveFormCode });
  const formsWithVars = forms.filter(f => f.variables && f.variables.length > 0).length;
  const approvedCount = forms.filter(f => f.status === 'approved' || f.status === 'final').length;
  // Rendering status for the header chip and the Generate/Re-render label.
  // Counts only approved forms — a draft has nothing to render, so this stays
  // truthful on a part-finished study.
  const renderSummary = summariseRenderings(
    forms,
    f => f.status === 'approved' || f.status === 'final',
    f => hasUsableRendering(f),
  );
  const allFormsRendered = renderSummary.total > 0 && renderSummary.rendered === renderSummary.total;

  // ─── RENDER ────────────────────────────────────────────

  // ── Loading state while checking for existing protocol ──

  if (loadingProject) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
        <p className="text-sm text-gray-500">Loading protocol data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Phase 1: Upload ── */}
      {phase === 'upload' && (
        <div className="max-w-3xl mx-auto py-12 px-4">
          <div className="text-center mb-8">
            <ClipboardList className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-gray-900">CRF Builder</h1>
            <p className="text-gray-500 mt-1">Upload a protocol to generate case report form variable lists</p>
          </div>

          {/* Drop zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
              dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400 bg-white'
            }`}
          >
            <Upload className={`h-10 w-10 mx-auto mb-3 ${dragActive ? 'text-primary-500' : 'text-gray-400'}`} />
            <p className="text-sm font-medium text-gray-700">
              Drop a <span className="font-semibold">.docx</span> or <span className="font-semibold">.txt</span> file here, or click to browse
            </p>
            <p className="text-xs text-gray-400 mt-1">Protocol text will be extracted automatically</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,.txt"
              onChange={handleFileInput}
              className="hidden"
            />
          </div>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-gray-200" />
            <span className="px-3 text-xs text-gray-400 uppercase">or paste below</span>
            <div className="flex-1 border-t border-gray-200" />
          </div>

          {/* Text area */}
          <textarea
            value={protocolText}
            onChange={e => setProtocolText(e.target.value.substring(0, MAX_PROTOCOL_LENGTH))}
            placeholder="Paste protocol text here..."
            rows={10}
            className="w-full rounded-lg border border-gray-300 p-4 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {protocolText.length.toLocaleString()} / {MAX_PROTOCOL_LENGTH.toLocaleString()} characters
          </p>

          {/* Title input (shown once text present) */}
          {protocolText.trim().length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Protocol Title</label>
              <input
                type="text"
                value={protocolTitle}
                onChange={e => setProtocolTitle(e.target.value)}
                placeholder="Enter a title for this protocol"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {/* Analyze button */}
          <button
            onClick={analyzeProtocol}
            disabled={protocolText.trim().length === 0}
            className={`mt-6 w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
              protocolText.trim().length > 0
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Analyze Protocol
          </button>
        </div>
      )}

      {/* ── Phase 1.5: Ready (protocol analyzed, awaiting user to generate CRFs) ── */}
      {phase === 'ready' && (
        <div className="max-w-2xl mx-auto py-16 px-4">
          <div className="text-center mb-8">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 mb-4">
              <ClipboardList className="h-7 w-7 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">CRF Builder</h1>
            {protocolTitle && (
              <p className="text-sm text-gray-500 mt-1">{protocolTitle}</p>
            )}
          </div>

          <div className="card p-6 mb-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">About this page</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              The CRF Builder reads the procedures, visits, and assessments in your
              protocol and suggests a complete set of Case Report Forms (CRFs) to
              collect the data required by the study. Each suggested form includes
              a name, a rationale for why it's needed, and a category (screening,
              demographics, safety, etc.).
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              After the initial list is generated, you can review each form,
              generate its variables, edit field definitions, delete forms you
              don't need, and approve forms for use in the data dictionary and
              database schema.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              Click <strong className="text-gray-700">Generate CRF List</strong> to
              start. This typically takes 15–30 seconds. Already have a CRF
              packet? Use <strong className="text-gray-700">Upload CRF Packet</strong> instead
              to extract forms from an existing document.
            </p>
          </div>

          <button
            onClick={analyzeProtocol}
            disabled={protocolText.trim().length === 0 || !canGenerate}
            title={!canGenerate ? "Your role doesn't allow generation on this project." : undefined}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-colors"
          >
            <Play className="h-4 w-4" />
            Generate CRF List
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <button
            onClick={() => setShowUploadPacket({ defaultSingleForm: false })}
            disabled={!canUpload}
            title={!canUpload ? "Your role doesn't allow uploads on this project." : undefined}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-colors"
          >
            <Upload className="h-4 w-4" />
            Upload CRF Packet
          </button>

          <button
            onClick={() => setShowUploadDd(true)}
            disabled={!canUpload}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-colors"
            title={!canUpload ? "Your role doesn't allow uploads on this project." : 'Import a REDCap data dictionary (.csv) to create CRF forms'}
          >
            <ClipboardList className="h-4 w-4" />
            Import REDCap Data Dictionary
          </button>

          {/*
            Jim, 2026-09-20, on this screen: "shouldn't I be able to upload a
            data dictionary here". The REDCap button above takes ONLY a REDCap
            export, and the dictionary of a study already built in this product
            is not one — so the obvious way to start the next study in a
            programme was not on the page it belongs on. This is the adoption
            flow, which now creates the forms the file names.
          */}
          <button
            onClick={() => setShowAdoptDd(true)}
            disabled={!canUpload}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-colors"
            title={!canUpload ? "Your role doesn't allow uploads on this project." : 'Import a DocumentSpark data dictionary (.csv) from another study and create its forms here'}
          >
            <ClipboardList className="h-4 w-4" />
            Import Data Dictionary
          </button>
        </div>
      )}

      {/* ── Phase 2: Analyzing ── */}
      {phase === 'analyzing' && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Loader2 className="h-12 w-12 text-primary-600 animate-spin" />
          <p className="text-lg font-medium text-gray-700">Analyzing protocol and suggesting forms...</p>
          {protocolTitle && <p className="text-sm text-gray-500">{protocolTitle}</p>}
          <div className="w-72">
            <StepProgress
              active={true}
              steps={[
                { label: 'Reading protocol structure...', delay: 0 },
                { label: 'Identifying study visits and procedures...', delay: 3000 },
                { label: 'Mapping data collection requirements...', delay: 8000 },
                { label: 'Suggesting CRF forms...', delay: 15000 },
              ]}
            />
          </div>
        </div>
      )}

      {/* ── Phase 3: Workspace ── */}
      {phase === 'workspace' && (
        <div className="max-w-5xl mx-auto py-6 px-4">

          {/* Header bar */}
          <div className="card p-4 mb-6">
            {/* Three stacked rows — title, status, actions — rather than one
                row holding all of it. The single row could not wrap (the
                inner container had no flex-wrap), so at the card's max width
                it overflowed and flexbox squeezed the buttons until their
                LABELS broke mid-phrase: "Generate All / Variables", "Export
                CRF / JSON". Splitting status from actions also stops four
                chips and five buttons competing on one line. */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-primary-600" />
                  CRF Builder
                </h1>
                {protocolTitle && <p className="text-sm text-gray-500 mt-0.5">{protocolTitle}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-sm mt-3">
                  <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium whitespace-nowrap">
                    {totalForms} forms
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium whitespace-nowrap">
                    {formsWithVars} with variables
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap">
                    {approvedCount} final
                  </span>
                  {/* Renderings are the one part of "is this done" the header
                      never reported. Without it, "Generate All CRFs" staying
                      enabled reads as outstanding work rather than as a
                      re-render that is always available. Same predicate the
                      server precondition and Study Documents use. */}
                  {renderSummary.total > 0 && (
                    <span
                      className={`px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                        renderSummary.rendered === renderSummary.total
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                      title={
                        renderSummary.rendered === renderSummary.total
                          ? 'Every approved form has a PDF rendering. Re-run Generate All CRFs after editing a form so the packets and the Instruction Guide pick up the change.'
                          : 'Some approved forms have no PDF rendering yet, so the CRF Instruction Guide cannot include them. Run Generate All CRFs.'
                      }
                    >
                      {renderSummary.rendered} of {renderSummary.total} rendered
                    </span>
                  )}
            </div>
            {/* Actions. `ml-auto` on New Protocol pushes it to the far end:
                it discards the session, so it should not sit shoulder to
                shoulder with the actions that build one. */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setBulkConfirmOpen(true)}
                  disabled={bulkGenerating || !hasFormsNeedingVariables(forms)}
                  className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                >
                  <Loader2 className={`h-3.5 w-3.5 ${bulkGenerating ? 'animate-spin' : 'hidden'}`} />
                  {!bulkGenerating && <Play className="h-3.5 w-3.5" />}
                  Generate All Variables
                </button>
                {reviewSummary?.status === 'partial' && !isStaleRunning(reviewSummary, new Date()) && (
                  <button
                    onClick={() => runProtocolReview(true)}
                    disabled={reviewRunning || bulkGenerating}
                    title="Review only the forms the last review did not finish."
                    className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                  >
                    {reviewRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5" />}
                    {continueLabel(reviewSummary)}
                  </button>
                )}
                <button
                  onClick={() => runProtocolReview()}
                  disabled={reviewRunning || bulkGenerating || forms.length === 0}
                  title="Compare every form with the protocol and list items to check. It only flags; it changes nothing and does not block approval."
                  className="text-sm px-3 py-1.5 rounded-lg border border-primary-300 bg-white text-primary-700 hover:bg-primary-50 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                >
                  {reviewRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardList className="h-3.5 w-3.5" />}
                  {reviewSummaryRunning && reviewSummary
                    ? `Reviewing ${reviewSummary.formsReviewed} of ${reviewSummary.formsTotal} forms...`
                    : reviewRunning ? 'Reviewing...' : 'Review against protocol'}
                </button>
                <button
                  onClick={exportAllForms}
                  disabled={exportingAllCrfs || exportingFormId !== null || bulkGenerating || !hasApprovedForm(forms)}
                  title="Render every approved CRF and save each one, so the packets and the Instruction Guide show the current version. Safe to re-run; uploaded forms keep their own document."
                  className="text-sm px-3 py-1.5 rounded-lg border border-primary-300 bg-white text-primary-700 hover:bg-primary-50 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                >
                  {exportingAllCrfs ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )}
                  {exportingAllCrfs
                    ? 'Generating...'
                    : allFormsRendered
                    ? 'Re-render All CRFs'
                    : 'Generate All CRFs'}
                </button>
                <button
                  onClick={exportRawCrfJson}
                  disabled={exportingRawJson}
                  title="Download the raw Firestore CRF JSON for this study"
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
                >
                  {exportingRawJson ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  Export CRF JSON
                </button>
                <button
                  onClick={resetSession}
                  title="Start over with a different protocol. Does not delete this study's forms."
                  className="text-sm px-3 py-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 flex items-center gap-1.5 whitespace-nowrap shrink-0 ml-auto"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  New Protocol
                </button>
                {undoStack.length > 0 && (
                  <button
                    onClick={performUndo}
                    title={`Undo: ${undoStack[undoStack.length - 1].description}${
                      undoStack.length > 1 ? ` (${undoStack.length} actions in history)` : ''
                    }`}
                    className="text-sm px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-1.5 font-semibold whitespace-nowrap shrink-0"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Undo
                    {undoStack.length > 1 && (
                      <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] leading-none">
                        {undoStack.length}
                      </span>
                    )}
                  </button>
                )}
            </div>
            <ProtocolReviewBanner summary={reviewSummary} reviewForms={reviewForms} currentFormIds={forms.map(f => f.id)} stillRunningNote={reviewStillRunningNote} onContinue={() => runProtocolReview(true)} continueDisabled={reviewRunning || bulkGenerating} />
            {/* Post-export data-quality warning count */}
            {lastExportWarningCount !== null && lastExportWarningCount > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 flex-1">
                  Export complete. {lastExportWarningCount} data quality warning
                  {lastExportWarningCount === 1 ? '' : 's'} found — see{' '}
                  <code className="font-mono bg-amber-100 px-1 rounded">dataQualityWarnings</code>{' '}
                  in the downloaded file.
                </p>
                <button
                  onClick={() => setLastExportWarningCount(null)}
                  className="text-xs text-amber-700 hover:text-amber-900 underline flex-shrink-0"
                >
                  Dismiss
                </button>
              </div>
            )}
            {/*
              View toggle. RESTYLED 2026-09-05 (Jim: "it's really hard to see
              the buttons") — this is the only way to reach the visit editor and
              the form-assignment grid, and it was rendering as low-emphasis
              chrome. Measured against WCAG AA on the values it used:

                selected tab vs unselected   1.09:1   <- the real problem
                "View" label   gray-400      2.54:1   FAILS AA (needs 4.5)
                hairline border gray-200     1.24:1

              A selected tab 9% different in luminance from its neighbours is
              not a selection anyone can see. The obvious fix — a grey track
              with a white active pill — measures 1.10:1 and is no better; it
              leans on a shadow to carry the whole signal. A SOLID selected tab
              measures 5.17:1, so which view you are on is unmistakable rather
              than inferred.

                selected  primary-600 ground, white text   5.17:1
                unselected gray-700 on white               10.31:1
                label      gray-600                         7.56:1  passes AA

              aria-pressed added because these are toggle buttons and were
              announcing nothing about their state.
            */}
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3">
              <span className="text-xs text-gray-600 font-semibold uppercase tracking-wide">View</span>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden shadow-sm">
                <button
                  onClick={() => setWorkspaceView('forms')}
                  aria-pressed={workspaceView === 'forms'}
                  className={`text-sm px-4 py-2 flex items-center gap-2 font-medium transition-colors ${
                    workspaceView === 'forms'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <LayoutList className="h-4 w-4" />
                  Forms
                </button>
                <button
                  onClick={() => setWorkspaceView('visits')}
                  aria-pressed={workspaceView === 'visits'}
                  className={`text-sm px-4 py-2 flex items-center gap-2 font-medium border-l border-gray-300 transition-colors ${
                    workspaceView === 'visits'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <CalendarDays className="h-4 w-4" />
                  Visits
                </button>
                <button
                  onClick={() => setWorkspaceView('schedule')}
                  aria-pressed={workspaceView === 'schedule'}
                  className={`text-sm px-4 py-2 flex items-center gap-2 font-medium border-l border-gray-300 transition-colors ${
                    workspaceView === 'schedule'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ClipboardList className="h-4 w-4" />
                  Form Assignments
                </button>
              </div>
            </div>
          </div>

          {/* EDC Staleness Banner */}
          {edcStale && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  EDC platform changed since these forms were generated
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Consider regenerating forms to tailor field types and validation to the updated platform.
                </p>
              </div>
              <button
                onClick={() => setEdcSystemAtGeneration(currentEdcSystem)}
                className="text-xs text-amber-700 hover:text-amber-900 underline flex-shrink-0"
              >
                Dismiss
              </button>
            </div>
          )}

          {workspaceView === 'forms' ? (
          <>
          {/* Quality Issues Banner + Approve All */}
          {(() => {
            // Use visibleWarnings (excludes dismissed and confirmed
            // cross-form duplicates) so dismissed errors don't keep
            // blocking the Approve All bar.
            const errors = visibleWarnings.filter(w => w.severity === 'error');
            // Intra-form duplicates: same variable appears multiple times
            // inside ONE form. Always errors; always block approval.
            const intraDuplicates = errors.filter(w => w.duplicateScope === 'intra');
            // Errors that are NOT duplicates — e.g., GCDMP violations like
            // "check all that apply" in question text. These silently block
            // Approve All unless we surface them at the top level.
            const nonDuplicateErrors = errors.filter(w => w.duplicateScope !== 'intra');
            // Cross-form duplicates: same variable appears in multiple
            // distinct forms. Surfaced as a warning only — does NOT block
            // approval. Often intentional for repeated-measures
            // instruments. Confirm dismisses the warning permanently.
            const crossDuplicates = visibleWarnings.filter(
              w => w.severity === 'warning' && w.duplicateScope === 'cross',
            );
            const draftCount = forms.filter(f => f.variables && f.status !== 'approved' && f.status !== 'final').length;
            // ONE detector for the banner and the approve action. On 2026-08-28
            // the banner said "no blocking issues" directly above an approve
            // that refused on a form-code collision, because the banner looked
            // only at quality errors and the gate looked only at collisions.
            // Third instance of the readiness-question family (8228ce98).
            const bannerCollisions = findFormCodeCollisions(
              forms.map(f => ({ id: f.id, name: f.name, code: f.code }))
            );

            // Reusable per-occurrence "Rename in X" / "Remove from X"
            // buttons. Used by the cross-form duplicate panel below and
            // also as a fallback inside the intra-form panel for any
            // malformed multi-form warning. Captures showToast,
            // applyFormUpdate, pushUndoable, and forms via closure.
            const renderCrossFormFixButtons = (
              varName: string,
              occ: { formId: string; formName: string },
              key: string,
            ): React.ReactNode => {
              const targetForm = forms.find(f => f.id === occ.formId);
              if (!targetForm) return null;
              const formName = occ.formName;
              const targetFormId = occ.formId;
              return (
                <span key={key} className="contents">
                  <button
                    onClick={() => {
                      const snapBefore = forms.find(f => f.id === targetFormId)?.variables;
                      const renameSnap = snapBefore ? [...snapBefore] : null;

                      let renamedTo = '';
                      applyFormUpdate(
                        f => f.id === targetFormId,
                        form => {
                          if (!form.variables) return form;
                          const words = (form.name || '').split(/\s+/).filter(Boolean);
                          let prefix: string;
                          if (words.length >= 2) {
                            prefix = words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
                          } else {
                            prefix = (form.name || form.id).substring(0, 3).toUpperCase();
                          }
                          let cleanVar = varName.replace(/^[A-Z]{2,3}_/, '');
                          if (cleanVar === varName && varName.startsWith(prefix)) {
                            cleanVar = varName.substring(prefix.length);
                          }
                          if (!cleanVar) cleanVar = varName;
                          let newName = (prefix + cleanVar).substring(0, 8).toUpperCase();
                          if (newName === varName) {
                            const existingNames = new Set(form.variables.map(v => v.varName));
                            for (let suffix = 2; suffix <= 9; suffix++) {
                              const candidate = (prefix + suffix + cleanVar).substring(0, 8).toUpperCase();
                              if (!existingNames.has(candidate) && candidate !== varName) {
                                newName = candidate;
                                break;
                              }
                            }
                          }
                          renamedTo = newName;
                          const updatedVars = form.variables.map(v =>
                            v.varName === varName ? { ...v, varName: newName } : v
                          );
                          return { ...form, variables: updatedVars };
                        },
                      );

                      if (renamedTo) {
                        const msg = `Renamed "${varName}" to "${renamedTo}" in ${formName}`;
                        showToast(msg, 'success');
                        if (renameSnap) {
                          pushUndoable(msg, () => {
                            applyFormUpdate(
                              f => f.id === targetFormId,
                              form => ({ ...form, variables: renameSnap }),
                            );
                            showToast(`Reverted rename of "${varName}" in ${formName}`, 'success');
                          });
                        }
                      }
                    }}
                    className="text-[10px] px-2 py-1 bg-white border border-red-200 text-red-700 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
                    title={`Rename in ${formName} with form prefix`}
                  >
                    Rename in {formName.substring(0, 15)}
                  </button>
                  <button
                    onClick={() => {
                      const srcForm = forms.find(f => f.id === targetFormId);
                      const srcIndex = srcForm?.variables?.findIndex(v => v.varName === varName) ?? -1;
                      const removedVar = srcIndex >= 0 ? srcForm!.variables![srcIndex] : null;

                      applyFormUpdate(
                        f => f.id === targetFormId,
                        form => form.variables
                          ? { ...form, variables: form.variables.filter(v => v.varName !== varName) }
                          : form,
                      );

                      const msg = `Removed "${varName}" from ${formName}`;
                      showToast(msg, 'success');
                      if (removedVar) {
                        pushUndoable(msg, () => {
                          applyFormUpdate(
                            f => f.id === targetFormId,
                            form => {
                              if (!form.variables) return { ...form, variables: [removedVar] };
                              const next = [...form.variables];
                              const insertAt = Math.min(srcIndex, next.length);
                              next.splice(insertAt, 0, removedVar);
                              return { ...form, variables: next };
                            },
                          );
                          showToast(`Restored "${varName}" to ${formName}`, 'success');
                        });
                      }
                    }}
                    className="text-[10px] px-2 py-1 bg-white border border-gray-200 text-gray-600 rounded hover:bg-gray-50 transition-colors whitespace-nowrap"
                    title={`Remove from ${formName}`}
                  >
                    Remove from {formName.substring(0, 13)}
                  </button>
                </span>
              );
            };

            const scrollToForm = (formIdOrName: string) => {
              // qualityWarnings set `formId` to either the form's id or name.
              // First try direct lookup; if that fails, resolve by name.
              let target = document.getElementById(`crf-form-${formIdOrName}`);
              if (!target) {
                const byName = forms.find(f => f.name === formIdOrName);
                if (byName) target = document.getElementById(`crf-form-${byName.id}`);
              }
              if (target) {
                // Expand the form so the user can see the flagged question.
                const form = forms.find(f => f.id === formIdOrName) || forms.find(f => f.name === formIdOrName);
                if (form && !form.expanded) {
                  updateForm(form.id, { expanded: true });
                }
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            };

            return (
              <div className="mb-4 space-y-3">
                {/* Intra-form duplicate resolution panel — always errors,
                    always blocks approval. */}
                {intraDuplicates.length > 0 && (
                  <div className="card border-l-4 border-l-red-400 p-4 animate-fade-in">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <span className="font-semibold text-red-800 text-sm">
                          {intraDuplicates.length} duplicate variable{intraDuplicates.length !== 1 ? 's' : ''} within form{intraDuplicates.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <span className="text-xs text-red-600">Must resolve before approving</span>
                    </div>
                    <div className="space-y-2">
                      {intraDuplicates.map((dup, i) => {
                        // Prefer the structured `duplicateOccurrences` from
                        // the detector (formId-keyed). Fall back to a
                        // legacy parse path for any warning that predates
                        // the structured field.
                        const occurrences = dup.duplicateOccurrences ?? (() => {
                          const varMatch = dup.message.match(/Variable "([^"]+)" is duplicated [\s\S]*?within form: (.+)$/);
                          const formNamesStr = varMatch?.[2] || '';
                          return (formNamesStr.split(', ')).map(n => {
                            const f = forms.find(fm => fm.name === n);
                            return { formId: f?.id ?? '', formName: n };
                          });
                        })();
                        const varName = dup.field || '';
                        const dupFormNames = occurrences.map(o => o.formName);
                        const uniqueFormIds = [...new Set(occurrences.map(o => o.formId).filter(Boolean))];
                        // For intra-form duplicates the detector emits one
                        // warning per (varName, formId), so we always have
                        // exactly one form id with multiple occurrences.
                        const isWithinForm = uniqueFormIds.length === 1 && occurrences.length > 1;
                        const withinFormTargetId = isWithinForm ? uniqueFormIds[0] : '';
                        const withinFormTargetName = isWithinForm ? occurrences[0].formName : '';

                        return (
                          <div key={i} className="flex items-center gap-3 p-2.5 bg-red-50/50 rounded-lg border border-red-100">
                            <code className="text-xs font-mono text-red-700 bg-red-100 px-2 py-0.5 rounded font-semibold min-w-[80px]">
                              {varName}
                            </code>
                            <span className="text-xs text-red-600 flex-1">
                              {isWithinForm
                                ? `Duplicated ${occurrences.length}× within: ${withinFormTargetName}`
                                : `Used in: ${dupFormNames.join(', ')}`}
                            </span>
                            <div className="flex gap-1.5">
                              {isWithinForm ? (
                                <>
                                  {/* Remove duplicate copies — keep only the first occurrence */}
                                  <button
                                    onClick={() => {
                                      // Snapshot the variables array so Undo can restore the
                                      // exact pre-remove state (including the removed copies
                                      // at their original positions).
                                      const srcForm = forms.find(f => f.id === withinFormTargetId);
                                      const snapshot = srcForm?.variables ? [...srcForm.variables] : null;

                                      applyFormUpdate(
                                        f => f.id === withinFormTargetId,
                                        form => {
                                          if (!form.variables) return form;
                                          let kept = false;
                                          const updatedVars = form.variables.filter(v => {
                                            if (v.varName === varName && !kept) {
                                              kept = true;
                                              return true;
                                            }
                                            return v.varName !== varName;
                                          });
                                          return { ...form, variables: updatedVars };
                                        },
                                      );

                                      const msg = `Removed ${occurrences.length - 1} duplicate(s) of "${varName}" in ${withinFormTargetName}`;
                                      showToast(msg, 'success');
                                      if (snapshot) {
                                        pushUndoable(msg, () => {
                                          applyFormUpdate(
                                            f => f.id === withinFormTargetId,
                                            form => ({ ...form, variables: snapshot }),
                                          );
                                          showToast(
                                            `Restored ${occurrences.length - 1} duplicate(s) of "${varName}" in ${withinFormTargetName}`,
                                            'success',
                                          );
                                        });
                                      }
                                    }}
                                    className="text-[10px] px-2 py-1 bg-white border border-red-200 text-red-700 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
                                    title="Remove duplicate copies, keeping the first"
                                  >
                                    Remove duplicates
                                  </button>
                                  {/* Rename the extra copies with a suffix */}
                                  <button
                                    onClick={() => {
                                      // Snapshot before the rename so Undo can restore the
                                      // original variable names at their original positions.
                                      const snapBefore = forms.find(f => f.id === withinFormTargetId)?.variables;
                                      const renameSnapshot = snapBefore ? [...snapBefore] : null;

                                      applyFormUpdate(
                                        f => f.id === withinFormTargetId,
                                        form => {
                                          if (!form.variables) return form;
                                          const existingNames = new Set(form.variables.map(v => v.varName));
                                          let copyNum = 1;
                                          let first = true;
                                          const updatedVars = form.variables.map(v => {
                                            if (v.varName !== varName) return v;
                                            if (first) { first = false; return v; }
                                            copyNum++;
                                            const base = varName.replace(/_$/, '').substring(0, 6);
                                            let newName = (base + copyNum).substring(0, 8).toUpperCase();
                                            while (existingNames.has(newName)) {
                                              copyNum++;
                                              newName = (base + copyNum).substring(0, 8).toUpperCase();
                                            }
                                            existingNames.add(newName);
                                            return { ...v, varName: newName };
                                          });
                                          return { ...form, variables: updatedVars };
                                        },
                                      );

                                      const msg = `Renamed duplicate(s) of "${varName}" in ${withinFormTargetName}`;
                                      showToast(msg, 'success');
                                      if (renameSnapshot) {
                                        pushUndoable(msg, () => {
                                          applyFormUpdate(
                                            f => f.id === withinFormTargetId,
                                            form => ({ ...form, variables: renameSnapshot }),
                                          );
                                          showToast(
                                            `Reverted rename of "${varName}" in ${withinFormTargetName}`,
                                            'success',
                                          );
                                        });
                                      }
                                    }}
                                    className="text-[10px] px-2 py-1 bg-white border border-amber-200 text-amber-700 rounded hover:bg-amber-50 transition-colors whitespace-nowrap"
                                    title="Rename duplicate copies with numeric suffixes"
                                  >
                                    Rename duplicates
                                  </button>
                                </>
                              ) : (
                                /* Fallback — only reachable for malformed
                                   intra-form warnings whose occurrences
                                   somehow span multiple formIds. Render
                                   the per-form rename/remove buttons so
                                   the user can still resolve. */
                                occurrences.slice(1).map((occ, fi) =>
                                  renderCrossFormFixButtons(varName, occ, `${occ.formId}-${fi}`)
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Cross-form duplicate panel — warning only. Does NOT
                    block approval. Often intentional for repeated-measures
                    instruments (e.g. C-SSRS at baseline + follow-up).
                    Per-row Confirm button suppresses the warning
                    permanently via confirmedCrossFormDuplicates. */}
                {crossDuplicates.length > 0 && (
                  <div className="card border-l-4 border-l-amber-400 p-4 animate-fade-in">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        <span className="font-semibold text-amber-800 text-sm">
                          {crossDuplicates.length} variable{crossDuplicates.length !== 1 ? 's' : ''} appear in multiple forms
                        </span>
                      </div>
                      <span className="text-xs text-amber-700">Confirm intentional or rename — does not block approval</span>
                    </div>
                    <p className="text-xs text-amber-700/90 mb-2">
                      This is often expected for repeated-measures instruments administered at multiple visits (e.g. baseline and follow-up). Confirm to suppress the warning, or rename in one of the forms to disambiguate.
                    </p>
                    <div className="space-y-3">
                      {groupCrossFormDuplicates(crossDuplicates).map(group => {
                        const groupLabel = group.formNames.join(' + ');
                        return (
                          <div
                            key={`cross-group-${group.key}`}
                            className="rounded-lg border border-amber-200 bg-amber-50/40 overflow-hidden"
                          >
                            {/* Group header. Variables spanning the SAME set of
                                forms are one decision, not several — "is it
                                intentional that these forms share fields" has a
                                single answer covering the whole group. */}
                            <div className="flex items-start justify-between gap-3 px-3 py-2 bg-amber-100/60 border-b border-amber-200">
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-amber-900">
                                  {group.variables.length} variable
                                  {group.variables.length !== 1 ? 's' : ''} shared by {group.formNames.length} forms
                                </div>
                                <div className="text-[11px] text-amber-700 break-words">{groupLabel}</div>
                              </div>
                              <button
                                onClick={() => {
                                  const names = group.variableNames;
                                  names.forEach(n => confirmCrossFormDuplicate(n));
                                  showToast(
                                    `Confirmed ${names.length} variable${names.length !== 1 ? 's' : ''} shared by ${groupLabel}`,
                                    'success',
                                  );
                                  // One undo entry for the whole group, so a
                                  // mis-click is one click to reverse rather
                                  // than N.
                                  pushUndoable(
                                    `Confirmed ${names.length} cross-form duplicate${names.length !== 1 ? 's' : ''} in ${groupLabel}`,
                                    () => {
                                      unconfirmCrossFormDuplicates(names);
                                      showToast(
                                        `Reverted confirmation of ${names.length} variable${names.length !== 1 ? 's' : ''}`,
                                        'success',
                                      );
                                    },
                                  );
                                }}
                                className="text-[11px] px-2.5 py-1 bg-white border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 transition-colors whitespace-nowrap inline-flex items-center gap-1 flex-shrink-0 font-medium"
                                title={`Confirm all ${group.variables.length} as intentional and dismiss these warnings`}
                              >
                                <Check className="h-3 w-3" />
                                Confirm all {group.variables.length}
                              </button>
                            </div>

                            <div className="divide-y divide-amber-100">
                              {group.variables.map(({ varName, occurrences }) => {
                                const fixKey = `${group.key}::${varName}`;
                                const fixOpen = openCrossFixFor === fixKey;
                                return (
                                  <div key={`cross-${fixKey}`} className="px-3 py-2">
                                    <div className="flex items-center gap-3">
                                      <code className="text-xs font-mono text-amber-800 bg-amber-100 px-2 py-0.5 rounded font-semibold flex-shrink-0">
                                        {varName}
                                      </code>
                                      <span className="flex-1" />
                                      <button
                                        onClick={() => {
                                          confirmCrossFormDuplicate(varName);
                                          showToast(
                                            `Confirmed "${varName}" as intentional across ${occurrences.length} forms`,
                                            'success',
                                          );
                                          pushUndoable(
                                            `Confirmed cross-form duplicate "${varName}"`,
                                            () => {
                                              unconfirmCrossFormDuplicates([varName]);
                                              showToast(`Reverted confirmation of "${varName}"`, 'success');
                                            },
                                          );
                                        }}
                                        className="text-[10px] px-2 py-1 bg-white border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50 transition-colors whitespace-nowrap inline-flex items-center gap-1 flex-shrink-0"
                                        title="Confirm this duplication is intentional and dismiss the warning"
                                      >
                                        <Check className="h-3 w-3" />
                                        Confirm
                                      </button>
                                      {/* Rename/Remove used to be rendered per
                                          occurrence, so a variable in six forms
                                          produced twelve buttons in one row and
                                          the layout collapsed. They now sit
                                          behind this toggle, so button count no
                                          longer scales with form count. */}
                                      <button
                                        onClick={() => setOpenCrossFixFor(fixOpen ? null : fixKey)}
                                        className="text-[10px] px-2 py-1 bg-white border border-amber-300 text-amber-800 rounded hover:bg-amber-50 transition-colors whitespace-nowrap inline-flex items-center gap-1 flex-shrink-0"
                                        title="Rename or remove this variable in one of the forms"
                                        aria-expanded={fixOpen}
                                      >
                                        Rename or remove
                                        <ChevronDown
                                          className={`h-3 w-3 transition-transform ${fixOpen ? 'rotate-180' : ''}`}
                                        />
                                      </button>
                                    </div>
                                    {fixOpen && (
                                      <div className="mt-2 pt-2 border-t border-amber-100 flex gap-1.5 flex-wrap">
                                        {occurrences.slice(1).map((occ, fi) =>
                                          renderCrossFormFixButtons(
                                            varName,
                                            occ,
                                            `cross-fix-${occ.formId}-${fi}`,
                                          ),
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Non-duplicate errors banner — the silent blockers
                    (GCDMP violations like "check all that apply") that
                    would otherwise hide the Approve All bar without
                    any visible explanation. */}
                {nonDuplicateErrors.length > 0 && (
                  <div className="card border-l-4 border-l-red-400 p-4 animate-fade-in">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold text-red-800 text-sm">
                          {nonDuplicateErrors.length} quality error{nonDuplicateErrors.length !== 1 ? 's' : ''} must be resolved before approval
                        </p>
                        <p className="text-xs text-red-600 mt-0.5">
                          Click an error to jump to the form. Edit the flagged question, then these will clear automatically.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {nonDuplicateErrors.map((err, i) => {
                        // Resolve form name for display (formId may be id or name)
                        const formForErr =
                          forms.find(f => f.id === err.formId) ||
                          forms.find(f => f.name === err.formId);
                        const formName = formForErr?.name || err.formId;
                        const isLicensed = formForErr?.licensedInstrument?.isLicensed === true;
                        const instrumentName = formForErr?.licensedInstrument?.instrumentName;
                        return (
                          <div
                            key={i}
                            className="flex items-start gap-2 p-2 rounded bg-red-50/50 border border-red-100"
                          >
                            <button
                              type="button"
                              onClick={() => scrollToForm(err.formId)}
                              className="flex-1 flex items-start gap-2 text-left hover:bg-red-100/40 -m-1 p-1 rounded transition-colors"
                              title="Jump to this form"
                            >
                              <span className="inline-block min-w-[140px] max-w-[180px] truncate text-[11px] font-semibold text-red-700">
                                {formName}
                              </span>
                              <span className="flex-1 text-xs text-red-600">
                                {err.message}
                                {isLicensed && (
                                  <span
                                    className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 border border-blue-200 text-[10px] font-medium text-blue-800"
                                    title={instrumentName ? `Licensed instrument: ${instrumentName}` : 'Licensed clinical instrument — published format applies'}
                                  >
                                    Licensed instrument
                                  </span>
                                )}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => dismissWarning(err.formId, err.message)}
                              className="text-[10px] px-2 py-0.5 rounded border border-red-200 bg-white text-red-700 hover:bg-red-50 flex-shrink-0"
                              title="Dismiss this error for this form (e.g., licensed instruments where the published format applies)"
                            >
                              Dismiss
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Approve All bar */}
                {draftCount > 0 && errors.length === 0 && bannerCollisions.length > 0 && (
                  <div className="card border-l-4 border-l-red-400 p-4 animate-fade-in">
                    <p className="text-sm text-red-800">
                      <span className="font-semibold">Approval is blocked</span> — form code
                      collision: {bannerCollisions.map(c => `"${c.code}" is shared by ${c.names.join(' and ')}`).join('; ')}.
                      Click a form-code badge to override it, or rename one of the forms.
                    </p>
                  </div>
                )}

                {draftCount > 0 && errors.length === 0 && bannerCollisions.length === 0 && !scheduleGate.ok && (
                  <div className="card border-l-4 border-l-amber-400 p-4 flex items-start gap-2 animate-fade-in">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-amber-800">
                      <span className="font-semibold">{draftCount} form{draftCount !== 1 ? 's' : ''}</span> waiting, but approval is on hold. {scheduleGate.message}
                    </span>
                  </div>
                )}
                {draftCount > 0 && errors.length === 0 && bannerCollisions.length === 0 && scheduleGate.ok && !factGate.ok && (
                  <div className="card border-l-4 border-l-amber-400 p-4 flex items-start gap-2 animate-fade-in">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-amber-800">
                      <span className="font-semibold">{draftCount} form{draftCount !== 1 ? 's' : ''}</span> waiting, but approval is on hold. {factGate.message}
                    </span>
                  </div>
                )}
                {draftCount > 0 && errors.length === 0 && bannerCollisions.length === 0 && scheduleGate.ok && factGate.ok && (
                  <div className="card border-l-4 border-l-green-400 p-4 flex items-center justify-between animate-fade-in">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm text-green-800">
                        <span className="font-semibold">{draftCount} form{draftCount !== 1 ? 's' : ''}</span> ready to approve — {approveBarSummary(visibleWarnings.length - errors.length)}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        // Approve forms sequentially so each saveCrfForm call
                        // finishes before the next starts. This guarantees
                        // every approved form has its artifact written before
                        // the user moves on to downstream generation.
                        const targets = forms.filter(f =>
                          f.variables && f.status !== 'approved' && f.status !== 'final');

                        if (!scheduleGate.ok) {
                          showToast(scheduleGate.message, 'error');
                          return;
                        }
                        if (!factGate.ok) {
                          showToast(factGate.message, 'error');
                          return;
                        }

                        // Block the bulk approval if the resulting set of
                        // approved forms would have any colliding derived
                        // form_codes — surface every collision at once so
                        // the user sees the full picture before fixing.
                        const candidates = forms
                          .filter(f =>
                            targets.some(t => t.id === f.id) ||
                            f.status === 'approved' ||
                            f.status === 'final'
                          )
                          .map(f => ({ id: f.id, name: f.name, code: f.code }));
                        const collisions = findFormCodeCollisions(candidates);
                        if (collisions.length > 0) {
                          // Quote each colliding code and list the form names
                          // that share it, so the user sees exactly which
                          // forms need to be renamed or have their code
                          // overridden via the badge.
                          const lines = collisions
                            .map(
                              c =>
                                `code "${c.code}" is shared by ${c.names
                                  .map(n => `“${n}”`)
                                  .join(' and ')}`
                            )
                            .join('; ');
                          showToast(
                            `Cannot approve — form code collision: ${lines}. Click a form-code badge to override it, or rename one of the forms.`,
                            'error'
                          );
                          return;
                        }

                        let succeeded = 0;
                        for (const f of targets) {
                          try {
                            await syncFormToFirestore({ ...f, status: 'approved' }, 'approved');
                            setForms(prev => prev.map(x =>
                              x.id === f.id ? { ...x, status: 'approved' } : x));
                            succeeded++;
                          } catch {
                            // Toast already shown by syncFormToFirestore.
                            break;
                          }
                        }
                        if (succeeded > 0) {
                          showToast(`Approved ${succeeded} form${succeeded !== 1 ? 's' : ''}`, 'success');
                        }
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Approve All ({draftCount})
                    </button>
                  </div>
                )}

                {/* All approved */}
                {draftCount === 0 && approvedCount > 0 && errors.length === 0 && (
                  <div className="card border-l-4 border-l-green-400 p-3 flex items-center gap-2 animate-fade-in">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-700">All {approvedCount} forms approved and ready for downstream use</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Form list toolbar */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-800">Case Report Forms</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowUploadPacket({ defaultSingleForm: true })}
                disabled={!canUpload}
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title={!canUpload ? "Your role doesn't allow uploads on this project." : 'Extract forms from an existing CRF document (one form; untick in the dialog for a multi-form packet)'}
              >
                <Upload className="h-4 w-4" />
                Upload CRF
              </button>
              <button
                onClick={() => setShowUploadDd(true)}
                disabled={!canUpload}
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                title={!canUpload ? "Your role doesn't allow uploads on this project." : 'Import forms and variables from a REDCap data dictionary export (.csv)'}
              >
                <ClipboardList className="h-4 w-4" />
                Import DD
              </button>
              <button
                onClick={() => { setShowAddForm(true); setNewFormName(''); setNewFormCategory('other'); }}
                disabled={!canEdit}
                title={!canEdit ? "Your role doesn't allow editing on this project." : undefined}
                className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add Form
              </button>
            </div>
          </div>

          {/* Add form row */}
          {showAddForm && (
            <div className="bg-white rounded-lg border border-dashed border-primary-300 p-4 mb-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Form Name</label>
                <input
                  autoFocus
                  value={newFormName}
                  onChange={e => setNewFormName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') setShowAddForm(false);
                    if (e.key === 'Enter' && newFormName.trim()) {
                      if (tryAddForm(newFormName, newFormCategory)) setShowAddForm(false);
                    }
                  }}
                  placeholder="Enter form name"
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select
                  value={newFormCategory}
                  onChange={e => setNewFormCategory(e.target.value as FormCategory)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500"
                >
                  {ALL_CATEGORIES.map(c => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => {
                  if (!newFormName.trim()) return;
                  if (tryAddForm(newFormName, newFormCategory)) setShowAddForm(false);
                }}
                disabled={!newFormName.trim()}
                className="px-4 py-1.5 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-50"
              >
                Add
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-1.5 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Sort / reorder control */}
          {forms.length > 1 && (
            <div className="flex items-center justify-end gap-2 mb-2">
              <label className="text-xs text-gray-500">Sort</label>
              <select
                value={sortMode}
                onChange={(e) => {
                  const m = e.target.value as FormSortMode;
                  // Seed the custom order from the current visible order so the
                  // list doesn't jump when switching into Custom.
                  if (m === 'custom') setCustomOrder(sortForms(forms, sortMode, customOrder, { codeOf: effectiveFormCode }).map((f) => f.id));
                  setSortMode(m);
                }}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:ring-2 focus:ring-primary-500"
              >
                <option value="created">As created</option>
                <option value="az">Alphabetical (A–Z)</option>
                <option value="code">By form code</option>
                <option value="category">By category</option>
                <option value="custom">Custom (drag)</option>
              </select>
            </div>
          )}

          {/* Form cards */}
          <div className="space-y-3">
            {displayForms.map(form => (
              <div
                key={form.id}
                id={`crf-form-${form.id}`}
                className="card overflow-hidden"
                onDragOver={(e) => {
                  if (sortMode !== 'custom' || !dragFormId || dragFormId === form.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropFormTarget({ beforeId: form.id });
                }}
                onDrop={(e) => {
                  if (sortMode !== 'custom' || !dragFormId) return;
                  e.preventDefault();
                  const beforeId = dropFormTarget ? dropFormTarget.beforeId : form.id;
                  if (dragFormId !== beforeId) setCustomOrder(moveInOrder(displayForms.map((f) => f.id), dragFormId, beforeId));
                  setDragFormId(null);
                  setDropFormTarget(null);
                }}
              >
                {sortMode === 'custom' && dragFormId && dropFormTarget?.beforeId === form.id && (
                  <div className="h-0.5 bg-primary-500 rounded-full mx-2 mt-1" />
                )}

                {/* Card header */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => updateForm(form.id, form.expanded ? { expanded: false } : { expanded: true, generationFailed: false })}
                >
                  {sortMode === 'custom' && (
                    <span
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); setDragFormId(form.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', form.id); } catch { /* some browsers reject */ } }}
                      onDragEnd={(e) => { e.stopPropagation(); setDragFormId(null); setDropFormTarget(null); }}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 flex-shrink-0"
                      title="Drag to reorder forms"
                    >
                      <GripVertical className="h-4 w-4" />
                    </span>
                  )}
                  {form.expanded
                    ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  }

                  {/* Form name (double-click to rename) */}
                  {renamingFormId === form.id ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => {
                        if (renameValue.trim()) updateForm(form.id, { name: renameValue.trim() });
                        setRenamingFormId(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          if (renameValue.trim()) updateForm(form.id, { name: renameValue.trim() });
                          setRenamingFormId(null);
                        }
                        if (e.key === 'Escape') setRenamingFormId(null);
                      }}
                      onClick={e => e.stopPropagation()}
                      className="font-semibold text-sm text-gray-900 border border-primary-300 rounded px-2 py-0.5 focus:ring-2 focus:ring-primary-500"
                    />
                  ) : (
                    <span
                      className="font-semibold text-sm text-gray-900 flex-1 inline-flex items-center gap-1.5 group/rename"
                      onDoubleClick={e => {
                        e.stopPropagation();
                        setRenamingFormId(form.id);
                        setRenameValue(form.name);
                      }}
                    >
                      {form.name}
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setRenamingFormId(form.id);
                          setRenameValue(form.name);
                        }}
                        title="Rename form"
                        className="text-gray-400 hover:text-primary-600 opacity-60 group-hover/rename:opacity-100 transition-opacity"
                      >
                        <Edit2 className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {/* Form code badge (effective: override-or-derived).
                      Click to edit; Enter saves, Escape cancels. Submitting
                      an empty value clears the override. */}
                  {editingCodeFormId === form.id ? (
                    <input
                      autoFocus
                      value={codeEditValue}
                      onChange={e => setCodeEditValue(e.target.value)}
                      onBlur={() => {
                        // Empty input clears the override; otherwise persist.
                        // Nothing is written unless the user changed the box.
                        const next = formCodeToSaveOnClose({ stored: form.code, openedWith: codeEditOpenedWith, typed: codeEditValue });
                        if (next !== null) {
                          updateForm(form.id, { code: next });
                        }
                        setEditingCodeFormId(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const next = formCodeToSaveOnClose({ stored: form.code, openedWith: codeEditOpenedWith, typed: codeEditValue });
                          if (next !== null) {
                            updateForm(form.id, { code: next });
                          }
                          setEditingCodeFormId(null);
                        }
                        if (e.key === 'Escape') setEditingCodeFormId(null);
                      }}
                      onClick={e => e.stopPropagation()}
                      maxLength={8}
                      placeholder={deriveFormCode(form.name)}
                      className="w-20 px-1.5 py-0.5 text-xs font-mono font-semibold border border-amber-300 rounded focus:ring-2 focus:ring-amber-400 focus:outline-none uppercase"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setEditingCodeFormId(form.id);
                        setCodeEditValue(effectiveFormCode(form));
                        setCodeEditOpenedWith(effectiveFormCode(form));
                      }}
                      className={`px-1.5 py-0.5 rounded text-xs font-mono font-semibold cursor-pointer hover:ring-2 hover:ring-amber-300 transition-shadow ${
                        form.code
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                      title={
                        form.code
                          ? `Form code (manual override): ${effectiveFormCode(form)} — click to edit, blank to reset`
                          : `Form code (auto-derived from name): ${effectiveFormCode(form)} — click to override`
                      }
                    >
                      {effectiveFormCode(form)}
                    </button>
                  )}

                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[form.category]}`}>
                    {CATEGORY_LABELS[form.category]}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[form.status]}`}>
                    {STATUS_LABELS[form.status]}
                  </span>
                  <ProtocolReviewBadge
                    doc={reviewForms[form.id]}
                    summaryRunId={reviewSummary?.runId}
                    onClick={form.expanded ? undefined : () => updateForm(form.id, { expanded: true, generationFailed: false })}
                  />
                  {/* Not rendered — approved/final form with no PDF rendering
                      the CRF Instruction Guide can embed. A Word-only upload
                      counts as un-rendered too: the guide's rasterizer is
                      PDF-only. Same predicate the Study Documents page and
                      the server precondition use (hasUsableRendering). */}
                  {(form.status === 'approved' || form.status === 'final')
                    && !hasUsableRendering(form) && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 inline-flex items-center gap-1 whitespace-nowrap"
                      title={form.sourcePacketPath
                        ? 'This form was imported from a Word document and has no page image for the CRF Instruction Guide. Use Attach rendering to add the form as a PDF or Word file.'
                        : "No PDF rendering yet — the CRF Instruction Guide can't include this form. Use Generate CRF on this form to render it, or Attach rendering to use your own file."}
                    >
                      <AlertCircle className="h-3 w-3" />
                      Not rendered
                    </span>
                  )}
                  {/* Duplicate page — this form's guide rendering is identical to
                      another form's (a wrong/shared source page). Surfaced from
                      the last CRF Instruction Guide generation. Use Generate CRF
                      to render this form's own page, then regenerate the guide. */}
                  {duplicateRenderingNames.has(form.name) && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 inline-flex items-center gap-1 whitespace-nowrap"
                      title="This form's page rendering is identical to another form's — the Instruction Guide would show the same (wrong) page for both. Use Generate CRF to render this form's own page, then regenerate the CRF Instruction Guide."
                    >
                      <AlertCircle className="h-3 w-3" />
                      Duplicate page — re-render
                    </span>
                  )}
                  {isUserRendering(form) && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 whitespace-nowrap"
                      title="This form's page rendering is a file you attached. Generate CRF and Generate All CRFs leave it in place; Attach rendering replaces it."
                    >
                      Your rendering
                    </span>
                  )}
                  {form.variables && (
                    <span className="text-xs text-gray-400">{form.variables.length} vars</span>
                  )}
                  {/* Attach rendering — a PDF or Word file becomes this form's
                      page rendering (Word converted on the server). Never
                      changes the form's questions, variables or status. */}
                  <button
                    onClick={e => { e.stopPropagation(); chooseRenderingFile(form.id); }}
                    disabled={!canEdit || attachingFormId !== null}
                    className="ml-auto text-xs px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!canEdit
                      ? "Your role doesn't allow editing on this project."
                      : "Use your own PDF or Word file as this form's page rendering (the image the CRF Instruction Guide and the packets show). The form's questions are not changed."}
                  >
                    {attachingFormId === form.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Upload className="h-3.5 w-3.5" />}
                    {attachingFormId === form.id ? 'Attaching…' : 'Attach rendering'}
                  </button>
                  {/* Original upload — only on forms that were actually uploaded
                      from a packet (sourcePacketPath is set only by the
                      extractor). For a generated form, the rendered blank PDF
                      is offered as "Download PDF" below, not here. */}
                  {isUploadedForm(form) && (
                    <button
                      onClick={e => { e.stopPropagation(); downloadFormSource(form.id, { preferOriginalPacket: true }); }}
                      className="text-xs px-2 py-1 rounded-md border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 inline-flex items-center gap-1 font-medium whitespace-nowrap"
                      title={
                        form.sourceFilePath
                          ? `Open the original uploaded document${form.sourcePageRange ? ` (pages ${form.sourcePageRange.start}${form.sourcePageRange.end !== form.sourcePageRange.start ? `–${form.sourcePageRange.end}` : ''})` : ''}`
                          : 'Open the original uploaded packet (this form could not be located individually)'
                      }
                    >
                      <Download className="h-3.5 w-3.5" />
                      Original upload
                    </button>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); setDeletingFormId(form.id); }}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                    title="Delete form"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Rationale */}
                {form.rationale && form.rationale !== 'Manually added' && (
                  <div className="px-4 pb-2 -mt-1">
                    <p className="text-xs text-gray-400 italic pl-7">{form.rationale}</p>
                  </div>
                )}

                {/* Published-standard offer.
                    Shown only when exactly one verified source form applies and
                    the form has no content yet. Both choices are explicit: there
                    is no default action and nothing happens on its own. */}
                {form.reuseMatch && !form.sourceProvenance && form.variables === null && (
                  <div className="px-4 pb-3 -mt-1">
                    <div className="ml-7 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-primary-900">
                            Available from a published standard
                          </p>
                          <p className="text-xs text-primary-800 mt-0.5">
                            {form.reuseMatch.sourceVersion}, {form.reuseMatch.sourceFormName}
                            {' — '}
                            {form.reuseMatch.fieldCount} field
                            {form.reuseMatch.fieldCount === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            disabled={form.importing}
                            onClick={e => { e.stopPropagation(); adoptStandardForm(form.id); }}
                            className="text-xs px-2.5 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                          >
                            {form.importing ? 'Adding…' : 'Use this form'}
                          </button>
                          <button
                            type="button"
                            disabled={form.importing}
                            onClick={e => { e.stopPropagation(); declineStandardForm(form.id); }}
                            className="text-xs px-2.5 py-1 rounded border border-primary-300 text-primary-800 hover:bg-primary-100 disabled:opacity-50"
                          >
                            Build it instead
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Source of an imported form (spec v2.0 §10). States where the
                    content came from, never how it was located. */}
                {form.sourceProvenance?.method === 'reuse' && (
                  <div className="px-4 pb-3 -mt-1">
                    <p className="ml-7 text-xs text-gray-600">
                      <span className="font-medium">Source:</span>{' '}
                      {form.sourceProvenance.sourceVersion}
                      {form.sourceProvenance.sourceFormName
                        ? `, ${form.sourceProvenance.sourceFormName}`
                        : ''}
                      <span className="text-gray-400"> · added from a standard, not built here</span>
                    </p>
                  </div>
                )}

                {/* Card body (expanded) */}
                {form.expanded && (
                  <div className="border-t border-gray-100 px-4 py-4">
                    <ProtocolReviewFindings record={reviewForms[form.id]} summaryRunId={reviewSummary?.runId} />

                    {/* Licensed instrument notice */}
                    {form.licensedInstrument?.isLicensed && (
                      <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-semibold text-amber-800">
                            This is a licensed instrument
                            {form.licensedInstrument.instrumentName && ` — ${form.licensedInstrument.instrumentName}`}
                          </p>
                          {form.licensedInstrument.publisher && (
                            <p className="text-amber-700 mt-0.5">
                              Publisher: {form.licensedInstrument.publisher}
                            </p>
                          )}
                          <p className="text-amber-600 mt-1">
                            Please check against the official instrument from the publisher and add or remove individual items. You will need to secure licensing before use.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* CT codelist review banner */}
                    {(() => {
                      const pending = (form.variables || []).filter(
                        v => v.ct_match_confidence === 'needs_review'
                      );
                      if (pending.length === 0) return null;
                      return (
                        <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 text-sm">
                            <p className="font-semibold text-amber-800">
                              Review CT codelists: {pending.length} pending
                            </p>
                            <p className="text-amber-700 mt-0.5 text-xs">
                              Proposed codelist matches need confirmation before approving the form.
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCtReviewFormId(form.id);
                            }}
                            className="px-3 py-1 text-xs font-semibold rounded bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
                          >
                            Review
                          </button>
                        </div>
                      );
                    })()}

                    {/* Loading state */}
                    {form.generatingVars && (
                      <div className="py-6 px-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
                          <span className="text-sm font-medium text-gray-700">Generating variable list...</span>
                        </div>
                        <StepProgress
                          active={true}
                          steps={[
                            { label: 'Analyzing form requirements...', delay: 0 },
                            { label: 'Mapping CDISC variables...', delay: 4000 },
                            { label: 'Generating response options...', delay: 10000 },
                            { label: 'Applying skip logic and validation...', delay: 18000 },
                          ]}
                        />
                      </div>
                    )}

                    {/* Variable list */}
                    {form.variables && form.variables.length > 0 && (() => {
                      // Precompute per-variable section context. When the
                      // form has no section metadata, every variable maps
                      // to sectionIdx -1 and no headers render — preserving
                      // the legacy flat appearance for unstructured forms.
                      const varToSectionIdx = new Map<string, number>();
                      if (Array.isArray(form.sections) && form.sections.length > 0) {
                        form.sections.forEach((s, idx) => {
                          s.variableIds.forEach((id) => varToSectionIdx.set(id, idx));
                        });
                      }
                      const showSectionHeaders =
                        Array.isArray(form.sections) &&
                        (form.sections.length > 1 ||
                          (form.sections.length === 1 && (form.sections[0].kind === 'repeating' || form.sections[0].kind === 'grid')));
                      return (
                      <div>
                        {/* Column header */}
                        <div className="grid grid-cols-[60px_100px_1fr_180px_90px] gap-2 px-2 pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                          <span>Order</span>
                          <span>Variable</span>
                          <span>Question Text</span>
                          <span>Type</span>
                          <span className="text-right">Actions</span>
                        </div>

                        {form.variables.map((variable, idx) => {
                          const hasOptions = OPTION_BASED_TYPES.includes(variable.responseType);
                          const isVarExpanded = expandedVarIds.has(variable.id);
                          const toggleVarExpand = () => {
                            setExpandedVarIds(prev => {
                              const next = new Set(prev);
                              if (next.has(variable.id)) next.delete(variable.id);
                              else next.add(variable.id);
                              return next;
                            });
                          };

                          // Decide whether a section header banner should
                          // render *above* this variable row. Headers
                          // appear at index 0 of each section run — i.e.,
                          // when this variable's section differs from
                          // the previous variable's section.
                          let sectionHeader: ReactNode = null;
                          if (showSectionHeaders) {
                            const mySec = varToSectionIdx.get(variable.id);
                            const prevSec =
                              idx === 0
                                ? -2
                                : varToSectionIdx.get(form.variables![idx - 1].id) ?? -2;
                            if (mySec !== undefined && mySec !== prevSec) {
                              const s = form.sections![mySec];
                              const isRepeating = s.kind === 'repeating';
                              const editKey = `${form.id}|${mySec}`;
                              const isEditing = editingSectionKey === editKey;
                              const canEdit =
                                form.status !== 'approved' && form.status !== 'final';

                              if (isEditing && sectionEditDraft) {
                                // Question list, repeating table or grid, and a grid's rows (CRF
                                // design spec §C9, 2026-09-18). The rules live in sectionEditUpdates.
                                const commit = () => {
                                  const result = sectionEditUpdates(sectionEditDraft, s);
                                  if (!result.ok) {
                                    showToast(result.error, 'error');
                                    return;
                                  }
                                  updateSectionMetadata(form.id, mySec, result.updates);
                                  setEditingSectionKey(null);
                                  setSectionEditDraft(null);
                                };
                                const cancel = () => {
                                  setEditingSectionKey(null);
                                  setSectionEditDraft(null);
                                };
                                const onKey = (e: React.KeyboardEvent) => {
                                  if (e.key === 'Enter') commit();
                                  if (e.key === 'Escape') cancel();
                                };
                                const draftKind = sectionEditDraft.kind;

                                sectionHeader = (
                                  <div
                                    className={`flex flex-wrap items-start gap-2 mt-3 mb-1 px-2 py-2 rounded ${draftKind === 'grid' ? 'bg-emerald-50 border border-emerald-200' : draftKind === 'repeating' ? 'bg-indigo-50 border border-indigo-200' : 'bg-gray-50 border border-gray-200'}`}
                                  >
                                    <input
                                      autoFocus
                                      type="text"
                                      value={sectionEditDraft.title}
                                      onChange={(e) => setSectionEditDraft(d => (d ? { ...d, title: e.target.value } : d))}
                                      onKeyDown={onKey}
                                      className="flex-1 min-w-[160px] px-2 py-1 text-sm font-semibold border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
                                      placeholder="Section title"
                                    />
                                    <label className="flex items-center gap-1 text-xs text-gray-700">
                                      Layout:
                                      <select
                                        value={draftKind}
                                        onChange={(e) => setSectionEditDraft(d => (d ? { ...d, kind: e.target.value as CrfSectionKind } : d))}
                                        className="px-1.5 py-0.5 text-xs border border-gray-300 rounded"
                                      >
                                        <option value="questions">Question list</option>
                                        <option value="repeating">Repeating table</option>
                                        <option value="grid">Grid</option>
                                      </select>
                                    </label>
                                    {draftKind === 'repeating' && (
                                      <label className="flex items-center gap-1 text-xs text-gray-700">
                                        Rows:
                                        <input
                                          type="number"
                                          min={1}
                                          max={99}
                                          value={sectionEditDraft.expectedRows}
                                          onChange={(e) => setSectionEditDraft(d => (d ? { ...d, expectedRows: e.target.value } : d))}
                                          onKeyDown={onKey}
                                          className="w-14 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
                                          placeholder="6"
                                        />
                                      </label>
                                    )}
                                    <button
                                      onClick={commit}
                                      className="text-xs px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={cancel}
                                      className="text-xs px-2 py-1 rounded text-gray-600 hover:bg-gray-200"
                                    >
                                      Cancel
                                    </button>
                                    {draftKind === 'grid' && (
                                      <div className="w-full flex flex-col gap-1">
                                        <label className="flex items-center gap-1 text-xs text-gray-700">
                                          Row label heading:
                                          <input
                                            type="text"
                                            value={sectionEditDraft.gridLabelHeader}
                                            onChange={(e) => setSectionEditDraft(d => (d ? { ...d, gridLabelHeader: e.target.value } : d))}
                                            className="w-40 px-1.5 py-0.5 text-xs border border-gray-300 rounded"
                                            placeholder="Test"
                                          />
                                        </label>
                                        <span className="text-xs text-gray-700">
                                          Rows, one per line (optionally &quot;Row | note&quot;, e.g. &quot;Potassium | mmol/L&quot;). The section&apos;s questions are the columns.
                                        </span>
                                        <textarea
                                          value={sectionEditDraft.rowsText}
                                          onChange={(e) => setSectionEditDraft(d => (d ? { ...d, rowsText: e.target.value } : d))}
                                          onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
                                          rows={Math.min(12, Math.max(4, sectionEditDraft.rowsText.split('\n').length + 1))}
                                          className="w-full px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              } else {
                                sectionHeader = (
                                  <div
                                    className={`group flex items-center gap-2 mt-3 mb-1 px-2 py-1.5 rounded ${isRepeating ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50 border border-gray-100'}`}
                                  >
                                    <span className={`text-sm font-semibold ${isRepeating ? 'text-indigo-900' : 'text-gray-700'}`}>
                                      {s.title}
                                    </span>
                                    {isRepeating && (
                                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo-600 text-white">
                                        Repeating{typeof s.expectedRows === 'number' ? ` · ${s.expectedRows} rows` : ''}
                                      </span>
                                    )}
                                    {s.kind === 'grid' && (
                                      <span
                                        className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-600 text-white"
                                        title={(s.rowEntities || []).map((r) => r.label).join(', ')}
                                      >
                                        Grid · {s.rowEntities?.length ?? 0} rows
                                        {s.gridLabelHeader ? ` (${s.gridLabelHeader})` : ''}
                                      </span>
                                    )}
                                    {!isRepeating && s.kind !== 'grid' && s.repeat && (
                                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-900">
                                        Complete once for each {s.repeat.label || 'item'}
                                        {typeof s.repeat.max === 'number' ? `, up to ${s.repeat.max}` : ''}
                                      </span>
                                    )}
                                    {canEdit && (
                                      <button
                                        onClick={() => {
                                          setEditingSectionKey(editKey);
                                          setSectionEditDraft({
                                            title: s.title,
                                            kind: (s.kind ?? 'questions') as CrfSectionKind,
                                            expectedRows:
                                              typeof s.expectedRows === 'number'
                                                ? String(s.expectedRows)
                                                : '',
                                            rowsText: gridRowsToText(s.rowEntities),
                                            gridLabelHeader: s.gridLabelHeader ?? '',
                                          });
                                        }}
                                        className="ml-auto text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Edit section"
                                      >
                                        <Edit2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                );
                              }
                            }
                          }

                          return (
                          <Fragment key={variable.id}>
                          {sectionHeader}
                          <div
                            onDragOver={(e) => {
                              if (!dragVarId || dragVarId === variable.id) return;
                              if (form.status === 'approved' || form.status === 'final') return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDropTarget({ formId: form.id, beforeId: variable.id });
                            }}
                            onDrop={(e) => {
                              if (!dragVarId) return;
                              e.preventDefault();
                              const beforeId = dropTarget && dropTarget.formId === form.id ? dropTarget.beforeId : variable.id;
                              if (dragVarId !== beforeId) reorderVariable(form.id, dragVarId, beforeId);
                              setDragVarId(null);
                              setDropTarget(null);
                            }}
                          >
                            {dragVarId && dropTarget?.formId === form.id && dropTarget.beforeId === variable.id && (
                              <div className="h-0.5 bg-primary-500 rounded-full mx-2 mb-0.5" />
                            )}
                            {/* Variable row — collapsed */}
                            {!variable.editing ? (
                              <>
                              <div
                                className={`grid grid-cols-[60px_100px_1fr_180px_90px] gap-2 items-center px-2 py-2 hover:bg-gray-50 rounded text-sm border-b border-gray-50 ${hasOptions ? 'cursor-pointer' : ''}`}
                                onClick={hasOptions ? toggleVarExpand : undefined}
                              >
                                <div className="flex items-center gap-0.5">
                                  {form.status !== 'approved' && form.status !== 'final' && (
                                    <span
                                      draggable
                                      onDragStart={(e) => {
                                        e.stopPropagation();
                                        setDragVarId(variable.id);
                                        e.dataTransfer.effectAllowed = 'move';
                                        try { e.dataTransfer.setData('text/plain', variable.id); } catch { /* some browsers reject */ }
                                      }}
                                      onDragEnd={(e) => { e.stopPropagation(); setDragVarId(null); setDropTarget(null); }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500"
                                      title="Drag to reorder"
                                    >
                                      <GripVertical className="h-3.5 w-3.5" />
                                    </span>
                                  )}
                                  {hasOptions && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleVarExpand(); }}
                                      className="text-gray-400 hover:text-gray-600 mr-0.5"
                                    >
                                      {isVarExpanded
                                        ? <ChevronDown className="h-3.5 w-3.5" />
                                        : <ChevronRight className="h-3.5 w-3.5" />}
                                    </button>
                                  )}
                                  {form.status !== 'approved' && form.status !== 'final' && (
                                  <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); moveVariable(form.id, variable.id, 'up'); }}
                                    disabled={idx === 0}
                                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30"
                                  >
                                    <ChevronUp className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); moveVariable(form.id, variable.id, 'down'); }}
                                    disabled={idx === (form.variables?.length ?? 0) - 1}
                                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30"
                                  >
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  </button>
                                  </>
                                  )}
                                </div>
                                <span className="font-mono text-xs truncate" title={variable.varName}>
                                  {variable.varName}
                                </span>
                                <div className="min-w-0 flex items-center gap-2">
                                  <span className="text-gray-700 truncate flex-1 min-w-0" title={variable.questionText}>
                                    {variable.questionText}
                                    {variable.required && (
                                      <span className="ml-0.5 font-semibold text-red-600" title="Required">*</span>
                                    )}
                                    {variable.helpText && (
                                      <span className="ml-2 text-xs italic text-gray-500" title={variable.helpText}>
                                        {variable.helpText}
                                      </span>
                                    )}
                                  </span>
                                  {hasOptions && (
                                    <CtBadge
                                      ct_codelist_ref={variable.ct_codelist_ref}
                                      ct_match_confidence={variable.ct_match_confidence}
                                      className="flex-shrink-0"
                                    />
                                  )}
                                </div>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium text-center ${RESPONSE_TYPE_COLORS[variable.responseType]}`}>
                                  {RESPONSE_TYPE_LABELS[variable.responseType]}
                                  {hasOptions && (
                                    <span className="ml-1 opacity-75">
                                      &middot; {variable.options?.length || 0} option{(variable.options?.length || 0) !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                </span>
                                <div className="flex items-center justify-end gap-1">
                                  {form.status !== 'approved' && form.status !== 'final' && (
                                    <>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); updateVariable(form.id, variable.id, { editing: true }); }}
                                        className="text-gray-400 hover:text-primary-600"
                                        title="Edit"
                                      >
                                        <Edit2 className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); duplicateVariable(form.id, variable.id); }}
                                        className="text-gray-400 hover:text-primary-600"
                                        title="Duplicate (insert a copy below)"
                                      >
                                        <Copy className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); removeVariable(form.id, variable.id); }}
                                        className="text-gray-400 hover:text-red-500"
                                        title="Delete"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              {/* Expanded options sub-section */}
                              {hasOptions && isVarExpanded && (
                                <div className="ml-16 mr-4 mb-2 mt-1 bg-gray-50 rounded-lg border border-gray-100 p-3">
                                  {variable.options && variable.options.length > 0 ? (
                                    <div className="space-y-1">
                                      <div className="grid grid-cols-[60px_1fr] gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider pb-1 border-b border-gray-200">
                                        <span>Code</span>
                                        <span>Label</span>
                                      </div>
                                      {variable.options.map((opt, optIdx) => (
                                        <div key={optIdx} className="grid grid-cols-[60px_1fr] gap-2 text-sm py-0.5">
                                          <span className="font-mono text-xs text-gray-500">{opt.code}</span>
                                          <span className="text-gray-700">{opt.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 text-xs text-amber-600">
                                      <AlertCircle className="h-3.5 w-3.5" />
                                      <span>No options configured — edit this variable to add response options.</span>
                                    </div>
                                  )}
                                  {answerCodesLine(variable.codesFrom) && (
                                    <p className="mt-2 text-xs text-gray-500">{answerCodesLine(variable.codesFrom)}</p>
                                  )}
                                </div>
                              )}
                              </>
                            ) : (
                              /* Variable row — editing */
                              <VariableEditor
                                variable={variable}
                                onSave={(updates) => updateVariable(form.id, variable.id, { ...updates, editing: false, codesFrom: codesFromAfterEdit(variable.codesFrom, variable.options, updates.options) })}
                                onCancel={() => {
                                  if (!variable.varName && !variable.questionText) {
                                    removeVariable(form.id, variable.id);
                                  } else {
                                    updateVariable(form.id, variable.id, { editing: false });
                                  }
                                }}
                              />
                            )}
                          </div>
                          </Fragment>
                          );
                        })}

                        {/* Drop zone to move a row to the very end (only active mid-drag) */}
                        {dragVarId && form.status !== 'approved' && form.status !== 'final' && (
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDropTarget({ formId: form.id, beforeId: null });
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (dragVarId) reorderVariable(form.id, dragVarId, null);
                              setDragVarId(null);
                              setDropTarget(null);
                            }}
                            className="h-6 flex items-center"
                          >
                            {dropTarget?.formId === form.id && dropTarget.beforeId === null && (
                              <div className="h-0.5 w-full bg-primary-500 rounded-full mx-2" />
                            )}
                          </div>
                        )}

                        {/* Add variable (hidden when final) */}
                        {form.status !== 'approved' && form.status !== 'final' && (
                          <button
                            onClick={() => addVariable(form.id)}
                            className="mt-2 w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-400 hover:text-primary-600 hover:border-primary-300 transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Plus className="h-4 w-4" />
                            Add Variable
                          </button>
                        )}
                      </div>
                      );
                    })()}

                    {/* Empty state */}
                    {form.variables && form.variables.length === 0 && !form.generatingVars && (
                      <div className="text-center py-6">
                        <p className="text-sm text-gray-400">No variables yet.</p>
                        {form.status !== 'approved' && form.status !== 'final' && (
                          <button
                            onClick={() => addVariable(form.id)}
                            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                          >
                            + Add Variable
                          </button>
                        )}
                      </div>
                    )}

                    {/* Quality warnings */}
                    {(() => {
                      const formWarnings = visibleWarnings.filter(w => w.formId === form.id || w.formId === form.name);
                      const dismissedForForm = form.dismissedWarnings || [];
                      const isLicensed = form.licensedInstrument?.isLicensed === true;
                      if (formWarnings.length === 0 && dismissedForForm.length === 0) return null;
                      return (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                          {/* Licensed instrument reminder — shown ABOVE the warnings
                              so the user knows the published format may dictate the
                              response type and the GCDMP guidance may not apply. */}
                          {isLicensed && formWarnings.length > 0 && (
                            <div className="mb-2 px-2.5 py-1.5 rounded bg-blue-50 border border-blue-200 text-[11px] text-blue-800 flex items-start gap-1.5">
                              <span className="font-semibold">ℹ Licensed instrument:</span>
                              <span className="flex-1">
                                {form.licensedInstrument?.instrumentName || 'Published clinical instrument'}
                                {form.licensedInstrument?.publisher && ` (${form.licensedInstrument.publisher})`}.
                                The published format dictates response types — style warnings below may not apply and can be dismissed.
                              </span>
                            </div>
                          )}
                          {formWarnings.length > 0 && (
                            <>
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                <span className="text-xs font-semibold text-amber-800">
                                  {formWarnings.length} quality {formWarnings.length === 1 ? 'issue' : 'issues'} found
                                </span>
                              </div>
                              <ul className="space-y-1">
                                {formWarnings.map((w, i) => (
                                  <li key={i} className="text-xs flex items-start gap-1.5 group">
                                    <span className={`mt-1 flex-shrink-0 h-1.5 w-1.5 rounded-full ${w.severity === 'error' ? 'bg-red-500' : w.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                                    <span className={`flex-1 ${w.severity === 'error' ? 'text-red-700' : 'text-amber-700'}`}>{w.message}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); dismissWarning(w.formId, w.message); }}
                                      className="text-[10px] px-2 py-0.5 rounded border border-amber-300 bg-white text-amber-700 hover:bg-amber-100 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex-shrink-0"
                                      title="Dismiss this warning for this form"
                                    >
                                      Dismiss
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          {dismissedForForm.length > 0 && (
                            <details className={formWarnings.length > 0 ? 'mt-2 pt-2 border-t border-amber-200' : ''}>
                              <summary className="text-[10px] text-amber-700/70 cursor-pointer hover:text-amber-700">
                                {dismissedForForm.length} dismissed warning{dismissedForForm.length !== 1 ? 's' : ''}
                              </summary>
                              <ul className="mt-1.5 space-y-1">
                                {dismissedForForm.map((message, i) => (
                                  <li key={i} className="text-[11px] flex items-start gap-1.5 text-amber-700/60">
                                    <span className="mt-1 flex-shrink-0 h-1.5 w-1.5 rounded-full bg-gray-300" />
                                    <span className="flex-1 line-through">{message}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); restoreWarning(form.id, message); }}
                                      className="text-[10px] px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 flex-shrink-0"
                                      title="Restore this warning"
                                    >
                                      Restore
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      );
                    })()}

                    {/* Status + actions footer */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        {/* Status badge */}
                        {(form.status === 'approved' || form.status === 'final') ? (
                          <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold uppercase tracking-wide">Final</span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold uppercase tracking-wide">Draft</span>
                        )}

                        {/* Action button */}
                        {form.status !== 'approved' && form.status !== 'final' ? (
                          (() => {
                            const formWarnings = visibleWarnings.filter(w => w.formId === form.id || w.formId === form.name);
                            const hasErrors = formWarnings.some(w => w.severity === 'error');
                            return (
                              <button
                                onClick={() => {
                                  if (hasErrors) {
                                    showToast('Resolve quality errors before approving', 'error');
                                    return;
                                  }
                                  // Block approval if this form's derived code
                                  // would collide with another already-approved
                                  // form. Forces the user to rename one of them
                                  // before downstream Study Build docs can be
                                  // generated from a clean Data Dictionary.
                                  const candidates = forms
                                    .filter(f =>
                                      f.id === form.id ||
                                      f.status === 'approved' ||
                                      f.status === 'final'
                                    )
                                    .map(f => ({ id: f.id, name: f.name, code: f.code }));
                                  const collisions = findFormCodeCollisions(candidates);
                                  if (collisions.length > 0) {
                                    // Highlight the conflicting code and the
                                    // other approved/draft forms that share
                                    // it, plus the two ways to fix
                                    // (override the code via the badge or
                                    // rename one of the forms).
                                    const c = collisions[0];
                                    const others = c.names
                                      .filter(n => n !== form.name)
                                      .map(n => `“${n}”`)
                                      .join(', ');
                                    showToast(
                                      `Cannot approve — code "${c.code}" is also used by ${others}. Click the form-code badge on this or the other form to override, or rename one of the forms.`,
                                      'error'
                                    );
                                    return;
                                  }
                                  if (!scheduleGate.ok) {
                                    showToast(scheduleGate.message, 'error');
                                    return;
                                  }
                                  if (!factGate.ok) {
                                    showToast(factGate.message, 'error');
                                    return;
                                  }
                                  updateForm(form.id, { status: 'approved' });
                                }}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium ${hasErrors ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                title={hasErrors ? 'Resolve quality errors first' : 'Approve form'}
                              >
                                Approve
                              </button>
                            );
                          })()
                        ) : (
                          <button
                            onClick={() => updateForm(form.id, { status: 'draft' })}
                            className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                          >
                            Back to Draft
                          </button>
                        )}
                      </div>

                      {/* Generate CRF / Download Word — the same render-and-
                          persist action either way (renders the blank CRF,
                          stores the PDF the Instruction Guide embeds, and
                          downloads a Word copy). Labeled by what the user
                          still needs from it: "Generate CRF" while the form
                          has no embeddable PDF rendering, "Download Word"
                          once it does. Enabled once the variable list is
                          approved/final. */}
                      {/* Download CRF — Word or PDF, mirroring the aCRF menu
                          beside it. The PDF is the Word document converted, so
                          the two are the same form in two formats and neither
                          needs a separate "generate it first" step. */}
                      {form.variables && form.variables.length > 0 && (() => {
                        const notApproved = form.status !== 'approved' && form.status !== 'final';
                        const busy = exportingFormId === form.id && crfMenuFormId === null && exportMenuFormId === null;
                        const offerPdf = blankPdfDownloadState(form) !== 'hidden';
                        return (
                          <div className="relative">
                            <button
                              onClick={() => setCrfMenuFormId(crfMenuFormId === form.id ? null : form.id)}
                              disabled={exportingFormId === form.id || notApproved}
                              title={notApproved
                                ? 'Approve the variable list first'
                                : form.sourcePacketPath
                                  ? 'Download a CRF built from this form\'s fields (your exact original is under "Original upload")'
                                  : 'Download the blank CRF'}
                              className="px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                            >
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              )}
                              Download CRF
                              <ChevronDown className="h-3 w-3" />
                            </button>
                            {crfMenuFormId === form.id && (
                              <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[200px]">
                                <button
                                  onClick={() => { setCrfMenuFormId(null); exportForm(form); }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FileText className="h-4 w-4 text-blue-500" />
                                  CRF as Word
                                </button>
                                {offerPdf && (
                                  <button
                                    onClick={() => { setCrfMenuFormId(null); exportFormPdf(form); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FileText className="h-4 w-4 text-red-500" />
                                    CRF as PDF
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Generate aCRF — enabled after CRF has been generated */}
                      {form.variables && form.variables.length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setExportMenuFormId(exportMenuFormId === form.id ? null : form.id)}
                            disabled={exportingFormId === form.id || !(form.crfGenerated || form.sourceFilePath || form.sourcePacketPath)}
                            title={!(form.crfGenerated || form.sourceFilePath || form.sourcePacketPath) ? 'Generate the CRF first' : 'Generate annotated CRF'}
                            className="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                          >
                            {exportingFormId === form.id && exportMenuFormId ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ClipboardList className="h-4 w-4" />
                            )}
                            Generate aCRF
                            <ChevronDown className="h-3 w-3" />
                          </button>
                          {exportMenuFormId === form.id && (
                            <div className="absolute right-0 bottom-full mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[200px]">
                              <button
                                onClick={() => { setExportMenuFormId(null); exportAnnotatedWord(form); }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4 text-indigo-500" />
                                Annotated CRF as Word
                              </button>
                              <button
                                onClick={() => { setExportMenuFormId(null); exportAnnotatedPdf(form); }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4 text-red-500" />
                                Annotated CRF as PDF
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {forms.length === 0 && (
            <div className="text-center py-16">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">No forms yet. Add one manually or start a new protocol analysis.</p>
            </div>
          )}

          {/* Second entry point for the visit packet download. The same card also
              renders under the Form Assignments view, where it sits below the
              matrix. It's repeated here because Forms is the default view and
              the feature was otherwise unreachable without knowing to click
              through to the third toggle. The two never render at once — the
              view branches are mutually exclusive. */}
          <VisitPacketExport
            projectId={projectId!}
            showToast={showToast}
          />
          </>
          ) : workspaceView === 'visits' ? (
            <VisitManager
              projectId={projectId!}
              showToast={showToast}
            />
          ) : (
            <>
              <VisitScheduleMatrix
                projectId={projectId!}
                forms={formSummaries}
                showToast={showToast}
              />
              <VisitPacketExport
                projectId={projectId!}
                showToast={showToast}
              />
            </>
          )}
        </div>
      )}

      {/* CT codelist review modal */}
      {ctReviewFormId && (() => {
        const form = forms.find(f => f.id === ctReviewFormId);
        if (!form) return null;
        const pending = (form.variables || []).filter(
          v => v.ct_match_confidence === 'needs_review'
        );
        const ctVersion =
          pending.find(v => v.ct_codelist_version)?.ct_codelist_version ||
          '2025-09-26';
        return (
          <CtReviewModal
            formName={form.name}
            variables={pending.map(v => ({
              id: v.id,
              varName: v.varName,
              questionText: v.questionText,
              ct_codelist_ref: v.ct_codelist_ref,
              ct_codelist_version: v.ct_codelist_version,
            }))}
            ctVersion={ctVersion}
            onClose={() => setCtReviewFormId(null)}
            onDecision={(decision) => applyCtDecision(form.id, decision)}
          />
        );
      })()}

      {/* Attach rendering — one hidden picker for every form card. */}
      <input
        ref={attachInputRef}
        type="file"
        accept={ATTACH_RENDERING_ACCEPT}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          const target = forms.find(f => f.id === attachTargetId);
          setAttachTargetId(null);
          if (file && target) void attachRendering(target, file);
        }}
      />

      {/* CRF Packet upload modal — toolbar "Upload CRF" button opens this. */}
      {showUploadPacket && projectId && (
        <UploadCrfPacketModal
          existingProjectId={projectId}
          defaultSingleForm={showUploadPacket.defaultSingleForm}
          onClose={() => setShowUploadPacket(null)}
          onComplete={async (_pid, formsExtracted) => {
            // Does NOT close: the result screen lists the extraction's notes
            // (duplicate form codes, retired dictionary entries). The person
            // closes it with Done.
            try {
              const list = httpsCallable<{ projectId: string }, { forms: ServerCrfForm[] }>(functions, CLOUD_FUNCTIONS.LIST_CRF_FORMS);
              const resp = await list({ projectId });
              const refreshed = (resp.data?.forms || []).map(serverFormToClientForm);
              setForms(refreshed);
              setPhase('workspace');
              showToast(`Extracted ${formsExtracted} form${formsExtracted === 1 ? '' : 's'}`, 'success');
            } catch (err) {
              console.error('[CRFBuilder] refresh after extract failed', err);
              showToast('Forms extracted — reload the page to see them.', 'error');
            }
          }}
        />
      )}

      {/* Data Dictionary import modal — toolbar "Import DD" button opens this. */}
      {showUploadDd && projectId && (
        <UploadDataDictionaryModal
          projectId={projectId}
          onClose={() => setShowUploadDd(false)}
          onComplete={async (formsImported) => {
            // Does NOT close: the result screen lists the import's notes
            // (a replaced form, skipped rows, retired dictionary entries).
            // The person closes it with Done.
            try {
              const list = httpsCallable<{ projectId: string }, { forms: ServerCrfForm[] }>(functions, CLOUD_FUNCTIONS.LIST_CRF_FORMS);
              const resp = await list({ projectId });
              const refreshed = (resp.data?.forms || []).map(serverFormToClientForm);
              setForms(refreshed);
              setPhase('workspace');
              showToast(`Imported ${formsImported} form${formsImported === 1 ? '' : 's'} from DD`, 'success');
            } catch (err) {
              console.error('[CRFBuilder] refresh after DD import failed', err);
              showToast('Forms imported — reload the page to see them.', 'error');
            }
          }}
        />
      )}

      {/* "Import Data Dictionary" — adoption, which creates the forms the file
          names (2026-09-20). Refreshes the list afterwards so a study that had
          no forms shows the ones it just gained, but does NOT close: the review
          result names every form created and every row it could not place. */}
      {showAdoptDd && projectId && (
        <ApplyDictionaryEditsModal
          projectId={projectId}
          mode="adopt"
          onClose={() => setShowAdoptDd(false)}
          onApplied={async () => {
            try {
              const list = httpsCallable<{ projectId: string }, { forms: ServerCrfForm[] }>(functions, CLOUD_FUNCTIONS.LIST_CRF_FORMS);
              const resp = await list({ projectId });
              const refreshed = (resp.data?.forms || []).map(serverFormToClientForm);
              setForms(refreshed);
              if (refreshed.length > 0) setPhase('workspace');
            } catch (err) {
              console.error('[CRFBuilder] refresh after dictionary adoption failed', err);
            }
          }}
        />
      )}

      {/* Delete confirmation dialog */}
      {deletingFormId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingFormId(null)}>
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-2">Delete Form</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete "{forms.find(f => f.id === deletingFormId)?.name}"? This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeletingFormId(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => removeForm(deletingFormId)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk generate confirmation dialog */}
      {bulkConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBulkConfirmOpen(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-2">Generate All Variable Lists</h3>
            <p className="text-sm text-gray-600 mb-2">
              This will generate variables for <strong>{forms.filter(formNeedsVariables).length} forms</strong> that
              don't have variables yet. Forms that already have variables will be skipped.
            </p>
            <p className="text-sm text-amber-600 mb-4">
              This may take several minutes depending on the number of forms. Each form is processed individually.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setBulkConfirmOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={generateAllVariables}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700"
              >
                Generate All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk generation progress modal */}
      {bulkGenerating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="font-semibold text-gray-900 mb-1">Generating Variable Lists</h3>
            <p className="text-sm text-gray-500 mb-4">
              Processing form {bulkProgress.current + 1} of {bulkProgress.total}
              {bulkProgress.currentForm && <span> — <strong>{bulkProgress.currentForm}</strong></span>}
            </p>

            {/* Progress bar */}
            <div className="w-full bg-gray-100 rounded-full h-2.5 mb-1">
              <div
                className="h-2.5 rounded-full bg-primary-600 transition-all duration-500 ease-out"
                style={{ width: `${bulkProgress.total > 0 ? Math.round((bulkProgress.current / bulkProgress.total) * 100) : 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 text-right mb-4">
              {bulkProgress.total > 0 ? Math.round((bulkProgress.current / bulkProgress.total) * 100) : 0}%
            </p>

            {/* Scrolling log */}
            <div className="max-h-64 overflow-y-auto bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-2">
              {bulkLog.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  {entry.status === 'success' && entry.message.includes('Generating') ? (
                    <Loader2 className="h-4 w-4 text-primary-500 animate-spin flex-shrink-0 mt-0.5" />
                  ) : entry.status === 'success' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : entry.status === 'error' ? (
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : entry.status === 'warning' ? (
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <span className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className={
                    entry.status === 'error' ? 'text-red-700' :
                    entry.status === 'warning' ? 'text-amber-700' :
                    'text-gray-700'
                  }>
                    {entry.form && <strong>{entry.form}:</strong>} {entry.message}
                  </span>
                </div>
              ))}
              <div ref={bulkLogEndRef} />
            </div>
          </div>
        </div>
      )}

      {/* Bulk generation complete modal */}
      {!bulkGenerating && bulkLog.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBulkLog([])}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 mb-1">Generation Complete</h3>
            <p className="text-sm text-gray-500 mb-4">
              {bulkLog.filter(e => e.status === 'success' && !e.message.includes('Generating')).length} of {bulkLog.length} forms processed successfully.
            </p>

            {/* Log */}
            <div className="max-h-64 overflow-y-auto bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-2 mb-4">
              {bulkLog.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  {entry.status === 'success' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : entry.status === 'error' ? (
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : entry.status === 'warning' ? (
                    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <span className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className={
                    entry.status === 'error' ? 'text-red-700' :
                    entry.status === 'warning' ? 'text-amber-700' :
                    'text-gray-700'
                  }>
                    {entry.form && <strong>{entry.form}:</strong>} {entry.message}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setBulkLog([])}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Variable Editor Sub-Component ───────────────────────

interface VariableEditorProps {
  variable: CRFVariable;
  onSave: (updates: Partial<CRFVariable>) => void;
  onCancel: () => void;
}

function VariableEditor({ variable, onSave, onCancel }: VariableEditorProps) {
  const [varName, setVarName] = useState(variable.varName);
  const [questionText, setQuestionText] = useState(variable.questionText);
  const [responseType, setResponseType] = useState<ResponseType>(variable.responseType);
  const [options, setOptions] = useState<CRFOption[]>(variable.options || []);
  const [unit, setUnit] = useState(variable.unit || '');
  // From the study's data dictionary (D2); editable here, printed on the CRF.
  const [required, setRequired] = useState(variable.required === true);
  const [helpText, setHelpText] = useState(variable.helpText || '');

  const handleResponseTypeChange = (newType: ResponseType) => {
    setResponseType(newType);
    if (newType === 'yesno') {
      setOptions([{ code: 1, label: 'Yes' }, { code: 0, label: 'No' }]);
      setUnit('');
    } else if (newType === 'numeric' || newType === 'date' || newType === 'text') {
      setOptions([]);
      if (newType !== 'numeric') setUnit('');
    }
  };

  const addOption = () => {
    const nextCode = nextOptionCode(options, responseType);
    setOptions([...options, { code: nextCode, label: '' }]);
  };

  const updateOption = (idx: number, field: 'code' | 'label', value: string | number | undefined) => {
    setOptions(prev => prev.map((o, i) => {
      if (i !== idx) return o;
      // An emptied code box means no code: drop the key rather than store
      // `undefined`, which the working-copy write would refuse.
      if (field === 'code' && value === undefined) {
        const { code: _dropped, ...rest } = o;
        return rest;
      }
      return { ...o, [field]: value };
    }));
  };

  const removeOption = (idx: number) => {
    setOptions(prev => prev.filter((_, i) => i !== idx));
  };

  const showOptions = responseType === 'single_select' || responseType === 'multi_select';
  const showUnit = responseType === 'numeric';

  return (
    <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 my-1">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Variable Name</label>
          <input
            autoFocus
            value={varName}
            onChange={e => setVarName(e.target.value.toUpperCase().substring(0, 40))}
            placeholder="e.g. BRTHDAT"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <span className="text-xs text-gray-400">{varName.length}/40 chars</span>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Response Type</label>
          <select
            value={responseType}
            onChange={e => handleResponseTypeChange(e.target.value as ResponseType)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500"
          >
            {ALL_RESPONSE_TYPES.map(rt => (
              <option key={rt} value={rt}>{RESPONSE_TYPE_LABELS[rt]}</option>
            ))}
          </select>
        </div>
      </div>

      {showUnit && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
          <input
            value={unit}
            onChange={e => setUnit(e.target.value)}
            placeholder="e.g. years, mg, kg"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      )}

      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">Question Text</label>
        <input
          value={questionText}
          onChange={e => setQuestionText(e.target.value)}
          placeholder="e.g. Age at study entry"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <div className="mb-3 grid grid-cols-[auto_1fr] items-start gap-3">
        <label className="flex items-center gap-1.5 pt-6 text-xs font-medium text-gray-600">
          <input
            type="checkbox"
            aria-label="Required"
            checked={required}
            onChange={e => setRequired(e.target.checked)}
          />
          Required
        </label>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Help text</label>
          <input
            aria-label="Help text"
            value={helpText}
            onChange={e => setHelpText(e.target.value)}
            placeholder="Printed in italics under the question"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Options editor */}
      {responseType === 'yesno' && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Options</label>
          <p className="text-xs text-gray-400 italic">Fixed: Yes (1) / No (0)</p>
        </div>
      )}

      {showOptions && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Options</label>
          <div className="space-y-1.5">
            {options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={opt.code ?? ''}
                  onChange={e => updateOption(idx, 'code', optionCodeFromInput(e.target.value))}
                  className="w-16 rounded-md border border-gray-300 px-2 py-1 text-sm text-center focus:ring-2 focus:ring-primary-500"
                />
                <input
                  value={opt.label}
                  onChange={e => updateOption(idx, 'label', e.target.value)}
                  placeholder="Label"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-1 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={() => removeOption(idx)}
                  className="text-gray-300 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addOption}
            className="mt-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            + Add option
          </button>
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-2 justify-end pt-2 border-t border-blue-100">
        <button
          onClick={onCancel}
          className="px-4 py-1.5 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave({
            varName, questionText, responseType, options, unit,
            required,
            helpText: helpText.trim() || undefined,
          })}
          className="px-4 py-1.5 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 flex items-center gap-1.5"
        >
          <Check className="h-3.5 w-3.5" />
          Save
        </button>
      </div>
    </div>
  );
}
